<template>
  <div class="wl" :class="{ 'wl--dark': darkMode, 'wl--mobile': isMobile }">

    <!-- ======== HEADER / LOCATION BAR ======== -->
    <header class="wl-header" v-show="!mapFullscreen">
      <div class="wl-header__inner">
        <!-- Location display / search -->
        <div class="wl-location" :class="{ 'wl-location--searching': showLocationSearch }">
          <button v-if="!showLocationSearch" class="wl-location__display" @click="showLocationSearch = true">
            <i class="bi bi-geo-alt-fill"></i>
            <span class="wl-location__name">{{ displayLocation }}</span>
            <i class="bi bi-chevron-down wl-location__arrow"></i>
          </button>
          <button v-if="!showLocationSearch" type="button" class="wl-gps-btn" :class="{ 'wl-gps-btn--active': isGpsLocation, 'wl-gps-btn--locating': gpsLocating }" @click.stop="findMyLocation" title="Use your location">
            <i class="bi" :class="gpsLocating ? 'bi-arrow-repeat wl-spin' : 'bi-crosshair'"></i>
          </button>
          <form v-else class="wl-location__search" @submit.prevent="searchLocation" action="#">
            <button type="submit" class="wl-location__search-btn" title="Search">
              <i class="bi bi-search"></i>
            </button>
            <input ref="locationInput" v-model="locationQuery" type="search" inputmode="search" enterkeyhint="search"
              placeholder="Search location..."
              class="wl-location__input" @keydown.escape="showLocationSearch = false" />
            <button type="button" class="wl-location__gps" :class="{ 'wl-location__gps--active': isGpsLocation, 'wl-location__gps--locating': gpsLocating }" @click="findMyLocation" title="Use your location">
              <i class="bi" :class="gpsLocating ? 'bi-arrow-repeat wl-spin' : 'bi-crosshair'"></i>
            </button>
            <button type="button" class="wl-location__close" @click="showLocationSearch = false">
              <i class="bi bi-x-lg"></i>
            </button>
          </form>
        </div>

        <!-- Right controls -->
        <div class="wl-header__controls">
          <!-- Dark mode toggle -->
          <button class="wl-icon-btn" @click="darkMode = !darkMode; saveDarkMode()" :title="darkMode ? 'Light mode' : 'Dark mode'">
            <i class="bi" :class="darkMode ? 'bi-sun-fill' : 'bi-moon-fill'"></i>
          </button>
        </div>
      </div>

      <!-- Recent locations dropdown -->
      <div v-if="showLocationSearch && locationsHistory && locationsHistory.length" class="wl-recent-locations">
        <div class="wl-recent-locations__label">Recent</div>
        <button v-for="(loc, i) in locationsHistory" :key="i" class="wl-recent-locations__item"
          @click="selectLocation(loc)">
          <i class="bi bi-clock-history"></i>
          {{ loc.name || loc.location }}
        </button>
      </div>
    </header>

    <!-- ======== LOADING ======== -->
    <div v-if="isLoading" class="wl-loading">
      <div class="wl-loading__spinner"></div>
      <span>Loading weather...</span>
      <span v-if="loadingStatus" class="wl-loading__status">{{ loadingStatus }}</span>
    </div>

    <!-- ======== NO LOCATION ======== -->
    <div v-else-if="!selectedLocation && !errorStatus" class="wl-empty">
      <i class="bi bi-geo-alt" style="font-size: 3rem; opacity: 0.3;"></i>
      <h5>Add a location to get started</h5>
      <div class="wl-empty__actions">
        <button class="wl-btn wl-btn--primary" @click="showLocationSearch = true">
          <i class="bi bi-search"></i> Search
        </button>
        <button class="wl-btn wl-btn--outline" @click="findMyLocation">
          <i class="bi bi-crosshair"></i> Use your location
        </button>
      </div>
    </div>

    <!-- ======== MAIN CONTENT ======== -->
    <template v-else>

      <!-- Error banner (non-blocking) -->
      <div v-if="errorStatus" class="wl-error">
        <i class="bi bi-exclamation-triangle-fill"></i>
        <span>{{ errorStatus }}</span>
        <button class="wl-error__retry" @click="fetchWeatherData(true)">Retry</button>
      </div>

      <!-- Refresh indicator -->
      <div v-if="isRefreshing" class="wl-refreshing">
        <div class="wl-refreshing__bar"></div>
      </div>

      <!-- Alerts Banner -->
      <div v-if="alerts && alerts.length" class="wl-alerts">
        <div v-for="(a, i) in alerts" :key="i" class="wl-alert-badge" :class="getAlertBadgeClass(a)"
          @click="expandedAlert = a">
          <i :class="getAlertIconClass(a)"></i>
          <span>{{ a.event }}</span>
        </div>
      </div>

      <!-- Tab content -->
      <main class="wl-main" ref="mainContent">

        <!-- Pull-to-refresh indicator -->
        <div class="wl-pull-indicator" ref="pullIndicator"
          :class="{ 'wl-pull-indicator--releasing': _pullReleasing, 'wl-pull-indicator--refreshing': pullRefreshing }">
          <div class="wl-pull-indicator__inner">
            <i class="bi" :class="pullRefreshing ? 'bi-arrow-repeat wl-spin' : (pullDistance >= 60 ? 'bi-arrow-up-circle' : 'bi-arrow-down-circle')"></i>
            <span>{{ pullRefreshing ? 'Refreshing...' : (pullDistance >= 60 ? 'Release to refresh' : 'Pull to refresh') }}</span>
          </div>
        </div>

        <!-- ======== TAB: NOW ======== -->
        <section v-show="activeTab === 'now'" class="wl-section wl-now">

          <!-- 1. Current temperature + conditions -->
          <div v-if="currentConditions" class="wl-current">
            <div class="wl-current__hero">
              <div class="wl-current__icon-wrap">
                <div class="wl-wx-icon" :class="currentConditions.icon"></div>
              </div>
              <div class="wl-current__temp">
                <span class="wl-current__temp-value" v-html="fmtVal(currentConditions, 'temp')"></span>
                <span v-if="currentConditions.feelslike != null" class="wl-current__feelslike">
                  Feels like <span v-html="fmtVal(currentConditions, 'feelslike')"></span>
                </span>
                <span v-if="currentConditions.datetimeEpoch" class="wl-current__obs-time">{{ fmtSunTimeHM(currentConditions.datetimeEpoch) }}<template v-if="lastUpdated"> · updated {{ lastUpdatedAgo }}</template></span>
              </div>
            </div>
          </div>

          <!-- Rain statement (only when upcoming rain detected) -->
          <div class="wl-rain-brief" v-if="hasSession && nearbyRain && lastNextRain && lastNextRain.next">
            <h6 class="wl-section-title">Precipitation</h6>
            <a href="#" class="wl-rain-brief__link" @click.prevent="activeTab = 'rain'">
              <i class="bi bi-broadcast" style="margin-right:4px;"></i>
              <span class="wl-rain-brief__title">{{ rainBriefTitle || 'Rain nearby' }}</span>
            </a>
            <div v-if="rainBriefDetail" class="wl-rain-brief__detail">{{ rainBriefDetail }}</div>
            <!-- Mini minutely rain chart -->
            <div v-if="hasMinutelyRain" class="wl-rain-brief__chart">
              <div class="wl-chart-scroll-wrap">
                <div class="wl-chart-ypin" ref="rainBriefYPin"></div>
                <div class="wl-chart-scroll" ref="rainBriefScroll">
                  <svg class="wl-rain-brief__svg" ref="rainBriefSvg"></svg>
                </div>
              </div>
            </div>
          </div>
          <!-- Rain upsell (not logged in, precip today) -->
          <div class="wl-rain-brief" v-else-if="!hasSession && hasTodayPrecip">
            <div class="wl-rain-brief__text">
              <i class="bi bi-droplet-fill" style="margin-right:4px; color: var(--wl-accent);"></i>Precipitation expected today.
              <a href="#" class="wl-rain-brief__link" @click.prevent="openSignIn">Sign in</a> for radar tracking.
            </div>
          </div>

          <!-- Snowfall Amounts KPI (shown when snow is predicted — takes priority over rain) -->
          <div v-if="hasUpcomingSnow" class="wl-precip-kpi">
            <h6 class="wl-section-title"><i class="bi bi-snow" style="color: var(--wl-info); margin-right:4px;"></i>Snowfall Amounts</h6>
            <div class="wl-precip-kpi__grid wl-precip-kpi__grid--3">
              <div class="wl-precip-kpi__item">
                <div class="wl-precip-kpi__label">Next 6 Hrs</div>
                <div class="wl-precip-kpi__value" v-html="fmtPrecipAccum(_snowAccum[6], 'snow')"></div>
              </div>
              <div class="wl-precip-kpi__item">
                <div class="wl-precip-kpi__label">Next 12 Hrs</div>
                <div class="wl-precip-kpi__value" v-html="fmtPrecipAccum(_snowAccum[12], 'snow')"></div>
              </div>
              <div class="wl-precip-kpi__item">
                <div class="wl-precip-kpi__label">Next 24 Hrs</div>
                <div class="wl-precip-kpi__value" v-html="fmtPrecipAccum(_snowAccum[24], 'snow')"></div>
              </div>
            </div>
          </div>

          <!-- Rainfall Amounts KPI (shown when rain is predicted and no snow) -->
          <div v-else-if="hasUpcomingRain" class="wl-precip-kpi">
            <h6 class="wl-section-title"><i class="bi bi-droplet-fill" style="color: var(--wl-accent); margin-right:4px;"></i>Rainfall Amounts</h6>
            <div class="wl-precip-kpi__grid wl-precip-kpi__grid--3">
              <div class="wl-precip-kpi__item">
                <div class="wl-precip-kpi__label">Next 2 Hrs</div>
                <div class="wl-precip-kpi__value" v-html="fmtPrecipAccum(_precipAccum[2], 'precip')"></div>
              </div>
              <div class="wl-precip-kpi__item">
                <div class="wl-precip-kpi__label">Next 12 Hrs</div>
                <div class="wl-precip-kpi__value" v-html="fmtPrecipAccum(_precipAccum[12], 'precip')"></div>
              </div>
              <div class="wl-precip-kpi__item">
                <div class="wl-precip-kpi__label">Next 24 Hrs</div>
                <div class="wl-precip-kpi__value" v-html="fmtPrecipAccum(_precipAccum[24], 'precip')"></div>
              </div>
            </div>
          </div>

          <!-- 3. Today's forecast: big high / low -->
          <h6 v-if="today" class="wl-section-title">Today</h6>
          <div v-if="today" class="wl-today-brief" @click="openDetail(today, 'day')" style="cursor:pointer;">
            <div class="wl-today-brief__temps">
              <span class="wl-today-brief__hi"><span v-html="fmtVal(today, 'tempmax')"></span></span>
              <span class="wl-today-brief__sep">/</span>
              <span class="wl-today-brief__lo"><span v-html="fmtVal(today, 'tempmin')"></span></span>
            </div>
            <div class="wl-today-brief__meta">
              <div class="wl-today-brief__conditions">
                <div class="wl-wx-icon wl-wx-icon--sm" :class="today.icon"></div>
                <span>{{ today.conditions }}</span>
              </div>
              <div v-if="today.precipprob > 0" class="wl-today-brief__precip">
                <i class="bi bi-droplet-fill"></i> {{ fmtVal(today, 'precipprob') }}
              </div>
            </div>
            <div v-if="today.description" class="wl-today-brief__desc">{{ today.description }}</div>
          </div>

          <!-- 4. Hourly forecast scroller -->
          <div v-if="todayHours && todayHours.length" class="wl-hourly">
            <h6 class="wl-section-title">Hourly</h6>
            <div class="wl-hourly__scroll" ref="hourlyScrollCombined">
              <template v-for="(hour, i) in todayHours" :key="i">
                <div v-if="hourDayLabel(hour, i)" class="wl-hour-day-label">
                  <span>{{ hourDayLabel(hour, i) }}</span>
                </div>
                <div class="wl-hour-card"
                  :class="hourProximityClass(hour)"
                  @click="openDetail(hour, 'hour')">
                  <div class="wl-hour-card__time">{{ fmtTime(hour) }}</div>
                <div class="wl-wx-icon wl-wx-icon--sm" :class="hour.icon"></div>
                <div class="wl-hour-card__temp" v-html="fmtVal(hour, 'temp')"></div>
                <div v-if="hour.feelslike != null && Math.round(hour.feelslike) !== Math.round(hour.temp)" class="wl-hour-card__feelslike">
                  Feels <span v-html="fmtVal(hour, 'feelslike')"></span>
                </div>
                <div class="wl-hour-card__precip">
                  <i class="bi bi-droplet-fill"></i>{{ hour.precipprob > 0 ? fmtVal(hour, 'precipprob') : '0%' }}
                </div>
                <div v-if="hour.windspeed >= windyThreshold" class="wl-hour-card__wind">
                  <span class="wl-wind-arrow wl-wind-arrow--sm"
                    :style="'transform:rotate(' + Math.round(hour.winddir) + 'deg)'">
                    <i class="bi bi-arrow-down"></i>
                  </span>
                  <span v-html="fmtVal(hour, 'windspeed', true)"></span>
                </div>
              </div>
              </template>
            </div>
          </div>

          <!-- 5. Map -->
          <div v-show="mapDisplayMode !== 'none'" class="wl-map-wrap" :class="{ 'wl-map-wrap--fullscreen': mapFullscreen, 'wl-map-wrap--small': mapDisplayMode === 'small' }" ref="homeMapWrap"
            @click.self="onHomeMapClick">
            <div class="wl-map" ref="mapEl" @click="onHomeMapClick"></div>

            <!-- Fullscreen exit button (always visible when fullscreen) -->
            <button class="wl-map-fullscreen-exit" @click="mapFullscreen = false" title="Exit fullscreen">
              <i class="bi bi-x-lg"></i>
            </button>

            <!-- Map layer picker overlay -->
            <div v-if="mapDisplayMode === 'full' || mapFullscreen" class="wl-map-layers">
              <button class="wl-map-layer-btn wl-map-layers-toggle" @click="showLayerPicker = !showLayerPicker" title="Layers">
                <i class="bi bi-layers-fill"></i>
                <span class="wl-map-layer-btn__label">Layers</span>
                <i class="bi" :class="showLayerPicker ? 'bi-chevron-up' : 'bi-chevron-down'" style="font-size:12px;margin-left:2px;"></i>
              </button>
              <div v-if="showLayerPicker" class="wl-map-layers-panel">
                <template v-for="layer in mapLayerOptions" :key="layer.id">
                  <button class="wl-map-layer-btn"
                    :class="{ 'wl-map-layer-btn--active': activeMapLayer === layer.id }" @click="switchMapLayer(layer.id)"
                    :title="layer.label">
                    <i :class="layer.icon"></i>
                    <span>{{ layer.shortLabel }}</span>
                  </button>
                </template>
              </div>
              <!-- Overlay toggles: alerts, hail, lightning -->
              <div class="wl-map-overlay-bar">
                <button class="wl-map-overlay-btn" :class="{ 'wl-map-overlay-btn--active': overlayAlerts }" @click="toggleOverlay('alerts')" title="Weather Alerts">
                  <i class="bi bi-exclamation-triangle-fill"></i>
                </button>
                <button class="wl-map-overlay-btn" :class="{ 'wl-map-overlay-btn--active': overlayHail }" @click="toggleOverlay('hail')" title="Hail">
                  <i class="bi bi-circle-fill"></i>
                </button>
                <button class="wl-map-overlay-btn" :class="{ 'wl-map-overlay-btn--active': overlayLightning }" @click="toggleOverlay('lightning')" title="Lightning">
                  <i class="bi bi-lightning-fill"></i>
                </button>
                <!-- Alert filter -->
                <template v-if="overlayAlerts && alertFilterTypes.length > 0">
                  <div class="wl-map-overlay-divider"></div>
                  <button class="wl-map-overlay-btn" :class="{ 'wl-map-overlay-btn--active': alertHiddenCount > 0 }" @click.stop="alertFilterOpen = !alertFilterOpen" title="Filter alert types">
                    <i class="bi bi-funnel-fill"></i>
                    <span v-if="alertHiddenCount > 0" class="wl-alert-badge">{{ alertHiddenCount }}</span>
                  </button>
                </template>
              </div>
              <!-- Alert filter dropdown -->
              <div v-if="alertFilterOpen" class="wl-alert-dropdown">
                <label v-for="t in alertFilterTypes" :key="t.event" class="wl-alert-filter-item">
                  <input type="checkbox" :checked="t.visible" @change="onAlertFilterChange(t.event, $event.target.checked)">
                  <span class="wl-alert-swatch" :style="{ backgroundColor: t.color }"></span>
                  <span class="wl-alert-filter-label">{{ t.label }} ({{ t.count }})</span>
                </label>
                <div class="wl-alert-filter-actions">
                  <button @click="alertFilterShowAll">Show all</button>
                  <button @click="alertFilterHideAll">Hide all</button>
                </div>
              </div>
            </div>

            <!-- Recenter button (under zoom controls, top-right) -->
            <button v-if="mapDisplayMode === 'full' || mapFullscreen" class="wl-map-locate-btn" @click="updateMapView" title="Recenter on location">
              <i class="bi bi-geo-alt-fill"></i>
            </button>

            <!-- Map time controls -->
            <div v-if="mapDisplayMode === 'full' || mapFullscreen" class="wl-map-time">
              <button class="wl-map-time__btn" @click="stepTime(-computedStepInterval)" :disabled="isPlaying">
                <i class="bi bi-chevron-left"></i>
              </button>
              <button class="wl-map-time__play" @click="playPause">
                <i class="bi" :class="isPlaying ? 'bi-pause-fill' : 'bi-play-fill'"></i>
              </button>
              <div class="wl-map-time__gap"></div>
              <div class="wl-map-time__display wl-map-time__display--clickable" @click="openMapDateTimePicker" title="Jump to date & time">
                <div class="wl-map-time__time">{{ mapTimeDisplay }}</div>
                <div class="wl-map-time__date">{{ mapDateDisplay }}</div>
              </div>
              <div class="wl-map-time__gap"></div>
              <button class="wl-map-time__btn" @click="stepTime(computedStepInterval)" :disabled="isPlaying">
                <i class="bi bi-chevron-right"></i>
              </button>
              <button class="wl-map-time__btn wl-map-time__reset" @click="resetToNow" :disabled="isPlaying"
                title="Reset to now">
                <i class="bi bi-arrow-clockwise"></i>
              </button>
              <button class="wl-map-time__btn" @click="mapFullscreen = !mapFullscreen"
                :title="mapFullscreen ? 'Exit fullscreen' : 'Fullscreen'">
                <i class="bi" :class="mapFullscreen ? 'bi-fullscreen-exit' : 'bi-arrows-fullscreen'"></i>
              </button>
            </div>
            <!-- Date/time picker overlay -->
            <div v-if="showMapDateTimePicker" class="wl-map-dtpicker-overlay" @click.self="showMapDateTimePicker = false">
              <div class="wl-map-dtpicker">
                <div class="wl-map-dtpicker__header">
                  <strong>Jump to date &amp; time</strong>
                  <button class="wl-map-dtpicker__close" @click="showMapDateTimePicker = false"><i class="bi bi-x-lg"></i></button>
                </div>
                <input type="datetime-local" class="wl-map-dtpicker__input" v-model="mapDateTimePickerValue" />
                <div class="wl-map-dtpicker__actions">
                  <button class="wl-btn wl-btn--outline" @click="showMapDateTimePicker = false">Cancel</button>
                  <button class="wl-btn wl-btn--primary" @click="applyMapDateTimePicker">Go</button>
                </div>
              </div>
            </div>
          </div>

          <!-- 6. Today's details -->

          <!-- Sunrise / Sunset -->
          <h6 v-if="currentConditions && today" class="wl-section-title">Sun &amp; Moon</h6>
          <div v-if="currentConditions && today" class="wl-current__sun-event">
            <i class="bi bi-sunrise-fill"></i>
            <span>↑ {{ fmtSunTimeHM(today.sunriseEpoch) }}</span>
            <span class="wl-current__sun-sep">·</span>
            <i class="bi bi-sunset-fill"></i>
            <span>↓ {{ fmtSunTimeHM(today.sunsetEpoch) }}</span>
            <template v-if="today.moonphase || today.moonphase === 0">
              <span class="wl-current__sun-sep">·</span>
              <i class="bi" :class="moonPhaseIcon"></i>
              <span>{{ moonPhaseDesc }}</span>
            </template>
          </div>

          <!-- 7-day forecast compact -->
          <div v-if="nowForecastDays && nowForecastDays.length" class="wl-now-forecast">
            <h6 class="wl-section-title">7-Day Forecast</h6>
            <div v-for="day in nowForecastDays" :key="'nf'+day.datetimeEpoch" class="wl-day-card" @click="openDetail(day, 'day')">
              <div class="wl-day-card__header">
                <div class="wl-day-card__day">
                  <div>{{ fmtDayName(day) }}</div>
                  <div class="wl-day-card__date">{{ fmtDateShort(day) }}</div>
                </div>
                <div class="wl-wx-icon wl-wx-icon--sm" :class="day.icon"></div>
                <div class="wl-day-card__temps">
                  <span class="wl-day-card__hi" v-html="fmtVal(day, 'tempmax')"></span>
                  <span class="wl-day-card__lo" v-html="fmtVal(day, 'tempmin')"></span>
                </div>
                <div class="wl-day-card__precip">
                  <div><i class="bi bi-droplet-fill"></i>{{ day.precipprob > 0 ? fmtVal(day, 'precipprob') : '0%' }}</div>
                  <div v-if="day.precipprob > 50" class="wl-day-card__precip-amts"><span v-if="day.snow > 0"><i class="bi bi-snow"></i> <span v-html="fmtVal(day, 'snow')"></span></span><span v-else-if="day.precip > 0" v-html="fmtVal(day, 'precip')"></span></div>
                </div>
                <div class="wl-day-card__wind">
                  <span class="wl-wind-arrow wl-wind-arrow--sm"
                    :style="'transform:rotate(' + Math.round(day.winddir) + 'deg)'">
                    <i class="bi bi-arrow-down"></i>
                  </span>
                  <span v-html="fmtVal(day, 'windspeed', true)"></span>
                </div>
              </div>
            </div>
          </div>

          <!-- Current conditions detail grid -->
          <h6 v-if="currentConditions" class="wl-section-title">Current Details</h6>
          <div v-if="currentConditions" class="wl-current__grid">
            <div class="wl-stat">
              <i class="bi bi-droplet-fill wl-stat__icon"></i>
              <div class="wl-stat__label">Precip</div>
              <div class="wl-stat__value">
                <template v-if="currentConditions.snow > 0">
                  <i class="bi bi-snow"></i> {{ fmtVal(currentConditions, 'snow') }}
                </template>
                <template v-else-if="currentConditions.precipremote >= 0.005">
                  <i class="bi bi-broadcast"></i> {{ fmtVal(currentConditions, 'precipremote', false, 'precip') }}
                </template>
                <template v-else-if="currentConditions.precip >= 0.005">
                  {{ fmtVal(currentConditions, 'precip') }}
                </template>
                <template v-else>—</template>
              </div>
            </div>
            <div class="wl-stat">
              <i class="bi bi-wind wl-stat__icon"></i>
              <div class="wl-stat__label">Wind</div>
              <div class="wl-stat__value">
                <span class="wl-wind-arrow" :style="'transform:rotate(' + Math.round(currentConditions.winddir) + 'deg)'">
                  <i class="bi bi-arrow-down"></i>
                </span>
                <span v-html="fmtVal(currentConditions, 'windspeed', true)"></span>
              </div>
            </div>
            <div class="wl-stat">
              <i class="bi bi-moisture wl-stat__icon"></i>
              <div class="wl-stat__label">Humidity</div>
              <div class="wl-stat__value" v-html="fmtVal(currentConditions, 'humidity')"></div>
            </div>
            <div class="wl-stat">
              <i class="bi bi-sun wl-stat__icon"></i>
              <div class="wl-stat__label">UV</div>
              <div class="wl-stat__value" v-html="fmtVal(currentConditions, 'uvindex2')"></div>
            </div>
            <div class="wl-stat">
              <i class="bi bi-eye wl-stat__icon"></i>
              <div class="wl-stat__label">Visibility</div>
              <div class="wl-stat__value" v-html="fmtVal(currentConditions, 'visibility')"></div>
            </div>
            <div class="wl-stat">
              <i class="bi bi-lungs wl-stat__icon"></i>
              <div class="wl-stat__label">AQI</div>
              <div class="wl-stat__value">{{ fmtAqi(currentConditions) }}</div>
            </div>
            <div class="wl-stat">
              <i class="bi bi-speedometer2 wl-stat__icon"></i>
              <div class="wl-stat__label">Pressure</div>
              <div class="wl-stat__value" v-html="fmtVal(currentConditions, 'pressure')"></div>
            </div>
            <div class="wl-stat">
              <i class="bi bi-droplet-half wl-stat__icon"></i>
              <div class="wl-stat__label">Dew Pt</div>
              <div class="wl-stat__value" v-html="fmtVal(currentConditions, 'dew')"></div>
            </div>
          </div>

        </section>

        <!-- ======== TAB: TODAY (always combined — hidden) ======== -->
        <section v-show="false" class="wl-section wl-today">
          <div v-if="today" class="wl-today__summary">
            <div class="wl-today__header" @click="openDetail(today, 'day')" style="cursor:pointer;">
              <div>
                <div class="wl-today__temp-range">
                  <span class="wl-today__hi" v-html="fmtVal(today, 'tempmax')"></span>
                  <span class="wl-today__sep">/</span>
                  <span class="wl-today__lo" v-html="fmtVal(today, 'tempmin')"></span>
                </div>
              </div>
              <div class="wl-today__center">
                <div class="wl-wx-icon wl-wx-icon--lg" :class="today.icon"></div>
                <div class="wl-today__conditions">{{ today.conditions }}</div>
              </div>
            </div>

            <!-- Key stats row -->
            <div class="wl-today__stats">
              <div class="wl-today__stat">
                <i class="bi bi-droplet-fill" style="color: var(--wl-accent);"></i>
                <span>{{ today.precipprob > 0 ? fmtVal(today, 'precipprob') : '0%' }}</span>
                <template v-if="today.precipprob > 50 && today.precip > 0">
                  <span class="wl-today__stat-sub" v-html="fmtVal(today, 'precip')"></span>
                </template>
                <template v-else-if="today.precip > 0">
                  <span class="wl-today__stat-sub" v-html="fmtVal(today, 'precip')"></span>
                </template>
              </div>
              <div class="wl-today__stat" v-if="today.snow > 0">
                <i class="bi bi-snow" style="color: var(--wl-info);"></i>
                <span v-html="fmtVal(today, 'snow')"></span>
              </div>
              <div class="wl-today__stat">
                <span class="wl-wind-arrow" :style="'transform:rotate(' + Math.round(today.winddir) + 'deg)'">
                  <i class="bi bi-arrow-down"></i>
                </span>
                <span v-html="fmtVal(today, 'windspeed', true)"></span>
                <span class="wl-today__stat-sub">gusts <span v-html="fmtVal(today, 'windgust', true)"></span></span>
              </div>
              <div class="wl-today__stat">
                <i class="bi bi-moisture" style="color: var(--wl-text-muted);"></i>
                <span v-html="fmtVal(today, 'humidity')"></span>
              </div>
              <div class="wl-today__stat">
                <i class="bi bi-sun" style="color: #f59e0b;"></i>
                <span>UV {{ fmtVal(today, 'uvindex2') }}</span>
              </div>
            </div>

            <div v-if="today.description" class="wl-today__desc">{{ today.description }}</div>

            <!-- Sunrise / Sunset / Moon -->
            <div class="wl-astro">
              <div class="wl-astro__item" v-for="evt in orderedSunEvents" :key="evt.type">
                <i class="bi wl-astro__icon" :class="evt.type === 'sunrise' ? 'bi-sunrise-fill wl-astro__icon--rise' : 'bi-sunset-fill wl-astro__icon--set'"></i>
                <div class="wl-astro__label">{{ evt.tomorrow ? 'Tomorrow ' : '' }}{{ evt.type === 'sunrise' ? 'Sunrise' : 'Sunset' }}</div>
                <div class="wl-astro__value">{{ fmtSunTime(evt.epoch) }}</div>
              </div>
              <div class="wl-astro__item">
                <i class="bi wl-astro__icon wl-astro__icon--moon" :class="moonPhaseIcon"></i>
                <div class="wl-astro__label">{{ moonPhaseDesc }}</div>
                <div class="wl-astro__value">{{ moonIllumination }}</div>
              </div>
              <div class="wl-astro__item">
                <i class="bi bi-clock wl-astro__icon"></i>
                <div class="wl-astro__label">Daylight</div>
                <div class="wl-astro__value">{{ daylightDuration }}</div>
              </div>
            </div>
          </div>

          <!-- Hourly scroll -->
          <div v-if="todayHours && todayHours.length" class="wl-hourly">
            <h6 class="wl-section-title">Hourly Forecast</h6>
            <div class="wl-hourly__scroll" ref="hourlyScroll">
              <template v-for="(hour, i) in todayHours" :key="i">
                <div v-if="hourDayLabel(hour, i)" class="wl-hour-day-label">
                  <span>{{ hourDayLabel(hour, i) }}</span>
                </div>
                <div class="wl-hour-card"
                  :class="hourProximityClass(hour)"
                  @click="openDetail(hour, 'hour')">
                  <div class="wl-hour-card__time">{{ fmtTime(hour) }}</div>
                <div class="wl-wx-icon wl-wx-icon--sm" :class="hour.icon"></div>
                <div class="wl-hour-card__temp" v-html="fmtVal(hour, 'temp')"></div>
                <div v-if="hour.feelslike != null && Math.round(hour.feelslike) !== Math.round(hour.temp)" class="wl-hour-card__feelslike">
                  Feels <span v-html="fmtVal(hour, 'feelslike')"></span>
                </div>
                <div class="wl-hour-card__precip">
                  <i class="bi bi-droplet-fill"></i>{{ hour.precipprob > 0 ? fmtVal(hour, 'precipprob') : '0%' }}
                </div>
                <div class="wl-hour-card__humidity">
                  <i class="bi bi-moisture"></i><span v-html="fmtVal(hour, 'humidity')"></span>
                </div>
                <div class="wl-hour-card__wind">
                  <span class="wl-wind-arrow wl-wind-arrow--sm"
                    :style="'transform:rotate(' + Math.round(hour.winddir) + 'deg)'">
                    <i class="bi bi-arrow-down"></i>
                  </span>
                  <span v-html="fmtVal(hour, 'windspeed', true)"></span>
                </div>
              </div>
              </template>
            </div>
          </div>
        </section>

        <!-- ======== TAB: RAIN ======== -->
        <section v-show="activeTab === 'rain'" class="wl-section wl-rain-view">

          <!-- Sign-in prompt / inline form for non-authenticated users -->
          <div v-if="!hasSession" class="wl-rain-signin-cta">
            <!-- Inline sign-in form -->
            <div v-if="showSignInForm" class="wl-signin-form">
              <div class="wl-signin-form__header">
                <strong>Sign in to your account</strong>
                <button class="wl-signin-form__close" @click="closeSignIn" aria-label="Close"><i class="bi bi-x-lg"></i></button>
              </div>
              <form @submit.prevent="submitSignIn" class="wl-signin-form__body">
                <div v-if="signInError" class="wl-signin-form__error">
                  <i class="bi bi-exclamation-circle"></i> {{ signInError }}
                </div>
                <label class="wl-signin-form__label" for="wl-signin-email">Email</label>
                <input id="wl-signin-email" type="text" class="wl-signin-form__input" v-model="signInUsername"
                  autocomplete="username" placeholder="you@example.com" :disabled="signInLoading">
                <label class="wl-signin-form__label" for="wl-signin-password">Password</label>
                <div class="wl-signin-form__password-wrapper">
                  <input id="wl-signin-password" :type="showSignInPassword ? 'text' : 'password'" class="wl-signin-form__input" v-model="signInPassword"
                    autocomplete="current-password" placeholder="Password" :disabled="signInLoading">
                  <button type="button" class="wl-signin-form__password-toggle" @click="showSignInPassword = !showSignInPassword" :title="showSignInPassword ? 'Hide password' : 'Show password'">
                    <i class="bi" :class="showSignInPassword ? 'bi-eye-slash' : 'bi-eye'"></i>
                  </button>
                </div>
                <button type="submit" class="wl-btn wl-btn--primary wl-signin-form__submit" :disabled="signInLoading">
                  <i v-if="signInLoading" class="bi bi-arrow-repeat wl-spin"></i>
                  <i v-else class="bi bi-box-arrow-in-right"></i>
                  {{ signInLoading ? 'Signing in...' : 'Sign In' }}
                </button>
                <div class="wl-signin-form__links">
                  <span>No account? <a href="/sign-up">Create one free</a></span>
                  <span><a href="/change-password">Forgot password?</a></span>
                </div>
              </form>
            </div>
            <!-- CTA card (when form is hidden) -->
            <template v-else>
              <div class="wl-rain-signin-cta__icon"><i class="bi bi-broadcast"></i></div>
              <div class="wl-rain-signin-cta__text">
                <strong>Unlock real-time rain tracking</strong>
                <p>Sign in for minute-by-minute radar data, rain rate charts, and precise precipitation nowcasts.</p>
              </div>
              <div class="wl-rain-signin-cta__actions">
                <button class="wl-btn wl-btn--primary" @click="openSignIn"><i class="bi bi-box-arrow-in-right"></i> Sign In</button>
                <a href="/sign-up" class="wl-btn wl-btn--outline">Create Free Account</a>
              </div>
            </template>
          </div>

          <!-- Rain intensity status (minutely-dependent: show simplified for non-session) -->
          <div v-if="hasSession" class="wl-rain-status" :class="'wl-rain-status--level-' + rainIntensityNow.level">
            <div class="wl-rain-status__icon">
              <i class="bi" :class="rainIntensityNow.level > 0 ? 'bi-cloud-rain-heavy-fill' : 'bi-cloud-fill'"></i>
            </div>
            <div class="wl-rain-status__info">
              <div class="wl-rain-status__label">{{ rainIntensityNow.label }}<span v-if="rainBriefDetail" class="wl-rain-status__detail"> — {{ rainBriefDetail }}</span></div>
              <div v-if="rainIntensityNow.level > 0" class="wl-rain-status__rate">{{ rainIntensityNow.rate }}</div>
              <div v-if="rainIntensityNow.dbz > 0" class="wl-rain-status__dbz">{{ Math.round(rainIntensityNow.dbz) }} dBZ reflectivity</div>
            </div>
          </div>

          <!-- Rain totals -->
          <div class="wl-rain-totals">
            <div class="wl-rain-total-card">
              <div class="wl-rain-total-card__icon"><i class="bi bi-clock-history"></i></div>
              <div class="wl-rain-total-card__label">So far</div>
              <div class="wl-rain-total-card__value" v-html="fmtRainAmount(todayRainSoFar)"></div>
              <div class="wl-rain-total-card__sub">Observed</div>
            </div>
            <div class="wl-rain-total-card">
              <div class="wl-rain-total-card__icon"><i class="bi bi-clock"></i></div>
              <div class="wl-rain-total-card__label">Remaining</div>
              <div class="wl-rain-total-card__value" v-html="fmtRainAmount(todayRainRemaining)"></div>
              <div class="wl-rain-total-card__sub">Forecast</div>
            </div>
            <div class="wl-rain-total-card">
              <div class="wl-rain-total-card__icon"><i class="bi bi-calendar-check"></i></div>
              <div class="wl-rain-total-card__label">Day total</div>
              <div class="wl-rain-total-card__value" v-html="fmtRainAmount(todayRainTotal)"></div>
              <div class="wl-rain-total-card__sub">Combined</div>
            </div>
          </div>

          <!-- Rain rate from radar (requires minutely) -->
          <div v-if="hasSession && hasMinutelyRain" class="wl-rain-chart-section">
            <h6 class="wl-section-title">Rain Rate (Radar + Forecast)</h6>
            <div class="wl-chart-card wl-rain-nowcast-card">
              <div class="wl-chart-scroll-wrap">
                <div class="wl-chart-ypin" ref="rainNowcastYPin"></div>
                <div class="wl-chart-scroll" ref="rainNowcastScroll">
                  <svg class="wl-chart-svg" ref="rainNowcastSvg"></svg>
                </div>
              </div>
            </div>
          </div>

          <!-- Hourly precip chart (works without minutely) -->
          <div class="wl-rain-chart-section">
            <h6 class="wl-section-title">Hourly Precipitation &mdash; &plusmn;6h</h6>
            <div class="wl-chart-card">
              <svg class="wl-chart-svg" ref="rainHourlySvg"></svg>
            </div>
          </div>

          <!-- Precip types -->
          <div v-if="todayPrecipTypes.length" class="wl-rain-precip-types">
            <h6 class="wl-section-title">Precipitation Types</h6>
            <div class="wl-rain-type-chips">
              <span v-for="pt in todayPrecipTypes" :key="pt" class="wl-rain-type-chip">
                <i class="bi" :class="pt === 'snow' ? 'bi-snow' : pt === 'freezingrain' ? 'bi-thermometer-snow' : pt === 'ice' ? 'bi-diamond-fill' : 'bi-droplet-fill'"></i>
                {{ pt }}
              </span>
            </div>
          </div>

          <!-- Upcoming rain hours -->
          <div v-if="upcomingRainHours.length" class="wl-rain-upcoming">
            <h6 class="wl-section-title">Hours with Precipitation</h6>
            <div v-for="h in upcomingRainHours" :key="h.time" class="wl-rain-hour-row" @click="openDetail(h.raw, 'hour')">
              <span class="wl-rain-hour-row__time">{{ fmtTime(h.raw) }}</span>
              <div class="wl-rain-hour-row__bar-track">
                <div class="wl-rain-hour-row__bar" :style="{ width: Math.min(h.precipprob, 100) + '%' }"
                  :class="{ 'wl-rain-hour-row__bar--heavy': h.precip > 0.1 }"></div>
              </div>
              <span class="wl-rain-hour-row__prob">{{ Math.round(h.precipprob) }}%</span>
              <span class="wl-rain-hour-row__amount" v-html="fmtRainAmount(h.precip)"></span>
              <span v-if="h.preciptype && h.preciptype.length" class="wl-rain-hour-row__type">
                <i class="bi" :class="h.preciptype.includes('snow') ? 'bi-snow' : 'bi-droplet-fill'"></i>
              </span>
            </div>
          </div>

        </section>

        <!-- ======== TAB: FORECAST ======== -->
        <section v-show="activeTab === 'forecast'" class="wl-section wl-forecast">

          <!-- Next 3 days prominent cards -->
          <div v-if="next3Days && next3Days.length" class="wl-forecast-cards">
            <h6 class="wl-section-title">3-Day Forecast</h6>
            <div v-for="day in next3Days" :key="day.datetimeEpoch" class="wl-day-card" @click="toggleDayExpand(day)">
              <div class="wl-day-card__header">
                <div class="wl-day-card__day">
                  <div>{{ fmtDayName(day) }}</div>
                  <div class="wl-day-card__date">{{ fmtDateShort(day) }}</div>
                </div>
                <div class="wl-wx-icon wl-wx-icon--sm" :class="day.icon"></div>
                <div class="wl-day-card__temps">
                  <span class="wl-day-card__hi" v-html="fmtVal(day, 'tempmax')"></span>
                  <span class="wl-day-card__lo" v-html="fmtVal(day, 'tempmin')"></span>
                </div>
                <div class="wl-day-card__precip">
                  <div><i class="bi bi-droplet-fill"></i>{{ day.precipprob > 0 ? fmtVal(day, 'precipprob') : '0%' }}</div>
                  <div v-if="day.precipprob > 50" class="wl-day-card__precip-amts"><span v-if="day.snow > 0"><i class="bi bi-snow"></i> <span v-html="fmtVal(day, 'snow')"></span></span><span v-else-if="day.precip > 0" v-html="fmtVal(day, 'precip')"></span></div>
                </div>
                <div class="wl-day-card__humidity">
                  <i class="bi bi-moisture"></i><span v-html="fmtVal(day, 'humidity')"></span>
                </div>
                <div class="wl-day-card__wind">
                  <span class="wl-wind-arrow wl-wind-arrow--sm"
                    :style="'transform:rotate(' + Math.round(day.winddir) + 'deg)'">
                    <i class="bi bi-arrow-down"></i>
                  </span>
                  <span v-html="fmtVal(day, 'windspeed', true)"></span>
                </div>
                <button class="wl-day-card__info" @click.stop="openDetail(day, 'day')" title="All weather details">
                  <i class="bi bi-info-circle"></i>
                </button>
                <i class="bi wl-day-card__chevron" :class="expandedDay === day ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
              </div>

              <!-- Expanded hourly for this day -->
              <transition name="wl-expand">
                <div v-if="expandedDay === day" class="wl-day-card__hours" @click.stop>
                  <button class="wl-day-detail-link" @click.stop="openDetail(day, 'day')">
                    <i class="bi bi-bar-chart-line"></i> View all {{ fmtDayName(day) }} details
                  </button>
                  <div v-if="day.precip > 0 || day.snow > 0" class="wl-day-expanded__precip-summary">
                    <span v-if="day.precip > 0"><i class="bi bi-droplet-fill"></i> Precip: <span v-html="fmtVal(day, 'precip')"></span></span>
                    <span v-if="day.snow > 0"><i class="bi bi-snow"></i> Snow: <span v-html="fmtVal(day, 'snow')"></span></span>
                  </div>
                  <div v-for="(hour, hi) in day.hours" :key="hi" class="wl-day-hour" @click.stop="openDetail(hour, 'hour')">
                    <span class="wl-day-hour__time">{{ fmtTime(hour) }}</span>
                    <div class="wl-wx-icon wl-wx-icon--xs" :class="hour.icon"></div>
                    <span class="wl-day-hour__temp" v-html="fmtVal(hour, 'temp')"></span>
                    <span class="wl-day-hour__precip">
                      <i class="bi bi-droplet-fill"></i>{{ hour.precipprob > 0 ? fmtVal(hour, 'precipprob') : '0%' }}
                    </span>
                    <span class="wl-day-hour__humidity">
                      <i class="bi bi-moisture"></i><span v-html="fmtVal(hour, 'humidity')"></span>
                    </span>
                    <span class="wl-day-hour__wind">
                      <span class="wl-wind-arrow wl-wind-arrow--xs"
                        :style="'transform:rotate(' + Math.round(hour.winddir) + 'deg)'">
                        <i class="bi bi-arrow-down"></i>
                      </span>
                      <span v-html="fmtVal(hour, 'windspeed', true)"></span>
                    </span>
                    <span class="wl-day-hour__cond">{{ hour.conditions }}</span>
                  </div>
                </div>
              </transition>
            </div>
          </div>

          <!-- Remaining days - same card layout -->
          <div v-if="remainingDays && remainingDays.length" class="wl-forecast-cards">
            <h6 class="wl-section-title">Extended Forecast</h6>
            <div v-for="day in remainingDays" :key="day.datetimeEpoch" class="wl-day-card" @click="toggleDayExpand(day)">
              <div class="wl-day-card__header">
                <div class="wl-day-card__day">
                  <div>{{ fmtDayName(day) }}</div>
                  <div class="wl-day-card__date">{{ fmtDateShort(day) }}</div>
                </div>
                <div class="wl-wx-icon wl-wx-icon--sm" :class="day.icon"></div>
                <div class="wl-day-card__temps">
                  <span class="wl-day-card__hi" v-html="fmtVal(day, 'tempmax')"></span>
                  <span class="wl-day-card__lo" v-html="fmtVal(day, 'tempmin')"></span>
                </div>
                <div class="wl-day-card__precip">
                  <div><i class="bi bi-droplet-fill"></i>{{ day.precipprob > 0 ? fmtVal(day, 'precipprob') : '0%' }}</div>
                  <div v-if="day.precipprob > 50" class="wl-day-card__precip-amts"><span v-if="day.snow > 0"><i class="bi bi-snow"></i> <span v-html="fmtVal(day, 'snow')"></span></span><span v-else-if="day.precip > 0" v-html="fmtVal(day, 'precip')"></span></div>
                </div>
                <div class="wl-day-card__humidity">
                  <i class="bi bi-moisture"></i><span v-html="fmtVal(day, 'humidity')"></span>
                </div>
                <div class="wl-day-card__wind">
                  <span class="wl-wind-arrow wl-wind-arrow--sm"
                    :style="'transform:rotate(' + Math.round(day.winddir) + 'deg)'">
                    <i class="bi bi-arrow-down"></i>
                  </span>
                  <span v-html="fmtVal(day, 'windspeed', true)"></span>
                </div>
                <button class="wl-day-card__info" @click.stop="openDetail(day, 'day')" title="All weather details">
                  <i class="bi bi-info-circle"></i>
                </button>
                <i class="bi wl-day-card__chevron" :class="expandedDay === day ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
              </div>
              <transition name="wl-expand">
                <div v-if="expandedDay === day" class="wl-day-card__hours" @click.stop>
                  <button class="wl-day-detail-link" @click.stop="openDetail(day, 'day')">
                    <i class="bi bi-bar-chart-line"></i> View all {{ fmtDayName(day) }} details
                  </button>
                  <div v-for="(hour, hi) in day.hours" :key="hi" class="wl-day-hour" @click.stop="openDetail(hour, 'hour')">
                    <span class="wl-day-hour__time">{{ fmtTime(hour) }}</span>
                    <div class="wl-wx-icon wl-wx-icon--xs" :class="hour.icon"></div>
                    <span class="wl-day-hour__temp" v-html="fmtVal(hour, 'temp')"></span>
                    <span class="wl-day-hour__precip">
                      <i class="bi bi-droplet-fill"></i>{{ hour.precipprob > 0 ? fmtVal(hour, 'precipprob') : '0%' }}
                    </span>
                    <span class="wl-day-hour__humidity">
                      <i class="bi bi-moisture"></i><span v-html="fmtVal(hour, 'humidity')"></span>
                    </span>
                    <span class="wl-day-hour__wind">
                      <span class="wl-wind-arrow wl-wind-arrow--xs"
                        :style="'transform:rotate(' + Math.round(hour.winddir) + 'deg)'">
                        <i class="bi bi-arrow-down"></i>
                      </span>
                      <span v-html="fmtVal(hour, 'windspeed', true)"></span>
                    </span>
                    <span class="wl-day-hour__cond">{{ hour.conditions }}</span>
                  </div>
                </div>
              </transition>
            </div>
          </div>

        </section>

        <!-- ======== TAB: HISTORY ======== -->
        <section v-show="activeTab === 'history'" class="wl-section wl-history">
          <div v-if="isLoadingHistory" class="wl-loading" style="padding: 40px 0;">
            <div class="wl-loading__spinner"></div>
            <span>Loading history...</span>
          </div>
          <div v-else-if="historyError" class="wl-error" style="margin: 16px 0;">
            <i class="bi bi-exclamation-triangle-fill"></i>
            <span>{{ historyError }}</span>
            <button class="wl-error__retry" @click="fetchHistoryData(true)">Retry</button>
          </div>
          <div v-else-if="historyDays && historyDays.length" class="wl-forecast-cards">
            <h6 class="wl-section-title">Last {{ useLlxApi ? 3 : 7 }} Days</h6>
            <div v-for="day in historyDays" :key="day.datetimeEpoch" class="wl-day-card" @click="toggleDayExpand(day)">
              <div class="wl-day-card__header">
                <div class="wl-day-card__day">
                  <div>{{ fmtDayName(day) }}</div>
                  <div class="wl-day-card__date">{{ fmtDateShort(day) }}</div>
                </div>
                <div class="wl-wx-icon wl-wx-icon--sm" :class="day.icon"></div>
                <div class="wl-day-card__temps">
                  <span class="wl-day-card__hi" v-html="fmtVal(day, 'tempmax')"></span>
                  <span class="wl-day-card__lo" v-html="fmtVal(day, 'tempmin')"></span>
                </div>
                <div class="wl-day-card__precip">
                  <div><i class="bi bi-droplet-fill"></i>{{ day.precipprob > 0 ? fmtVal(day, 'precipprob') : '0%' }}</div>
                  <div v-if="day.precipprob > 50" class="wl-day-card__precip-amts"><span v-if="day.snow > 0"><i class="bi bi-snow"></i> <span v-html="fmtVal(day, 'snow')"></span></span><span v-else-if="day.precip > 0" v-html="fmtVal(day, 'precip')"></span></div>
                </div>
                <div class="wl-day-card__humidity">
                  <i class="bi bi-moisture"></i><span v-html="fmtVal(day, 'humidity')"></span>
                </div>
                <div class="wl-day-card__wind">
                  <span class="wl-wind-arrow wl-wind-arrow--sm"
                    :style="'transform:rotate(' + Math.round(day.winddir) + 'deg)'">
                    <i class="bi bi-arrow-down"></i>
                  </span>
                  <span v-html="fmtVal(day, 'windspeed', true)"></span>
                </div>
                <button class="wl-day-card__info" @click.stop="openDetail(day, 'day')" title="All weather details">
                  <i class="bi bi-info-circle"></i>
                </button>
                <i class="bi wl-day-card__chevron" :class="expandedDay === day ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
              </div>

              <!-- Expanded hourly for this day -->
              <transition name="wl-expand">
                <div v-if="expandedDay === day" class="wl-day-card__hours" @click.stop>
                  <button class="wl-day-detail-link" @click.stop="openDetail(day, 'day')">
                    <i class="bi bi-bar-chart-line"></i> View all {{ fmtDayName(day) }} details
                  </button>
                  <div v-if="day.precip > 0 || day.snow > 0" class="wl-day-expanded__precip-summary">
                    <span v-if="day.precip > 0"><i class="bi bi-droplet-fill"></i> Precip: <span v-html="fmtVal(day, 'precip')"></span></span>
                    <span v-if="day.snow > 0"><i class="bi bi-snow"></i> Snow: <span v-html="fmtVal(day, 'snow')"></span></span>
                  </div>
                  <div v-for="(hour, hi) in day.hours" :key="hi" class="wl-day-hour" @click.stop="openDetail(hour, 'hour')">
                    <span class="wl-day-hour__time">{{ fmtTime(hour) }}</span>
                    <div class="wl-wx-icon wl-wx-icon--xs" :class="hour.icon"></div>
                    <span class="wl-day-hour__temp" v-html="fmtVal(hour, 'temp')"></span>
                    <span class="wl-day-hour__precip">
                      <i class="bi bi-droplet-fill"></i>{{ hour.precipprob > 0 ? fmtVal(hour, 'precipprob') : '0%' }}
                    </span>
                    <span class="wl-day-hour__humidity">
                      <i class="bi bi-moisture"></i><span v-html="fmtVal(hour, 'humidity')"></span>
                    </span>
                    <span class="wl-day-hour__wind">
                      <span class="wl-wind-arrow wl-wind-arrow--xs"
                        :style="'transform:rotate(' + Math.round(hour.winddir) + 'deg)'">
                        <i class="bi bi-arrow-down"></i>
                      </span>
                      <span v-html="fmtVal(hour, 'windspeed', true)"></span>
                    </span>
                    <span class="wl-day-hour__cond">{{ hour.conditions }}</span>
                  </div>
                </div>
              </transition>
            </div>
          </div>
          <div v-else-if="!isLoadingHistory" class="wl-empty" style="padding: 40px 0;">
            <i class="bi bi-clock-history" style="font-size: 2rem; opacity: 0.3;"></i>
            <p style="color: var(--wl-text-muted); margin-top: 8px;">No history data available</p>
          </div>

        </section>

        <!-- ======== TAB: SETTINGS ======== -->
        <section v-show="activeTab === 'settings'" class="wl-section wl-settings">

          <h6 class="wl-section-title">Units</h6>
          <div class="wl-settings-group">
            <div v-for="u in unitOptions" :key="u.value" class="wl-settings-option"
              :class="{ 'wl-settings-option--active': unitGroup === u.value }"
              @click="setUnitGroup(u.value)">
              <i class="bi" :class="unitGroup === u.value ? 'bi-check-circle-fill' : 'bi-circle'"></i>
              <span>{{ u.label }}</span>
            </div>
          </div>

          <h6 class="wl-section-title">Forecast Options</h6>
          <div class="wl-settings-group">
            <div class="wl-settings-toggle" @click="toggleV2Forecast">
              <div class="wl-settings-toggle__info">
                <div class="wl-settings-toggle__label">Use V2 Forecast Model</div>
                <div class="wl-settings-toggle__desc">Enable the v2 forecast model for improved accuracy</div>
              </div>
              <div class="wl-settings-switch" :class="{ 'wl-settings-switch--on': useV2Forecast }">
                <div class="wl-settings-switch__thumb"></div>
              </div>
            </div>
            <div class="wl-settings-toggle" @click="toggleLlxApi">
              <div class="wl-settings-toggle__info">
                <div class="wl-settings-toggle__label">Use LLX API</div>
                <div class="wl-settings-toggle__desc">Switch to the Timeline LLX Weather API for ultra-fast, low-latency responses (&lt;40ms). Best for real-time apps.</div>
              </div>
              <div class="wl-settings-switch" :class="{ 'wl-settings-switch--on': useLlxApi }">
                <div class="wl-settings-switch__thumb"></div>
              </div>
            </div>
          </div>

          <h6 class="wl-section-title">Map</h6>
          <div class="wl-settings-group">
            <div class="wl-settings-toggle" @click="setPrecipSource(precipSource === 'radar' ? null : 'radar')">
              <div class="wl-settings-toggle__info">
                <div class="wl-settings-toggle__label">Radar Only Precipitation</div>
                <div class="wl-settings-toggle__desc">Use radar-only data for the precipitation map layer. When off, a hybrid of radar and forecast model data is used.</div>
              </div>
              <div class="wl-settings-switch" :class="{ 'wl-settings-switch--on': precipSource === 'radar' }">
                <div class="wl-settings-switch__thumb"></div>
              </div>
            </div>
            <div class="wl-settings-toggle">
              <div class="wl-settings-toggle__info">
                <div class="wl-settings-toggle__label">Animation Speed</div>
              </div>
              <div class="wl-speed-picker">
                <button type="button" class="wl-speed-picker__btn" :class="{ 'wl-speed-picker__btn--active': playSpeed === 1200 }" @click="setPlaySpeed(1200)">Slow</button>
                <button type="button" class="wl-speed-picker__btn" :class="{ 'wl-speed-picker__btn--active': playSpeed === 800 }" @click="setPlaySpeed(800)">Normal</button>
                <button type="button" class="wl-speed-picker__btn" :class="{ 'wl-speed-picker__btn--active': playSpeed === 400 }" @click="setPlaySpeed(400)">Fast</button>
              </div>
            </div>
            <div class="wl-settings-toggle">
              <div class="wl-settings-toggle__info">
                <div class="wl-settings-toggle__label">Home Page Map</div>
                <div class="wl-settings-toggle__desc">Choose how the map appears on the main screen</div>
              </div>
              <div class="wl-speed-picker">
                <button type="button" class="wl-speed-picker__btn" :class="{ 'wl-speed-picker__btn--active': mapDisplayMode === 'full' }" @click="setMapDisplayMode('full')">Full</button>
                <button type="button" class="wl-speed-picker__btn" :class="{ 'wl-speed-picker__btn--active': mapDisplayMode === 'small' }" @click="setMapDisplayMode('small')">Small</button>
                <button type="button" class="wl-speed-picker__btn" :class="{ 'wl-speed-picker__btn--active': mapDisplayMode === 'none' }" @click="setMapDisplayMode('none')">None</button>
              </div>
            </div>
          </div>

          <h6 class="wl-section-title">Display</h6>
          <div class="wl-settings-group">
            <div class="wl-settings-toggle" @click="darkMode = !darkMode; saveDarkMode()">
              <div class="wl-settings-toggle__info">
                <div class="wl-settings-toggle__label">Dark Mode</div>
                <div class="wl-settings-toggle__desc">Use dark theme across the app</div>
              </div>
              <div class="wl-settings-switch" :class="{ 'wl-settings-switch--on': darkMode }">
                <div class="wl-settings-switch__thumb"></div>
              </div>
            </div>
            <div class="wl-settings-toggle" @click="use24HourTime = !use24HourTime; saveTimeFormat()">
              <div class="wl-settings-toggle__info">
                <div class="wl-settings-toggle__label">24-Hour Time</div>
                <div class="wl-settings-toggle__desc">{{ use24HourTime ? 'Using 24-hour format (e.g. 14:00)' : 'Using 12-hour format (e.g. 2:00 PM)' }}</div>
              </div>
              <div class="wl-settings-switch" :class="{ 'wl-settings-switch--on': use24HourTime }">
                <div class="wl-settings-switch__thumb"></div>
              </div>
            </div>
          </div>

          <h6 class="wl-section-title">About</h6>
          <div class="wl-settings-about">
            <div class="wl-settings-about__logo">
              <i class="bi bi-cloud-sun-fill" style="font-size: 32px; color: var(--wl-accent, #3b82f6);"></i>
            </div>
            <div class="wl-settings-about__title">Powered by Visual Crossing Corporation</div>
            <p class="wl-settings-about__text">
              Visual Crossing provides hyper-local weather data and global forecasting APIs designed for developers, researchers, and enterprise teams.
              With 50+ years of historical data and industry-leading accuracy, Visual Crossing powers smarter decisions across every industry &mdash;
              from agriculture and logistics to energy and construction.
            </p>
            <div class="wl-settings-about__links">
              <a href="https://www.visualcrossing.com/" target="_blank" rel="noopener" class="wl-settings-link">
                <i class="bi bi-house-door-fill"></i> www.visualcrossing.com
              </a>
              <a href="https://www.visualcrossing.com/weather-api" target="_blank" rel="noopener" class="wl-settings-link">
                <i class="bi bi-cloud-fill"></i> Weather API
              </a>
              <a href="https://www.visualcrossing.com/weather-data" target="_blank" rel="noopener" class="wl-settings-link">
                <i class="bi bi-database-fill"></i> Weather Data
              </a>
              <a href="https://www.visualcrossing.com/resources/documentation/weather-api/timeline-weather-api/" target="_blank" rel="noopener" class="wl-settings-link">
                <i class="bi bi-book-fill"></i> API Documentation
              </a>
              <a href="https://www.visualcrossing.com/resources/documentation/weather-api/timeline-llx-weather-api/" target="_blank" rel="noopener" class="wl-settings-link">
                <i class="bi bi-lightning-charge-fill"></i> LLX API Documentation
              </a>
              <a href="https://www.visualcrossing.com/sign-up" target="_blank" rel="noopener" class="wl-settings-link">
                <i class="bi bi-person-plus-fill"></i> Create Free Account
              </a>
            </div>
          </div>

          <h6 class="wl-section-title">Debug</h6>
          <div class="wl-settings-group">
            <div class="wl-settings-toggle" @click="sendTestNotification">
              <div class="wl-settings-toggle__info">
                <div class="wl-settings-toggle__label"><i class="bi bi-bell" style="margin-right:4px;"></i>Test Notification</div>
                <div class="wl-settings-toggle__desc">Schedule a test weather alert in 30 seconds. Background the app to see it.</div>
              </div>
              <div v-if="testNotifStatus" style="font-size:11px; color: var(--wl-text-muted); white-space:nowrap;">{{ testNotifStatus }}</div>
              <i v-else class="bi bi-chevron-right" style="color: var(--wl-text-light);"></i>
            </div>
          </div>
        </section>

        <!-- ======== MAP TAB (shown when map not full on home) ======== -->
        <section v-if="mapDisplayMode !== 'full'" v-show="activeTab === 'map'" class="wl-section wl-map-tab">
          <div class="wl-map-wrap wl-map-wrap--tab" ref="mapTabWrap">
            <!-- map container is moved here dynamically from home map -->

            <!-- Map layer picker overlay -->
            <div class="wl-map-layers">
              <button class="wl-map-layer-btn wl-map-layers-toggle" @click="showLayerPicker = !showLayerPicker" title="Layers">
                <i class="bi bi-layers-fill"></i>
                <span class="wl-map-layer-btn__label">Layers</span>
                <i class="bi" :class="showLayerPicker ? 'bi-chevron-up' : 'bi-chevron-down'" style="font-size:12px;margin-left:2px;"></i>
              </button>
              <div v-if="showLayerPicker" class="wl-map-layers-panel">
                <template v-for="layer in mapLayerOptions" :key="layer.id">
                  <button class="wl-map-layer-btn"
                    :class="{ 'wl-map-layer-btn--active': activeMapLayer === layer.id }" @click="switchMapLayer(layer.id)"
                    :title="layer.label">
                    <i :class="layer.icon"></i>
                    <span>{{ layer.shortLabel }}</span>
                  </button>
                </template>
              </div>
              <!-- Overlay toggles: alerts, hail, lightning -->
              <div class="wl-map-overlay-bar">
                <button class="wl-map-overlay-btn" :class="{ 'wl-map-overlay-btn--active': overlayAlerts }" @click="toggleOverlay('alerts')" title="Weather Alerts">
                  <i class="bi bi-exclamation-triangle-fill"></i>
                </button>
                
                <button class="wl-map-overlay-btn" :class="{ 'wl-map-overlay-btn--active': overlayHail }" @click="toggleOverlay('hail')" title="Hail">
                  <i class="bi bi-circle-fill"></i>
                </button>
                <button class="wl-map-overlay-btn" :class="{ 'wl-map-overlay-btn--active': overlayLightning }" @click="toggleOverlay('lightning')" title="Lightning">
                  <i class="bi bi-lightning-fill"></i>
                </button>
                <!-- Alert filter -->
                <template v-if="overlayAlerts && alertFilterTypes.length > 0">
                  <div class="wl-map-overlay-divider"></div>
                  <button class="wl-map-overlay-btn" :class="{ 'wl-map-overlay-btn--active': alertHiddenCount > 0 }" @click.stop="alertFilterOpen = !alertFilterOpen" title="Filter alert types">
                    <i class="bi bi-funnel-fill"></i>
                    <span v-if="alertHiddenCount > 0" class="wl-alert-badge">{{ alertHiddenCount }}</span>
                  </button>
                </template>
              </div>
              <!-- Alert filter dropdown -->
              <div v-if="alertFilterOpen" class="wl-alert-dropdown">
                <label v-for="t in alertFilterTypes" :key="t.event" class="wl-alert-filter-item">
                  <input type="checkbox" :checked="t.visible" @change="onAlertFilterChange(t.event, $event.target.checked)">
                  <span class="wl-alert-swatch" :style="{ backgroundColor: t.color }"></span>
                  <span class="wl-alert-filter-label">{{ t.label }} ({{ t.count }})</span>
                </label>
                <div class="wl-alert-filter-actions">
                  <button @click="alertFilterShowAll">Show all</button>
                  <button @click="alertFilterHideAll">Hide all</button>
                </div>
              </div>
            </div>

            <!-- Recenter button (under zoom controls, top-right) -->
            <button class="wl-map-locate-btn" @click="updateMapView" title="Recenter on location">
              <i class="bi bi-geo-alt-fill"></i>
            </button>

            <!-- Map time controls -->
            <div class="wl-map-time">
              <button class="wl-map-time__btn" @click="stepTime(-computedStepInterval)" :disabled="isPlaying">
                <i class="bi bi-chevron-left"></i>
              </button>
              <button class="wl-map-time__play" @click="playPause">
                <i class="bi" :class="isPlaying ? 'bi-pause-fill' : 'bi-play-fill'"></i>
              </button>
              <div class="wl-map-time__gap"></div>
              <div class="wl-map-time__display wl-map-time__display--clickable" @click="openMapDateTimePicker" title="Jump to date & time">
                <div class="wl-map-time__time">{{ mapTimeDisplay }}</div>
                <div class="wl-map-time__date">{{ mapDateDisplay }}</div>
              </div>
              <div class="wl-map-time__gap"></div>
              <button class="wl-map-time__btn" @click="stepTime(computedStepInterval)" :disabled="isPlaying">
                <i class="bi bi-chevron-right"></i>
              </button>
              <button class="wl-map-time__btn wl-map-time__reset" @click="resetToNow" :disabled="isPlaying"
                title="Reset to now">
                <i class="bi bi-arrow-clockwise"></i>
              </button>
            </div>
            <!-- Date/time picker overlay (map tab) -->
            <div v-if="showMapDateTimePicker" class="wl-map-dtpicker-overlay" @click.self="showMapDateTimePicker = false">
              <div class="wl-map-dtpicker">
                <div class="wl-map-dtpicker__header">
                  <strong>Jump to date &amp; time</strong>
                  <button class="wl-map-dtpicker__close" @click="showMapDateTimePicker = false"><i class="bi bi-x-lg"></i></button>
                </div>
                <input type="datetime-local" class="wl-map-dtpicker__input" v-model="mapDateTimePickerValue" />
                <div class="wl-map-dtpicker__actions">
                  <button class="wl-btn wl-btn--outline" @click="showMapDateTimePicker = false">Cancel</button>
                  <button class="wl-btn wl-btn--primary" @click="applyMapDateTimePicker">Go</button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- ======== CHARTS TAB ======== -->
        <section v-show="activeTab === 'charts'" class="wl-section wl-charts">
          <h6 class="wl-section-title">Temperature Range</h6>
          <div class="wl-chart-card">
            <svg ref="chartTemp" class="wl-chart-svg"></svg>
          </div>

          <h6 class="wl-section-title">Precipitation Outlook</h6>
          <div class="wl-chart-card">
            <svg ref="chartPrecip" class="wl-chart-svg"></svg>
          </div>

          <h6 class="wl-section-title">Wind Forecast</h6>
          <div class="wl-chart-card">
            <svg ref="chartWind" class="wl-chart-svg"></svg>
          </div>

          <h6 class="wl-section-title">Humidity &amp; Cloud Cover</h6>
          <div class="wl-chart-card">
            <svg ref="chartAtmos" class="wl-chart-svg"></svg>
          </div>

          <h6 class="wl-section-title">UV Index</h6>
          <div class="wl-chart-card">
            <svg ref="chartUV" class="wl-chart-svg"></svg>
          </div>

          <h6 class="wl-section-title">Daylight Hours</h6>
          <div class="wl-chart-card">
            <svg ref="chartDaylight" class="wl-chart-svg"></svg>
          </div>

          <h6 class="wl-section-title">Pressure</h6>
          <div class="wl-chart-card">
            <svg ref="chartPressure" class="wl-chart-svg"></svg>
          </div>

          <h6 class="wl-section-title">Hourly Precipitation Detail</h6>
          <div class="wl-chart-card">
            <svg ref="chartHourlyPrecip" class="wl-chart-svg"></svg>
          </div>

          <h6 class="wl-section-title">Cumulative Rainfall</h6>
          <div class="wl-chart-card">
            <svg ref="chartCumulativeRain" class="wl-chart-svg"></svg>
          </div>
        </section>

      </main>

      <!-- Bottom tab bar -->
      <nav class="wl-tabbar" v-show="!mapFullscreen">
        <button v-for="tab in visibleTabs" :key="tab.id" class="wl-tabbar__btn"
          :class="{ 'wl-tabbar__btn--active': activeTab === tab.id }" @click="activeTab = tab.id">
          <i class="bi" :class="tab.icon"></i>
          <span>{{ tab.label }}</span>
          <span v-if="tab.id === 'now' && alerts && alerts.length" class="wl-tabbar__badge">{{ alerts.length }}</span>
        </button>
      </nav>

    </template>

    <!-- ======== WEATHER DETAIL DIALOG ======== -->
    <template v-if="detailRecord">
      <div class="wl-modal-backdrop" @click="detailRecord = null"></div>
      <div class="wl-detail">
        <div class="wl-detail__content">
          <!-- Header -->
          <div class="wl-detail__header">
            <div class="wl-detail__title">
              <div class="wl-wx-icon wl-wx-icon--md" :class="detailRecord.icon"></div>
              <div>
                <div class="wl-detail__datetime">
                  <template v-if="detailType === 'hour'">{{ fmtTime(detailRecord) }} &middot; {{ detailDayLabel }}</template>
                  <template v-else>{{ fmtDayName(detailRecord) }} &middot; {{ fmtDateShort(detailRecord) }}</template>
                </div>
                <div class="wl-detail__conditions">{{ detailRecord.conditions || '—' }}</div>
              </div>
            </div>
            <button class="wl-modal__close" @click="detailRecord = null">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>

          <!-- Hero temp -->
          <div class="wl-detail__hero">
            <template v-if="detailType === 'day'">
              <span class="wl-detail__temp-hi" v-html="fmtVal(detailRecord, 'tempmax')"></span>
              <span class="wl-detail__temp-sep">/</span>
              <span class="wl-detail__temp-lo" v-html="fmtVal(detailRecord, 'tempmin')"></span>
            </template>
            <template v-else>
              <span class="wl-detail__temp-main" v-html="fmtVal(detailRecord, 'temp')"></span>
              <span v-if="detailRecord.feelslike != null && Math.round(detailRecord.feelslike) !== Math.round(detailRecord.temp)" class="wl-detail__feelslike">
                Feels <span v-html="fmtVal(detailRecord, 'feelslike')"></span>
              </span>
            </template>
          </div>

          <div v-if="detailRecord.description" class="wl-detail__desc">{{ detailRecord.description }}</div>

          <!-- Element sections -->
          <div class="wl-detail__scroll">
            <template v-for="section in detailSections" :key="section.title">
              <div v-if="sectionHasData(section)" class="wl-detail__section">
                <div class="wl-detail__section-title">
                  <i class="bi" :class="section.icon"></i>
                  {{ section.title }}
                </div>
                <div class="wl-detail__grid">
                  <template v-for="el in section.elements" :key="el.key">
                    <div v-if="hasValue(detailRecord[el.key])" class="wl-detail__item" :class="{ 'wl-detail__item--full': el.key === 'moonphase' }">
                      <div class="wl-detail__item-label">{{ el.label }}</div>
                      <div class="wl-detail__item-value">
                        <template v-if="el.key === 'winddir' || el.key === 'wavedir' || el.key === 'swelldir' || el.key === 'winddir50' || el.key === 'winddir80' || el.key === 'winddir100'">
                          <span class="wl-wind-arrow wl-wind-arrow--xs" :style="'transform:rotate(' + Math.round(detailRecord[el.key]) + 'deg)'">
                            <i class="bi bi-arrow-down"></i>
                          </span>
                          {{ getCompassDir(detailRecord[el.key]) }} <span style="font-size:0.75em;opacity:0.7">({{ Math.round(detailRecord[el.key]) }}°)</span>
                        </template>
                        <template v-else-if="el.key === 'preciptype'">
                          {{ Array.isArray(detailRecord[el.key]) ? detailRecord[el.key].join(', ') : detailRecord[el.key] }}
                        </template>
                        <template v-else-if="el.key === 'source'">
                          {{ detailRecord[el.key] }}
                        </template>
                        <template v-else-if="el.key === 'sunriseEpoch' || el.key === 'sunsetEpoch' || el.key === 'moonriseEpoch' || el.key === 'moonsetEpoch'">
                          {{ fmtSunTime(detailRecord[el.key]) }}
                        </template>
                        <template v-else-if="el.key === 'moonphase'">
                          {{ detailRecord[el.key] }} — {{ getMoonPhaseLabel(detailRecord[el.key]) }}
                        </template>
                        <template v-else>
                          <span v-html="fmtVal(detailRecord, el.key, false, el.format)"></span>
                        </template>
                      </div>
                    </div>
                  </template>
                </div>
              </div>
            </template>

            <!-- Source / Data attribution -->
            <div class="wl-detail__section wl-detail__section--source" v-if="detailRecord.source">
              <div class="wl-detail__section-title">
                <i class="bi bi-database"></i>
                Data Source
              </div>
              <div class="wl-detail__source">{{ detailRecord.source }}</div>
            </div>

            <div class="wl-detail__powered">
              Powered by <strong>Visual Crossing Weather</strong>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- ======== ALERT DETAIL MODAL ======== -->
    <template v-if="expandedAlert">
      <div class="wl-modal-backdrop" @click="expandedAlert = null"></div>
      <div class="wl-modal">
        <div class="wl-modal__content">
          <div class="wl-modal__header">
            <div class="wl-alert-badge wl-alert-badge--lg" :class="getAlertBadgeClass(expandedAlert)">
              <i :class="getAlertIconClass(expandedAlert)"></i>
              {{ expandedAlert.event }}
            </div>
            <button class="wl-modal__close" @click="expandedAlert = null">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>
          <h6 v-if="expandedAlert.headline" class="wl-modal__headline">{{ expandedAlert.headline }}</h6>
          <div class="wl-modal__body" v-html="formatAlertDesc(expandedAlert)"></div>
        </div>
      </div>
    </template>

  </div>
</template>

<script>
import '/src/weather-map/WxTileLayer.js';
import { VcGlobal } from '/src/visualcrossing-core/vcglobal_conn.js';

const COMPASS = ['N','NNE','NE','ENE','E','ESE','SE','SSE','S','SSW','SW','WSW','W','WNW','NW','NNW'];

const ELEMENT_INFO = {
  temp: { title: 'Temp', fixed: 0 },
  tempmax: { title: 'Max', baseelement: 'temp', fixed: 0 },
  tempmin: { title: 'Min', baseelement: 'temp', fixed: 0 },
  feelslike: { title: 'Feels like', baseelement: 'temp', fixed: 0 },
  feelslikemax: { title: 'Feels like max', baseelement: 'temp', fixed: 0 },
  feelslikemin: { title: 'Feels like min', baseelement: 'temp', fixed: 0 },
  precip: { title: 'Rain' },
  precipprob: { title: 'Precip %', baseelement: 'percent', fixed: 0 },
  snow: { title: 'Snow' },
  snowdepth: { title: 'Snow depth', baseelement: 'snow' },
  windspeed: { title: 'Wind', baseelement: 'wind' },
  wind: { title: 'Wind', baseelement: 'wind' },
  windgust: { title: 'Gust', baseelement: 'wind' },
  humidity: { title: 'Humidity', baseelement: 'percent' },
  dew: { title: 'Dew point', baseelement: 'temp' },
  pressure: { title: 'Pressure' },
  cloudcover: { title: 'Cloud cover', baseelement: 'percent' },
  solarradiation: { title: 'Solar radiation' },
  uvindex: { title: 'UV index', fixed: 0 },
  uvindex2: { title: 'UV index', fixed: 0 },
  reflectivity: { title: 'Reflectivity' },
  pm2p5: { title: 'Particulates (2.5)' },
  pm10: { title: 'Particulates (10)' },
  pm1: { title: 'Particulates (1.0)' },
  co: { title: 'CO' },
  so2: { title: 'SO2' },
  no2: { title: 'NO2' },
  o3: { title: 'Ozone' },
};

const UNITGROUPS = {
  base: { temp: 'K', precip: 'mm', snow: 'cm', wind: 'm/s', visibility: 'km', percent: '%', distance: 'm', pressure: 'mb' },
  us: { temp: '\u2109', temp_u: '\u2109', precip: 'in', snow: 'in', wind: 'mph', visibility: 'mi', percent: '%', distance: 'mi', pressure: 'mb' },
  uk: { temp: '\u2103', temp_u: '\u2103', precip: 'mm', snow: 'cm', wind: 'mph', visibility: 'mi', percent: '%', pressure: 'mb' },
  metric: { temp: '\u2103', temp_u: '\u2103', precip: 'mm', snow: 'cm', wind: 'kph', visibility: 'km', percent: '%', distance: 'km', pressure: 'mb' },
};

function _formatDateTime(epochSecsOrDate, tz, options) {
  var date = epochSecsOrDate instanceof Date ? epochSecsOrDate : new Date(epochSecsOrDate * 1000);
  options = options || { month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric', hour12: false, timeZone: tz };
  if (tz) options.timeZone = tz;
  try { return new Intl.DateTimeFormat('en-US', options).format(date); }
  catch (e) { return '?'; }
}

function _formatTime(epochSecs, tz, use24Hour) {
  if (use24Hour === false) {
    return _formatDateTime(epochSecs, tz, { hour: 'numeric', hour12: true, timeZone: tz });
  }
  return _formatDateTime(epochSecs, tz, { hour: 'numeric', hourCycle: 'h23', timeZone: tz });
}

function _formatDateVeryShort(epochSecs, tz) {
  return _formatDateTime(epochSecs, tz, { weekday: 'short', timeZone: tz });
}

function _isSameDate(date1EpochSecs, date2, tz) {
  var opts = { year: 'numeric', month: 'numeric', day: 'numeric', timeZone: tz };
  return _formatDateTime(date1EpochSecs, tz, opts) === _formatDateTime(date2, tz, opts);
}

function _isToday(epochSecs, tz) { return _isSameDate(epochSecs, new Date(), tz); }

function _isYesterday(epochSecs, tz) {
  var d = new Date(); d.setDate(d.getDate() - 1);
  return _isSameDate(epochSecs, d, tz);
}

function _getCompassDirection(degrees) {
  if (!degrees && degrees !== 0) return '';
  var i = Math.round(degrees / 22.5);
  if (i < 0) i += 16;
  if (i > 15) i -= 16;
  return COMPASS[i];
}

function _getMoonPhaseDesc(moonphase, simple) {
  if (!moonphase && moonphase !== 0) return '-';
  var illum = Math.round(200 * (moonphase <= 0.5 ? moonphase : (1 - moonphase))) + '%';
  if (simple) return illum;
  if (moonphase === 0 || moonphase === 1) return 'New moon';
  if (moonphase < 0.25) return 'Waxing Crescent';
  if (moonphase === 0.25) return 'First Quarter';
  if (moonphase < 0.5) return 'Waxing Gibbous';
  if (moonphase === 0.5) return 'Full moon';
  if (moonphase < 0.75) return 'Waning Gibbous';
  if (moonphase === 0.75) return 'Last Quarter';
  if (moonphase < 1.0) return 'Waning Crescent';
  return moonphase + '?';
}

function _getAqiText(aqi, shortForm) {
  if (!aqi && aqi !== 0) return '-';
  if (aqi < 51) return shortForm ? 'OK' : 'Good';
  if (aqi < 101) return shortForm ? 'Mod' : 'Moderate';
  if (aqi < 151) return shortForm ? 'Poor' : 'Unhealthy for Sensitive Groups';
  if (aqi < 201) return shortForm ? 'Poor' : 'Unhealthy';
  if (aqi < 300) return shortForm ? 'Bad' : 'Very Unhealthy';
  return shortForm ? 'Bad' : 'Hazardous';
}

function _formatElementValue(value, element, unitgroup, fixed, emptyvalue, unicode, noSymbol) {
  if (!value && value !== 0) return emptyvalue || '-';
  if (typeof value === 'string') return value;
  if (ELEMENT_INFO[element] && ELEMENT_INFO[element].baseelement) element = ELEMENT_INFO[element].baseelement;
  unitgroup = unitgroup || 'us';
  if (!fixed && fixed !== 0) {
    if (ELEMENT_INFO[element] && (ELEMENT_INFO[element].fixed || ELEMENT_INFO[element].fixed === 0)) {
      fixed = ELEMENT_INFO[element].fixed;
    } else {
      var absValue = Math.abs(value);
      fixed = (absValue > 0 && absValue < 1) ? 2 : (absValue > 0 && absValue < 10) ? 1 : 0;
    }
  }
  // Handle trace amounts for precip and snow
  if ((element === 'precip' || element === 'snow') && value > 0) {
    var formatted = value.toFixed(fixed);
    if (parseFloat(formatted) === 0) {
      var sym = unicode ? UNITGROUPS[unitgroup][element + '_u'] : null;
      if (!sym) sym = UNITGROUPS[unitgroup][element];
      return '<' + (0).toFixed(fixed).replace(/0$/, '1') + (sym || '');
    }
  }
  // Eliminate negative zero (e.g. -0.4 rounded to 0 decimal places)
  var rounded = +(value.toFixed(fixed));
  var display = rounded === 0 ? (0).toFixed(fixed) : value.toFixed(fixed);
  if (!noSymbol) {
    var symbol = unicode ? UNITGROUPS[unitgroup][element + '_u'] : null;
    if (!symbol) symbol = UNITGROUPS[unitgroup][element];
    if (symbol) return display + symbol;
  }
  return display;
}

function _dbzToRainRate(dbz, unitGroup) {
  var rainRate = 0.0365 * Math.pow(10, (0.625 * dbz / 10));
  var rate = Math.round(rainRate * 10) / 10;
  if (unitGroup === 'us') {
    rate = rate * 0.0393701;
    if (rate < 0.01) return '<0.01 in/h';
    return rate.toFixed(2) + ' in/h';
  }
  if (rate < 0.1) return '<0.1 mm/h';
  return rate.toFixed(1) + ' mm/h';
}

function _resolveLocation(data, location, name) {
  // Prefer the geocoded name, then fall back to resolvedAddress
  var display = name || location || (data && data.resolvedAddress);
  if (!display) return display;
  // Trim long display names: drop full country name if country code available
  if (display.length > 30) {
    // Try to shorten "City, State, United Kingdom" → "City, State, UK"
    var parts = display.split(',').map(function (p) { return p.trim(); });
    if (parts.length >= 3) {
      // Keep first two parts only (City, State/Region)
      display = parts[0] + ', ' + parts[1];
    }
  }
  var maxLength = 40;
  if (display.length > maxLength) {
    return '...' + display.substr(display.length - maxLength, maxLength);
  }
  return display;
}

function _dbzColor(v) {
  var dbzColors = ['#e0f8e0','#ccffcc','#aaffaa','#66ff66','#33cc33','#339900','#99ff00','#ffff00','#ffcc00','#ff9900','#ff6600','#ff0000','#cc0000','#cc00cc','#990099','#660066','#000000'];
  var index = Math.floor((dbzColors.length - 1) * v / 100);
  index = Math.max(0, Math.min(index, dbzColors.length - 1));
  return dbzColors[index];
}

function _dbzColorIntervals() {
  var n = 17, colors = [];
  for (var i = 0; i < n; i++) {
    colors.push({ offset: (i * 100 / (n - 1)).toFixed(1) + '%', color: _dbzColor(i * (100 / (n - 1))) });
  }
  return colors;
}

// Numeric rain rate in mm/hr from dBZ (Marshall-Palmer Z-R)
function _dbzToRainRateNum(dbz) {
  if (!dbz || dbz <= 0) return 0;
  var Z = Math.pow(10, dbz / 10);
  return Math.pow(Z / 200, 1 / 1.6);
}

/** Get rain rate (mm/hr) from a minutely data point.
 *  Prefer the direct preciprate field if available; fall back to reflectivity→Z-R conversion. */
function _minutelyRainRate(d) {
  if (d.preciprate != null && d.preciprate > 0) return d.preciprate;
  if (d.reflectivity != null && d.reflectivity > 0) return _dbzToRainRateNum(d.reflectivity);
  return 0;
}

/** Format a rain rate (already in mm/hr) to a display string like "2.5 mm/h" or "0.10 in/h". */
function _formatRainRate(rateMMH, unitGroup) {
  if (unitGroup === 'us') {
    var rateIn = rateMMH * 0.0393701;
    if (rateIn < 0.01) return '<0.01 in/h';
    return rateIn.toFixed(2) + ' in/h';
  }
  if (rateMMH < 0.1) return '<0.1 mm/h';
  return rateMMH.toFixed(1) + ' mm/h';
}

function _rainRateColorIntervals(maxRate, maxRateMMH, legend) {
  if (legend && legend.length) return _legendColorIntervals(legend, maxRate);
  // Fallback to old dBZ-derived colors (needs mm/h scale)
  var rate = maxRateMMH || maxRate;
  var n = 17, colors = [];
  for (var i = 0; i < n; i++) {
    var r = (i / (n - 1)) * rate;
    var dbz = r > 0 ? 10 * Math.log10(200 * Math.pow(r, 1.6)) : 0;
    colors.push({ offset: (i * 100 / (n - 1)).toFixed(1) + '%', color: _dbzColor(Math.max(0, Math.min(100, dbz))) });
  }
  return colors;
}

/** Convert a rain rate (mm/h) to a color using a dBZ lookup */
function _rainRateToColor(rateMMH) {
  var dbz = rateMMH > 0 ? 10 * Math.log10(200 * Math.pow(rateMMH, 1.6)) : 0;
  return _dbzColor(Math.max(0, Math.min(100, dbz)));
}

/** Build SVG gradient stops from the cached map legend.
 *  legend = [{min, max, color}, ...] with rates in user units.
 *  Maps each legend bin's midpoint to a gradient offset based on maxRate. */
function _legendColorIntervals(legend, maxRate) {
  if (!legend || !legend.length || maxRate <= 0) return _dbzColorIntervals();
  var stops = [];
  // First stop at 0 — transparent / lightest color
  stops.push({ offset: '0%', color: legend[0].color });
  for (var i = 0; i < legend.length; i++) {
    var mid = (legend[i].min + legend[i].max) / 2;
    var pct = Math.min(mid / maxRate * 100, 100);
    stops.push({ offset: pct.toFixed(1) + '%', color: legend[i].color });
  }
  // Ensure we reach 100%
  var lastColor = legend[legend.length - 1].color;
  if (parseFloat(stops[stops.length - 1].offset) < 100) {
    stops.push({ offset: '100%', color: lastColor });
  }
  return stops;
}

function _debounce(fn, delay) {
  var timeoutID = null;
  return function () {
    clearTimeout(timeoutID);
    var args = arguments, that = this;
    timeoutID = setTimeout(function () { fn.apply(that, args); }, delay);
  };
}
import { VcLocationsStorage } from '/src/visualcrossing-core/vclocationsstorage';
import { VcFindMyLocation } from '/src/visualcrossing-core/vcfindmylocation';
import { VcGeocoder } from '/src/visualcrossing-core/vcgeocoder';
import { VcClipboardUtils } from '/src/visualcrossing-core/vcclipboardutils';
import { getAlertByType, getAlertClass, getAlertIcon } from '/src/data/weatherAlerts.js';
import * as d3 from 'd3';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { patchLeaflet } from '/src/utils/leaflet-patches.js';
patchLeaflet();
import { end } from '@popperjs/core';

export default {
  name: 'WeatherLive',

  data() {
    return {
      // --- UI state ---
      activeTab: 'now',
      darkMode: false,
      isMobile: false,
      isLoading: true,
      loadingStatus: '',
      isRefreshing: false,
      pullDistance: 0,
      pullRefreshing: false,
      _pullReleasing: false,
      errorStatus: null,
      showLocationSearch: false,
      showUnitMenu: false,
      mapFullscreen: false,
      useV2Forecast: true,
      expandedAlert: null,
      expandedDay: null,
      detailRecord: null,
      detailType: 'hour',
      locationQuery: '',

      // --- Weather state ---
      selectedLocation: null,
      isGpsLocation: false,
      gpsLocating: false,
      unitGroup: 'us',
      wxdata: null,
      locationsHistory: null,

      // --- Map state ---
      leafletMap: null,
      circleMarker: null,
      wxLayers: {},
      activeMapLayer: 'precip',
      mapDateTime: null,
      isPlaying: false,
      playInterval: null,
      playSpeed: 800,
      activeLayerInterval: 10,
      precipSource: null, // 'radar' or null (hybrid)
      mapDisplayMode: 'small', // 'full', 'small', or 'none'
      use24HourTime: true, // true = 24h format, false = 12h AM/PM format

      // --- Refresh tracking ---
      lastUpdated: null,
      nowTick: Date.now(),

      // --- Session ---
      masterKey: 'GTAK2GNZ26E4N45F7Z6KUVS2A',
      sessionId: null,
      showSignInForm: false,
      signInUsername: '',
      signInPassword: '',
      showSignInPassword: false,
      signInError: null,
      signInLoading: false,

      // --- Notification test ---
      testNotifStatus: null,

      // --- Chart ---
      updateMapTimeLine: null,
      _rainNowcastTimeMarker: null,
      _rainHourlyTimeMarker: null,
      _precipLegend: null,  // cached legend from map tile service [{min,max,color}...]

      // --- History ---
      historyData: null,
      isLoadingHistory: false,
      historyError: null,
      _historyLastDate: null,
      _historyRefreshTimer: null,

      // --- LLX API ---
      useLlxApi: false,

      // --- Config ---
      tabs: [
        { id: 'now', label: 'Now', icon: 'bi-broadcast-pin' },
        { id: 'rain', label: 'Rain', icon: 'bi-cloud-rain-heavy-fill' },
        { id: 'forecast', label: 'Forecast', icon: 'bi-calendar3' },
        { id: 'history', label: 'History', icon: 'bi-clock-history' },
        { id: 'charts', label: 'Charts', icon: 'bi-graph-up' },
        { id: 'settings', label: 'Settings', icon: 'bi-gear' },
      ],
      unitOptions: [
        { value: 'us', label: 'US (°F, mph)' },
        { value: 'metric', label: 'Metric (°C, km/h)' },
        { value: 'uk', label: 'UK (°C, mph)' },
        { value: 'base', label: 'Base (K, m/s)' },
      ],
      mapLayerOptions: [
        { id: 'precip', label: 'Precipitation', shortLabel: 'Rain', icon: 'bi bi-cloud-rain-fill', element: 'precipcomposite', interval: 10 },
        { id: 'temp', label: 'Temperature', shortLabel: 'Temp', icon: 'bi bi-thermometer-half', element: 'temp', interval: 60 },
        { id: 'wind', label: 'Wind', shortLabel: 'Wind', icon: 'bi bi-wind', element: 'wind', interval: 60 },
        { id: 'lightning', label: 'Lightning', shortLabel: 'Ltng', icon: 'bi bi-lightning-fill', element: 'lightning', interval: 10 },
        { id: 'cloud', label: 'Cloud Cover', shortLabel: 'Cloud', icon: 'bi bi-clouds-fill', element: 'cloudcover', interval: 60 },
        { id: 'pressure', label: 'Pressure', shortLabel: 'Pres', icon: 'bi bi-speedometer2', element: 'sealevelpressure', interval: 60 },
      ],
      // Overlay toggles
      overlayLightning: false,
      overlayHail: false,
      overlayAlerts: false,
      _overlayLightningLayer: null,
      _overlayHailLayer: null,
      _overlayAlertsLayer: null,
      alertFilterTypes: [],
      alertHiddenCount: 0,
      alertFilterOpen: false,
      _alertFilterTimer: null,
      showMapDateTimePicker: false,
      showLayerPicker: false,
      mapDateTimePickerValue: '',
    };
  },

  computed: {
    visibleTabs() {
      var me = this;
      var tabs = this.tabs.filter(function (t) { return t.id !== 'today'; });
      // Show Map tab when map is not full on home page
      if (me.mapDisplayMode !== 'full') {
        // Insert Map tab after 'rain' if not already present
        if (!tabs.find(function (t) { return t.id === 'map'; })) {
          var idx = tabs.findIndex(function (t) { return t.id === 'rain'; });
          tabs = tabs.slice();
          tabs.splice(idx + 1, 0, { id: 'map', label: 'Map', icon: 'bi-map' });
        }
      } else {
        tabs = tabs.filter(function (t) { return t.id !== 'map'; });
      }
      return tabs;
    },

    displayLocation() {
      if (this.wxdata && this.wxdata.resolvedAddress) {
        return _resolveLocation(this.wxdata, this.selectedLocation?.location, this.selectedLocation?.name);
      }
      if (this.selectedLocation) return this.selectedLocation.name || this.selectedLocation.location;
      return 'Select location';
    },

    unitLabel() {
      var opt = this.unitOptions.find(u => u.value === this.unitGroup);
      return opt ? opt.label.split(' ')[0] : 'US';
    },

    currentConditions() {
      return this.wxdata && this.wxdata.currentConditions;
    },

    hasSession() {
      return !!this.sessionId || !!this.masterKey;
    },

    alerts() {
      return this.wxdata && this.wxdata.alerts;
    },

    days() {
      return this.wxdata && this.wxdata.days;
    },

    today() {
      var me = this;
      if (!me.days) return null;
      return me.days.find(function (d) {
        return _isToday(d.datetimeEpoch, me.wxdata.timezone);
      }) || null;
    },

    todayHours() {
      var me = this;
      if (!me.today || !me.today.hours) return null;
      var hours = me.today.hours.slice();
      // Append tomorrow's hours to create a combined 48-hour view
      if (me.days) {
        var todayIdx = me.days.indexOf(me.today);
        var tomorrow = todayIdx >= 0 && todayIdx + 1 < me.days.length ? me.days[todayIdx + 1] : null;
        if (tomorrow && tomorrow.hours) {
          hours = hours.concat(tomorrow.hours);
        }
      }
      return hours;
    },

    next3Days() {
      var me = this;
      if (!me.days) return null;
      return me.days.slice(0, 3);
    },

    remainingDays() {
      var me = this;
      if (!me.days) return null;
      return me.days.slice(3);
    },

    historyDays() {
      var me = this;
      if (!me.historyData || !me.historyData.days) return null;
      // Return all days from the history response (should be last 3 days)
      return me.historyData.days.slice().sort(function (a, b) {
        return b.datetimeEpoch - a.datetimeEpoch;
      });
    },

    minutely() {
      var me = this;
      if (!me.wxdata || !me.wxdata.days) return [];
      var minutes = [];
      var nowEpoch = new Date().getTime() / 1000;
      var startTime = me.today ? nowEpoch - 6 * 3600 : me.wxdata.days[0].datetimeEpoch;
      var endTime = me.today ? nowEpoch + 12 * 3600 : me.wxdata.days[0].datetimeEpoch + 24 * 3600;

      me.wxdata.days.forEach(function (day) {
        day.hours && day.hours.forEach(function (hour) {
          hour.minutes && hour.minutes.forEach(function (minute) {
            if (minute && minute.datetimeEpoch >= startTime && minute.datetimeEpoch <= endTime) {
              minute.precipprob = hour.precipprob;
              if (minute.precip == null) minute.precip = hour.precip;
              if (minute.precipremote == null) minute.precipremote = hour.precipremote;
              minutes.push(minute);
            }
          });
        });
      });
      return minutes;
    },

    lastNextRain() {
      var me = this;
      if (!me.minutely.length) return null;
      var nowEpoch = new Date().getTime() / 1000;

      var findHourForMinute = function (minute) {
        if (!minute || !me.wxdata || !me.wxdata.days) return null;
        for (var di = 0; di < me.wxdata.days.length; di++) {
          var day = me.wxdata.days[di];
          if (!day.hours) continue;
          for (var hi = 0; hi < day.hours.length; hi++) {
            var hour = day.hours[hi];
            if (!hour.minutes) continue;
            for (var mi = 0; mi < hour.minutes.length; mi++) {
              if (hour.minutes[mi].datetimeEpoch === minute.datetimeEpoch) return hour;
            }
          }
        }
        return null;
      };

      var latestPrecip = me.minutely.reduce(function (a, d) {
        if (d.datetimeEpoch > nowEpoch) return a;
        if (_minutelyRainRate(d) > 0) return d;
        return a;
      }, null);

      var nextPrecip = null;
      var foundLight = null;
      var norainPeriods = 0;

      for (var i = 0; i < me.minutely.length; i++) {
        var d = me.minutely[i];
        if (d.datetimeEpoch < nowEpoch) continue;
        var dRate = _minutelyRainRate(d);
        if (dRate <= 0) {
          norainPeriods++;
          if (foundLight && norainPeriods > 3) { nextPrecip = foundLight; break; }
          continue;
        }
        norainPeriods = 0;
        if (!nextPrecip && dRate > 2.5) { nextPrecip = d; break; }
        if (!foundLight && dRate <= 2.5) foundLight = d;
      }
      if (!nextPrecip && foundLight) nextPrecip = foundLight;

      var maxFuturePrecip = me.minutely.reduce(function (a, d) {
        if (d.datetimeEpoch < nowEpoch) return a;
        if (_minutelyRainRate(d) > 0 && (!a || _minutelyRainRate(d) > _minutelyRainRate(a))) return d;
        return a;
      }, null);

      if (latestPrecip) {
        latestPrecip.minsAgo = Math.round((nowEpoch - latestPrecip.datetimeEpoch) / 60);
        latestPrecip.hour = findHourForMinute(latestPrecip);
      }
      if (nextPrecip) {
        nextPrecip.minsUntil = Math.round((nextPrecip.datetimeEpoch - nowEpoch) / 60);
        nextPrecip.hour = findHourForMinute(nextPrecip);
      }
      if (maxFuturePrecip) {
        maxFuturePrecip.minsUntil = Math.round((maxFuturePrecip.datetimeEpoch - nowEpoch) / 60);
        maxFuturePrecip.hour = findHourForMinute(maxFuturePrecip);
      }

      return { latest: latestPrecip, next: nextPrecip, maxFuture: maxFuturePrecip, nowEpoch: nowEpoch };
    },

    nearbyRainWithNone() {
      return this.nearbyRain || 'No rain nearby';
    },

    /** Short title for rain brief on Now tab, e.g. "Light rain now" or "Rain expected in 2 hours" */
    rainBriefTitle() {
      var me = this;
      var lr = me.lastNextRain;
      if (!lr) return null;
      if (lr.latest && lr.latest.minsAgo < 20) {
        var pt = lr.latest.hour && lr.latest.hour.preciptype;
        var desc = me.getRainDescription(_minutelyRainRate(lr.latest), pt, true);
        if (!desc) desc = 'Precipitation';
        return desc + ' now';
      }
      if (lr.next) {
        var pt2 = lr.next.hour && lr.next.hour.preciptype;
        var desc2 = me.getRainDescription(_minutelyRainRate(lr.next), pt2, true);
        if (!desc2) desc2 = 'Rain';
        var timing = me.formatTimeAgo(lr.next.minsUntil, 'in ', 'soon');
        return desc2 + ' expected ' + timing;
      }
      return 'Rain nearby';
    },

    /** Longer detail for rain brief — describes duration / change in intensity without exact rates or temps */
    rainBriefDetail() {
      var me = this;
      var lr = me.lastNextRain;
      if (!lr) return null;
      var situation = null;
      var intensityChange = null;

      // Current situation
      if (lr.latest && lr.latest.minsAgo < 20 && lr.next && lr.next.minsUntil < 20) {
        var totalDuration = lr.latest.minsAgo + lr.next.minsUntil;
        if (totalDuration > 30) {
          situation = 'Expected to continue for at least ' + me.formatTimeAgo(totalDuration, '', null);
        } else {
          situation = 'Ongoing';
        }
      } else if (lr.latest && lr.latest.minsAgo < 20) {
        situation = 'Currently falling';
        if (lr.next && lr.next.minsUntil > 60) {
          situation += ' but may ease soon';
        }
      } else if (lr.next) {
        situation = 'Expected to start ' + me.formatTimeAgo(lr.next.minsUntil, 'in ', 'any moment');
      }

      // Intensity change
      if (lr.next) {
        var max = lr.maxFuture;
        if (max && _minutelyRainRate(max) > _minutelyRainRate(lr.next)) {
          var nextRate = _minutelyRainRate(lr.next);
          var maxRate = _minutelyRainRate(max);
          var timeDiff = (max.datetimeEpoch - lr.next.datetimeEpoch) / 60;
          if (timeDiff > 30 && maxRate > nextRate * 1.5) {
            var maxPt = max.hour && max.hour.preciptype;
            var maxDesc = me.getRainDescription(maxRate, maxPt, true);
            if (maxDesc) {
              var timeStr = me.formatTimeAgo(max.minsUntil, '', null);
              intensityChange = 'May increase to ' + maxDesc.toLowerCase() + ' over the next ' + timeStr;
            }
          }
        }
      }

      if (situation && intensityChange) return situation + '. ' + intensityChange + '.';
      if (situation) return situation + '.';
      if (intensityChange) return intensityChange + '.';
      return null;
    },

    /** 7-day forecast for Now tab (today + next 6 days) */
    nowForecastDays() {
      if (!this.days) return null;
      return this.days.slice(0, 7);
    },

    /** Wind speed threshold (in user's units) above which wind is shown in hourly cards.
     *  ~25 km/h / ~15 mph / ~7 m/s — generally considered breezy/windy */
    windyThreshold() {
      var unit = this.unitGroup;
      if (unit === 'us' || unit === 'uk') return 15;  // mph
      if (unit === 'metric') return 25;                // km/h
      return 7;                                        // m/s (base)
    },

    nearbyRain() {
      var me = this;
      var lr = me.lastNextRain;
      if (!lr || (!lr.latest && !lr.next)) return null;
      if (lr.latest && !lr.next) return me.lastRainDesc;
      if (!lr.latest && lr.next) return me.nextRainDesc;

      // If recent past and near future overlap, it's continuous precipitation
      if (lr.latest.minsAgo <= 20 && lr.next.minsUntil <= 20) {
        var pt = lr.latest.hour && lr.latest.hour.preciptype;
        var rateVal = Math.max(_minutelyRainRate(lr.latest), _minutelyRainRate(lr.next));
        var desc = me.getRainDescription(rateVal, pt, true);
        if (!desc) desc = 'Precipitation';
        var rate = _formatRainRate(rateVal, me.unitGroup);
        var output = desc + ' continuing';
        // Only show rate if it's a meaningful amount (not trace)
        if (rate.charAt(0) !== '<') output += ' (' + rate + ')';
        // Estimate duration
        if (lr.next.hour && lr.latest.hour) {
          var totalDuration = lr.latest.minsAgo + lr.next.minsUntil;
          if (totalDuration > 30) output += ' for at least ' + me.formatTimeAgo(totalDuration, '', null);
        }
        // Wind context
        var hour = lr.latest.hour || lr.next.hour;
        if (hour && hour.windspeed > 15) output += ' with ' + me.fmtVal(hour, 'windspeed') + ' winds';
        return output;
      }
      return me.lastRainDesc + '. <br/>' + me.nextRainDesc;
    },

    lastRainDesc() {
      var me = this;
      var latest = me.lastNextRain && me.lastNextRain.latest;
      if (!latest) return 'No recent precip';
      var pt = latest.hour && latest.hour.preciptype;
      var rateVal = _minutelyRainRate(latest);
      var desc = me.getRainDescription(rateVal, pt, true);
      if (!desc) return 'No recent precip';
      var rate = _formatRainRate(rateVal, me.unitGroup);
      var output;
      if (latest.minsAgo < 10) {
        output = desc + ' right now';
      } else {
        output = desc + ' ' + me.formatTimeAgo(latest.minsAgo, '', null) + ' ago';
      }
      // Only show rate if meaningful (not trace)
      if (rate.charAt(0) !== '<') output += ' (' + rate + ')';
      if (latest.hour) {
        if (latest.hour.windspeed > 20) output += ' with ' + me.fmtVal(latest.hour, 'windspeed') + ' winds';
      }
      return output;
    },

    nextRainDesc() {
      var me = this;
      var next = me.lastNextRain && me.lastNextRain.next;
      var max = me.lastNextRain && me.lastNextRain.maxFuture;
      if (!next) return 'No upcoming precip';
      var pt = next.hour && next.hour.preciptype;
      var nextRate = _minutelyRainRate(next);
      var desc = me.getRainDescription(nextRate, pt, true);
      if (!desc) return 'No upcoming precip';
      var rate = _formatRainRate(nextRate, me.unitGroup);
      var period = me.formatTimeAgo(next.minsUntil, 'in ', 'any moment');
      var output = desc + ' expected ' + period;
      // Only show rate if meaningful (not trace)
      if (rate.charAt(0) !== '<') output += ' (' + rate + ')';
      if (next.hour) {
        if (me.currentConditions && Math.abs(next.hour.temp - me.currentConditions.temp) > 5) {
          output += ', ' + (next.hour.temp > me.currentConditions.temp ? 'warmer' : 'cooler') + ' at ' + me.fmtVal(next.hour, 'temp');
        }
        if (next.hour.windspeed > 15) output += ' with ' + me.fmtVal(next.hour, 'windspeed') + ' winds';
      }
      var maxRateVal = max ? _minutelyRainRate(max) : 0;
      if (max && maxRateVal > nextRate) {
        var rateDiff = maxRateVal - nextRate;
        var timeDiff = (max.datetimeEpoch - next.datetimeEpoch) / 60;
        // Only mention intensification if meaningfully different and not at same time
        if (timeDiff > 30 && rateDiff > 0.5 && maxRateVal > nextRate * 1.5) {
          var maxPt = max.hour && max.hour.preciptype;
          var maxDesc = me.getRainDescription(maxRateVal, maxPt, true);
          var maxRateStr = _formatRainRate(maxRateVal, me.unitGroup);
          if (maxDesc && maxDesc.toLowerCase() !== desc.toLowerCase()) {
            // Category changes (e.g. drizzle → moderate rain)
            output += ', increasing to ' + maxDesc.toLowerCase();
            if (maxRateStr.charAt(0) !== '<') output += ' (' + maxRateStr + ')';
          } else {
            // Same category but higher intensity — just show rate change
            if (maxRateStr.charAt(0) !== '<') output += ', intensifying to ' + maxRateStr;
          }
          output += ' ' + me.formatTimeAgo(max.minsUntil, 'by ', null);
        }
      }
      return output;
    },

    mapTimeDisplay() {
      if (!this.mapDateTime) return '--:--';
      try {
        var tz = this.wxdata && this.wxdata.timezone;
        var showMinutes = this.computedStepInterval < 60;
        var opts = this.use24HourTime
          ? { hour: 'numeric', minute: showMinutes ? '2-digit' : undefined, hourCycle: 'h23' }
          : { hour: 'numeric', minute: showMinutes ? '2-digit' : undefined, hour12: true };
        if (tz) opts.timeZone = tz;
        var str = new Intl.DateTimeFormat('en-US', opts).format(this.mapDateTime);
        if (!this.use24HourTime) str = str.replace(/\s?AM/, 'A').replace(/\s?PM/, 'P');
        return str;
      } catch (e) { return '--'; }
    },

    mapDateDisplay() {
      if (!this.mapDateTime) return '';
      try {
        var tz = this.wxdata && this.wxdata.timezone;
        var opts = { weekday: 'short', day: 'numeric' };
        if (tz) opts.timeZone = tz;
        var parts = new Intl.DateTimeFormat('en-US', opts).formatToParts(this.mapDateTime);
        var weekday = '', day = '';
        parts.forEach(function (p) {
          if (p.type === 'weekday') weekday = p.value;
          if (p.type === 'day') day = p.value;
        });
        return weekday + ' ' + day;
      } catch (e) { return ''; }
    },

    /* Whether the current layer is precipcomposite */
    isPrecipCompositeLayer() {
      var config = this.mapLayerOptions.find(function (l) { return l.id === this.activeMapLayer; }.bind(this));
      return config && config.element === 'precipcomposite';
    },

    /* Step interval for skip buttons:
       - precipcomposite within 3h past / 2h future: 10 min
       - more than 1 day in the future: 3h (aligned to UTC 3h intervals)
       - otherwise: 1h */
    computedStepInterval() {
      var now = Date.now();
      var mapT = this.mapDateTime ? this.mapDateTime.getTime() : now;
      var diff = mapT - now;
      if (this.isPrecipCompositeLayer) {
        if (diff >= -3 * 60 * 60 * 1000 && diff <= 2 * 60 * 60 * 1000) return 10;
      }
      // More than 1 day in the future → 3 hour steps
      if (diff > 24 * 60 * 60 * 1000) return 180;
      return 60;
    },

    moonPhaseDesc() {
      if (!this.today || (this.today.moonphase !== 0 && !this.today.moonphase)) return '-';
      return _getMoonPhaseDesc(this.today.moonphase);
    },

    moonIllumination() {
      if (!this.today || (this.today.moonphase !== 0 && !this.today.moonphase)) return '';
      return _getMoonPhaseDesc(this.today.moonphase, true);
    },

    moonPhaseIcon() {
      if (!this.today) return 'bi-moon';
      var p = this.today.moonphase;
      if (p === 0 || p === 1) return 'bi-circle';
      if (p < 0.25) return 'bi-moon';
      if (p === 0.25) return 'bi-moon-stars';
      if (p < 0.5) return 'bi-moon-stars-fill';
      if (p === 0.5) return 'bi-circle-fill';
      if (p < 0.75) return 'bi-moon-stars-fill';
      if (p === 0.75) return 'bi-moon-stars';
      return 'bi-moon';
    },

    daylightDuration() {
      if (!this.today || !this.today.sunriseEpoch || !this.today.sunsetEpoch) return '-';
      var mins = Math.round((this.today.sunsetEpoch - this.today.sunriseEpoch) / 60);
      var h = Math.floor(mins / 60);
      var m = mins % 60;
      return h + 'h ' + m + 'm';
    },

    lastUpdatedAgo() {
      if (!this.lastUpdated) return '';
      // nowTick forces reactivity every 30s
      var secs = Math.round((this.nowTick - this.lastUpdated.getTime()) / 1000);
      if (secs < 60) return 'just now';
      var mins = Math.floor(secs / 60);
      if (mins < 60) return mins + 'm ago';
      var hrs = Math.floor(mins / 60);
      return hrs + 'h ' + (mins % 60) + 'm ago';
    },

    detailDayLabel() {
      if (!this.detailRecord) return '';
      // Find which day this hour belongs to
      var epoch = this.detailRecord.datetimeEpoch;
      if (this.days) {
        for (var i = 0; i < this.days.length; i++) {
          var d = this.days[i];
          if (d.hours) {
            for (var h = 0; h < d.hours.length; h++) {
              if (d.hours[h].datetimeEpoch === epoch) {
                return this.fmtDayName(d) + ' ' + this.fmtDateShort(d);
              }
            }
          }
        }
      }
      return this.fmtDateShort(this.detailRecord);
    },

    detailSections() {
      var isDay = this.detailType === 'day';
      var sections = [
        {
          title: 'Temperature',
          icon: 'bi-thermometer-half',
          elements: isDay ? [
            { key: 'tempmax', label: 'High', format: 'temp' },
            { key: 'tempmin', label: 'Low', format: 'temp' },
            { key: 'temp', label: 'Average', format: 'temp' },
            { key: 'feelslike', label: 'Feels Like', format: 'temp' },
            { key: 'feelslikemax', label: 'Feels Like Max', format: 'temp' },
            { key: 'feelslikemin', label: 'Feels Like Min', format: 'temp' },
            { key: 'dew', label: 'Dew Point', format: 'dew' },
            { key: 'tempwet', label: 'Wet Bulb', format: 'temp' },
            { key: 'deltat', label: 'Delta T', format: 'temp' },
            { key: 'wbgt', label: 'WBGT', format: 'temp' },
            { key: 'wbgtmax', label: 'WBGT Max', format: 'temp' },
            { key: 'wbgtmin', label: 'WBGT Min', format: 'temp' },
          ] : [
            { key: 'temp', label: 'Temperature', format: 'temp' },
            { key: 'feelslike', label: 'Feels Like', format: 'temp' },
            { key: 'dew', label: 'Dew Point', format: 'dew' },
            { key: 'tempwet', label: 'Wet Bulb', format: 'temp' },
            { key: 'deltat', label: 'Delta T', format: 'temp' },
            { key: 'wbgt', label: 'WBGT', format: 'temp' },
          ],
        },
        {
          title: 'Precipitation',
          icon: 'bi-cloud-rain-fill',
          elements: isDay ? [
            { key: 'precip', label: 'Precipitation', format: 'precip' },
            { key: 'precipprob', label: 'Chance', format: 'precipprob' },
            { key: 'precipcover', label: 'Coverage', format: 'precipprob' },
            { key: 'preciptype', label: 'Type' },
            { key: 'snow', label: 'Snow', format: 'snow' },
            { key: 'snowdepth', label: 'Snow Depth', format: 'snowdepth' },
            { key: 'precipremote', label: 'Radar Precip', format: 'precip' },
            { key: 'frozenprecippercent', label: 'Frozen %', format: 'precipprob' },
          ] : [
            { key: 'precip', label: 'Precipitation', format: 'precip' },
            { key: 'precipprob', label: 'Chance', format: 'precipprob' },
            { key: 'preciptype', label: 'Type' },
            { key: 'snow', label: 'Snow', format: 'snow' },
            { key: 'snowdepth', label: 'Snow Depth', format: 'snowdepth' },
            { key: 'precipremote', label: 'Radar Precip', format: 'precip' },
            { key: 'reflectivity', label: 'Reflectivity' },
            { key: 'frozenprecippercent', label: 'Frozen %', format: 'precipprob' },
          ],
        },
        {
          title: 'Wind',
          icon: 'bi-wind',
          elements: isDay ? [
            { key: 'windspeed', label: 'Speed', format: 'wind' },
            { key: 'windspeedmax', label: 'Max Speed', format: 'wind' },
            { key: 'windspeedmin', label: 'Min Speed', format: 'wind' },
            { key: 'windspeedmean', label: 'Mean Speed', format: 'wind' },
            { key: 'windgust', label: 'Gust', format: 'wind' },
            { key: 'winddir', label: 'Direction' },
          ] : [
            { key: 'windspeed', label: 'Speed', format: 'wind' },
            { key: 'windgust', label: 'Gust', format: 'wind' },
            { key: 'winddir', label: 'Direction' },
          ],
        },
        {
          title: 'Atmosphere',
          icon: 'bi-clouds',
          elements: [
            { key: 'humidity', label: 'Humidity', format: 'humidity' },
            { key: 'pressure', label: 'Pressure', format: 'pressure' },
            { key: 'visibility', label: 'Visibility', format: 'visibility' },
            { key: 'cloudcover', label: 'Cloud Cover', format: 'cloudcover' },
          ],
        },
        {
          title: 'Solar & UV',
          icon: 'bi-sun-fill',
          elements: [
            { key: 'solarradiation', label: 'Solar Radiation' },
            { key: 'solarenergy', label: 'Solar Energy' },
            { key: 'uvindex', label: 'UV Index', format: 'uvindex' },
            { key: 'uvindex2', label: 'UV Index v2', format: 'uvindex2' },
            { key: 'ghiradiation', label: 'GHI Radiation' },
            { key: 'dniradiation', label: 'DNI Radiation' },
            { key: 'difradiation', label: 'DIF Radiation' },
            { key: 'gtiradiation', label: 'GTI Radiation' },
            { key: 'clearsolar', label: 'Clear Sky Solar' },
            { key: 'sunelevation', label: 'Sun Elevation' },
            { key: 'sunazimuth', label: 'Sun Azimuth' },
          ],
        },
        {
          title: 'Severe Weather',
          icon: 'bi-exclamation-triangle-fill',
          elements: [
            { key: 'severerisk', label: 'Severe Risk' },
            { key: 'cape', label: 'CAPE' },
            { key: 'cin', label: 'CIN' },
          ],
        },
        {
          title: 'Air Quality',
          icon: 'bi-lungs-fill',
          elements: [
            { key: 'aqius', label: 'AQI (US)' },
            { key: 'aqieur', label: 'AQI (EU)' },
            { key: 'pm1', label: 'PM 1.0', format: 'pm1' },
            { key: 'pm2p5', label: 'PM 2.5', format: 'pm2p5' },
            { key: 'pm10', label: 'PM 10', format: 'pm10' },
            { key: 'o3', label: 'Ozone (O₃)', format: 'o3' },
            { key: 'no2', label: 'NO₂', format: 'no2' },
            { key: 'so2', label: 'SO₂', format: 'so2' },
            { key: 'co', label: 'CO', format: 'co' },
          ],
        },
        {
          title: 'Ocean & Waves',
          icon: 'bi-water',
          elements: [
            { key: 'waveheight', label: 'Wave Height' },
            { key: 'maxwaveheight', label: 'Max Wave Height' },
            { key: 'waveperiod', label: 'Wave Period' },
            { key: 'wavedir', label: 'Wave Direction' },
            { key: 'swellheight', label: 'Swell Height' },
            { key: 'swellperiod', label: 'Swell Period' },
            { key: 'swelldir', label: 'Swell Direction' },
          ],
        },
        {
          title: 'Soil & Agriculture',
          icon: 'bi-tree-fill',
          elements: [
            { key: 'et0', label: 'Evapotranspiration (ET₀)' },
            { key: 'soiltemp01', label: 'Soil Temp 0-1cm', format: 'temp' },
            { key: 'soiltemp04', label: 'Soil Temp 4cm', format: 'temp' },
            { key: 'soiltemp10', label: 'Soil Temp 10cm', format: 'temp' },
            { key: 'soiltemp20', label: 'Soil Temp 20cm', format: 'temp' },
            { key: 'soilmoisture01', label: 'Soil Moisture 0-1cm' },
            { key: 'soilmoisture04', label: 'Soil Moisture 4cm' },
            { key: 'soilmoisture10', label: 'Soil Moisture 10cm' },
            { key: 'soilmoisture20', label: 'Soil Moisture 20cm' },
          ],
        },
        {
          title: 'Upper-Level Winds',
          icon: 'bi-arrow-up-right-circle',
          elements: [
            { key: 'windspeed50', label: '50m Speed', format: 'wind' },
            { key: 'winddir50', label: '50m Direction' },
            { key: 'windspeed80', label: '80m Speed', format: 'wind' },
            { key: 'winddir80', label: '80m Direction' },
            { key: 'windspeed100', label: '100m Speed', format: 'wind' },
            { key: 'winddir100', label: '100m Direction' },
          ],
        },
      ];

      // Add astronomy section for day records
      if (isDay) {
        sections.push({
          title: 'Astronomy',
          icon: 'bi-moon-stars-fill',
          elements: [
            { key: 'sunriseEpoch', label: 'Sunrise' },
            { key: 'sunsetEpoch', label: 'Sunset' },
            { key: 'moonphase', label: 'Moon Phase' },
            { key: 'moonriseEpoch', label: 'Moonrise' },
            { key: 'moonsetEpoch', label: 'Moonset' },
          ],
        });
      }

      return sections;
    },

    nextSunEvent() {
      var me = this;
      if (!me.today) return null;
      var nowEpoch = Math.floor(Date.now() / 1000);
      var sunrise = me.today.sunriseEpoch;
      var sunset = me.today.sunsetEpoch;
      if (!sunrise || !sunset) return null;

      // Before today's sunrise → show sunrise
      if (nowEpoch < sunrise) {
        return { type: 'sunrise', epoch: sunrise };
      }
      // After sunrise but before sunset → show sunset
      if (nowEpoch < sunset) {
        return { type: 'sunset', epoch: sunset };
      }
      // After sunset → show tomorrow's sunrise
      var tomorrow = me.days && me.days.find(function (d) {
        return d.datetimeEpoch > me.today.datetimeEpoch;
      });
      if (tomorrow && tomorrow.sunriseEpoch) {
        return { type: 'sunrise', epoch: tomorrow.sunriseEpoch, tomorrow: true };
      }
      return null;
    },

    orderedSunEvents() {
      var me = this;
      if (!me.today) return [];
      var nowEpoch = Math.floor(Date.now() / 1000);
      var sunrise = me.today.sunriseEpoch;
      var sunset = me.today.sunsetEpoch;
      if (!sunrise || !sunset) return [];

      var tomorrow = me.days && me.days.find(function (d) {
        return d.datetimeEpoch > me.today.datetimeEpoch;
      });

      // Before sunrise: show sunrise then sunset (both today)
      if (nowEpoch < sunrise) {
        return [
          { type: 'sunrise', epoch: sunrise },
          { type: 'sunset', epoch: sunset },
        ];
      }
      // Before sunset: show sunset then tomorrow's sunrise
      if (nowEpoch < sunset) {
        var events = [{ type: 'sunset', epoch: sunset }];
        if (tomorrow && tomorrow.sunriseEpoch) {
          events.push({ type: 'sunrise', epoch: tomorrow.sunriseEpoch, tomorrow: true });
        }
        return events;
      }
      // After sunset: show tomorrow's sunrise then tomorrow's sunset
      if (tomorrow) {
        var events = [];
        if (tomorrow.sunriseEpoch) events.push({ type: 'sunrise', epoch: tomorrow.sunriseEpoch, tomorrow: true });
        if (tomorrow.sunsetEpoch) events.push({ type: 'sunset', epoch: tomorrow.sunsetEpoch, tomorrow: true });
        return events;
      }
      return [
        { type: 'sunrise', epoch: sunrise },
        { type: 'sunset', epoch: sunset },
      ];
    },

    // ---- Rain & Precip computed ----
    hasTodayPrecip() {
      if (!this.today) return false;
      return (this.todayRainSoFar > 0.01 || this.todayRainRemaining > 0.01);
    },

    hasMinutelyRain() {
      return this.minutely.some(function (d) { return _minutelyRainRate(d) > 0; });
    },

    todayRainSoFar() {
      var me = this;
      if (!me.today || !me.today.hours) return 0;
      var nowEpoch = Date.now() / 1000;
      var total = 0;
      me.today.hours.forEach(function (h) {
        if (h.datetimeEpoch < nowEpoch) {
          var val = (h.precipremote != null && h.precipremote > 0) ? h.precipremote : (h.precip || 0);
          total += val;
        }
      });
      return total;
    },

    todayRainRemaining() {
      var me = this;
      if (!me.today || !me.today.hours) return 0;
      var nowEpoch = Date.now() / 1000;
      var total = 0;
      me.today.hours.forEach(function (h) {
        if (h.datetimeEpoch >= nowEpoch) {
          total += h.precip || 0;
        }
      });
      return total;
    },

    todayRainTotal() {
      return this.todayRainSoFar + this.todayRainRemaining;
    },

    rainIntensityNow() {
      var me = this;
      var cc = me.currentConditions;
      if (!cc) return { label: 'No Data', level: 0, dbz: 0, rate: '--' };
      var lr = me.lastNextRain;
      var refl = 0;
      var rateVal = 0;
      var upcoming = false;
      var pt = null;
      // Check current: recent minutely or current conditions
      if (lr && lr.latest && lr.latest.minsAgo < 10) {
        refl = lr.latest.reflectivity || 0;
        rateVal = _minutelyRainRate(lr.latest);
        pt = lr.latest.hour && lr.latest.hour.preciptype;
      } else if (cc.reflectivity) {
        refl = cc.reflectivity;
        rateVal = _dbzToRainRateNum(refl);
      }
      // If nothing current, check upcoming radar
      if (refl <= 0 && lr && lr.next) {
        refl = lr.next.reflectivity || 0;
        rateVal = _minutelyRainRate(lr.next);
        pt = lr.next.hour && lr.next.hour.preciptype;
        upcoming = true;
      }
      // Use rate-based classification (consistent with rainBriefTitle)
      var desc = me.getRainDescription(rateVal, pt, true);
      var label = desc || 'None';
      var level = 0;
      if (rateVal > 50) level = 5;
      else if (rateVal > 7.6) level = 4;
      else if (rateVal > 2.5) level = 3;
      else if (rateVal > 0.5) level = 2;
      else if (rateVal > 0) level = 1;
      if (upcoming && level > 0) {
        label = label + ' ' + me.formatTimeAgo(lr.next.minsUntil, 'in ', 'soon');
      }
      return { label: label, level: level, dbz: refl, rate: _dbzToRainRate(refl, me.unitGroup) };
    },

    hourlyPrecipToday() {
      var me = this;
      if (!me.days || !me.days.length) return [];
      var nowEpoch = Date.now() / 1000;
      var startEpoch = nowEpoch - 6 * 3600;
      var cutoff = nowEpoch + 6 * 3600;

      // Gather hours from all available days
      var allHours = [];
      me.days.forEach(function (day) {
        if (day.hours) {
          day.hours.forEach(function (h) { allHours.push(h); });
        }
      });

      return allHours
        .filter(function (h) { return h.datetimeEpoch >= startEpoch && h.datetimeEpoch <= cutoff; })
        .map(function (h) {
          return {
            time: h.datetimeEpoch,
            precip: h.precip || 0,
            precipremote: h.precipremote || 0,
            precipprob: h.precipprob || 0,
            snow: h.snow || 0,
            isPast: h.datetimeEpoch < nowEpoch,
            isCurrent: Math.abs(h.datetimeEpoch - nowEpoch) < 3600,
            conditions: h.conditions,
            preciptype: h.preciptype,
          };
        });
    },

    todayPrecipTypes() {
      var me = this;
      if (!me.today || !me.today.hours) return [];
      var types = {};
      me.today.hours.forEach(function (h) {
        if (h.preciptype && h.preciptype.length) {
          h.preciptype.forEach(function (t) { types[t] = true; });
        }
      });
      return Object.keys(types);
    },

    upcomingRainHours() {
      var me = this;
      if (!me.days) return [];
      var nowEpoch = Date.now() / 1000;
      var cutoff = nowEpoch + 12 * 3600;
      // First pass: collect all hours in range and find first/last with precip
      var allHours = [];
      var firstPrecipIdx = -1;
      var lastPrecipIdx = -1;
      for (var di = 0; di < me.days.length; di++) {
        var day = me.days[di];
        if (!day.hours) continue;
        for (var hi = 0; hi < day.hours.length; hi++) {
          var h = day.hours[hi];
          if (h.datetimeEpoch < nowEpoch - 3600 || h.datetimeEpoch > cutoff) continue;
          var idx = allHours.length;
          allHours.push(h);
          if (h.precipprob >= 30 || h.precip > 0) {
            if (firstPrecipIdx < 0) firstPrecipIdx = idx;
            lastPrecipIdx = idx;
          }
        }
      }
      // Return all hours between first and last precipitation hour (inclusive)
      var hours = [];
      if (firstPrecipIdx < 0) return hours;
      for (var i = firstPrecipIdx; i <= lastPrecipIdx; i++) {
        var h = allHours[i];
        hours.push({
          time: h.datetimeEpoch,
          precip: h.precip || 0,
          precipremote: h.precipremote || 0,
          precipprob: h.precipprob || 0,
          preciptype: h.preciptype,
          raw: h,
        });
      }
      return hours;
    },

    /* Collect all upcoming hourly data from now across all forecast days */
    _upcomingHours() {
      var me = this;
      if (!me.days) return [];
      var nowEpoch = Date.now() / 1000;
      var hours = [];
      for (var di = 0; di < me.days.length; di++) {
        var day = me.days[di];
        if (!day.hours) continue;
        for (var hi = 0; hi < day.hours.length; hi++) {
          var h = day.hours[hi];
          if (h.datetimeEpoch >= nowEpoch) hours.push(h);
        }
      }
      return hours;
    },

    /* Sum precip (rain) for the next N hours from now (only hours with >50% chance) */
    _precipAccum() {
      var hours = this._upcomingHours;
      var buckets = [2, 12, 24];
      var result = {};
      var nowEpoch = Date.now() / 1000;
      for (var b = 0; b < buckets.length; b++) {
        var limit = nowEpoch + buckets[b] * 3600;
        var total = 0;
        for (var i = 0; i < hours.length; i++) {
          if (hours[i].datetimeEpoch >= limit) break;
          if ((hours[i].precipprob || 0) > 50) total += hours[i].precip || 0;
        }
        result[buckets[b]] = total;
      }
      return result;
    },

    /* Whether any upcoming rain is predicted */
    hasUpcomingRain() {
      var a = this._precipAccum;
      return a[24] > 0;
    },

    /* Sum snow for the next N hours from now (only hours with >50% chance) */
    _snowAccum() {
      var hours = this._upcomingHours;
      var buckets = [6, 12, 24];
      var result = {};
      var nowEpoch = Date.now() / 1000;
      for (var b = 0; b < buckets.length; b++) {
        var limit = nowEpoch + buckets[b] * 3600;
        var total = 0;
        for (var i = 0; i < hours.length; i++) {
          if (hours[i].datetimeEpoch >= limit) break;
          if ((hours[i].precipprob || 0) > 50) total += hours[i].snow || 0;
        }
        result[buckets[b]] = total;
      }
      return result;
    },

    /* Whether any upcoming snow is predicted */
    hasUpcomingSnow() {
      var a = this._snowAccum;

      return a[24] > 0;
    },
  },

  methods: {
    // ---- Data fetching ----
    fetchWeatherData(forceRefresh) {
      var me = this;
      if (!me.selectedLocation) { me.isLoading = false; return; }

      // Cancel any in-flight request
      if (me._fetchAbort) me._fetchAbort.abort();
      me._fetchAbort = new AbortController();

      // Show loading state: full spinner on first load, inline indicator on refresh
      var isRefresh = !!me.wxdata;
      me.isLoading = !isRefresh;
      me.isRefreshing = isRefresh;
      me.errorStatus = null;
      if (!isRefresh) me.loadingStatus = 'Fetching weather data...';

      // Destroy old map before DOM is removed by v-if (only on initial load)
      if (!isRefresh && me.leafletMap) {
        me.leafletMap.remove();
        me.leafletMap = null;
        me.wxTileLayer = null;
        me.baseLayer = null;
        me.circleMarker = null;
      }

      var loc = encodeURIComponent(me.selectedLocation.location);
      var url;

      if (me.useLlxApi) {
        // LLX API - uses query params for location (latitude/longitude or city/admin1/country)
        var llxParams = new URLSearchParams({
          key: me.getApiKey(),
          unitGroup: me.unitGroup,
          include: me.hasSession ? 'days,hours,current,alerts,minutes,info' : 'days,hours,current,alerts,info',
          iconSet: 'icons2'
        });
        llxParams.set('elements', 'add:datetimeEpoch,add:dew,add:precipcover,add:preciptype,add:snowdepth,add:pressure,add:solarradiation,add:solarenergy,add:uvindex,add:uvindex2,add:severerisk,add:sunrise,add:sunset,add:moonphase,add:description,add:offset,add:source,add:sunriseEpoch,add:sunsetEpoch,add:precipremote,add:reflectivity,add:reflectivity2,add:aqius,add:aqieur,add:pm1,add:pm2p5,add:pm10,add:o3,add:no2,add:so2,add:co');
        if (me.useV2Forecast) llxParams.set('options', 'usev2forecast');
        me._setLlxLocationParams(llxParams);
        url = VcGlobal.SERVER_ROOT + '/VisualCrossingWebServices/rest/api/v1/timelinellx?' + llxParams.toString();
      } else {
        // Standard Timeline API
        var params = new URLSearchParams({
          key: me.getApiKey(),
          unitGroup: me.unitGroup,
          include: me.hasSession ? 'days,hours,current,alerts,minutes' : 'days,hours,current,alerts',
          iconSet: 'icons2'
        });
        params.set('elements', '+precipremote,+reflectivity,+reflectivity2,+uvindex2,+aqius,+aqieur,+pm1,+pm2p5,+pm10,+o3,+no2,+so2,+co');
        if (me.useV2Forecast) params.set('options', 'usev2forecast');
        url = VcGlobal.SERVER_ROOT + '/VisualCrossingWebServices/rest/services/timeline/' + loc + '?' + params.toString();
      }
      fetch(url, {
        method: 'GET',
        mode: 'cors',
        cache: 'no-cache',
        credentials: 'same-origin',
        redirect: 'follow',
        referrerPolicy: 'strict-origin-when-cross-origin',
        signal: me._fetchAbort.signal
      }).then(function (response) {
        if (!response.ok) throw response;
        return response.json();
      }).then(function (data) {
        if (!isRefresh) me.loadingStatus = 'Processing weather data...';
        // Convert LLX flat format if needed
        if (me.useLlxApi) {
          data = me.convertLlxToHierarchical(data);
        }
        me.wxdata = data;
        if (me.wxdata.unitGroup) me.unitGroup = me.wxdata.unitGroup;
        me.loadingStatus = '';
        me.isLoading = false;
        me.isRefreshing = false;
        me._closePullIndicator();
        me._lastFetchTime = Date.now();
        me.lastUpdated = new Date();

        // If display name is still raw lat,lon, try to reverse geocode
        if (me.selectedLocation && me.wxdata.latitude != null && me.wxdata.longitude != null) {
          var curName = me.selectedLocation.name || '';
          // Check if name looks like raw coordinates (e.g. "40.7128,-74.0060")
          var coordPattern = /^-?\d+\.\d+\s*,\s*-?\d+\.\d+$/;
          if (!curName || coordPattern.test(curName)) {
            VcGeocoder.reverseGeocode(me.wxdata.latitude, me.wxdata.longitude).then(function (result) {
              if (result && result.displayName) {
                me.selectedLocation = Object.assign({}, me.selectedLocation, { name: result.displayName });
                VcLocationsStorage.addLocation(me.selectedLocation);
                me.refreshLocationsHistory();
              }
            });
          }
        }
        me.$nextTick(function () {
          if (!me.leafletMap) {
            me.initMap();
          } else {
            me.updateMapView();
          }
          me.initializeMapDateTime();
          if (me.activeTab === 'rain') {
            me.renderRainNowcastChart();
            me.renderRainHourlyChart();
          } else {
            me.renderNowcastChart();
            me.renderMiniRainChart();
          }
          me.scrollHourlyToNow();
          if (me.activeTab === 'charts') me.renderAllCharts();
        });
      }).catch(function (error) {
        if (error && error.name === 'AbortError') return; // Cancelled, ignore
        console.error('Error loading weather data:', error);
        if (error && error.text) {
          error.text().then(function (msg) {
            me.errorStatus = msg || 'Failed to load weather data';
          });
        } else {
          me.errorStatus = (error && error.message) || 'Failed to load weather data';
        }
        me.loadingStatus = '';
        me.isLoading = false;
        me.isRefreshing = false;
        me._closePullIndicator();
      });
    },

    getApiKey() {
      var vcCommunicator = window.vcCommunicator;
      if (vcCommunicator && vcCommunicator.sessionState && vcCommunicator.sessionState.apiKey) {
        return vcCommunicator.sessionState.apiKey;
      }
      if (this.masterKey) return this.masterKey;
      return  VcGlobal.GLOBAL_KEY;
    },

    /** Fetch precipitation legend from the map tile service and cache it.
     *  The legend provides the official color-to-rate mapping used by the radar tiles. */
    fetchPrecipLegend() {
      var me = this;
      var url = VcGlobal.TILE_SERVER_ROOT +
        '/VisualCrossingWebServices/rest/api/v1/map/legend/precipcomposite' +
        '?key=' + VcGlobal.MAP_TILE_KEY +
        '&unitGroup=' + me.unitGroup;
      fetch(url, { method: 'GET', mode: 'cors', cache: 'default' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (data) {
          if (data && data.length) {
            // data is [[rain bins], [snow bins], [frozen bins]]
            // Store rain group (index 0) — rates are already in user units
            me._precipLegend = data[0];
            // Re-render rain charts so they use the legend colors matching the map tiles
            if (me.wxdata) {
              me.$nextTick(function () {
                if (me.activeTab === 'rain') {
                  me.renderRainNowcastChart();
                  me.renderRainHourlyChart();
                } else {
                  me.renderNowcastChart();
                  me.renderMiniRainChart();
                }
              });
            }
          }
        })
        .catch(function (err) {
          console.warn('[WeatherLive] legend fetch failed:', err);
        });
    },

    /* Set LLX location params on a URLSearchParams object.
       Prefers lat/lon from wxdata if available, then parses the location string. */
    _setLlxLocationParams(params) {
      var me = this;
      var locStr = me.selectedLocation && me.selectedLocation.location;
      if (!locStr) return;
      // Check for lat,lon format
      var parts = locStr.split(',');
      if (parts.length === 2 && !isNaN(parseFloat(parts[0].trim())) && !isNaN(parseFloat(parts[1].trim()))) {
        params.set('latitude', parts[0].trim());
        params.set('longitude', parts[1].trim());
        return;
      }
      // If we already have resolved lat/lon from a previous fetch for the SAME location, use those
      if (me.wxdata && me.wxdata.latitude != null && me.wxdata.longitude != null) {
        var wxAddr = me.wxdata.resolvedAddress || '';
        // Only reuse wxdata coords if the resolved address matches the current location
        if (wxAddr && locStr && (wxAddr.toLowerCase().indexOf(locStr.toLowerCase()) >= 0 || locStr.toLowerCase().indexOf(wxAddr.split(',')[0].trim().toLowerCase()) >= 0)) {
          params.set('latitude', me.wxdata.latitude);
          params.set('longitude', me.wxdata.longitude);
          return;
        }
      }
      // Parse as "City, Admin1, Country" or "City, Country"
      var trimmed = parts.map(function (p) { return p.trim(); });
      if (trimmed.length >= 3) {
        params.set('city', trimmed[0]);
        params.set('admin1', trimmed[1]);
        params.set('country', trimmed[2]);
      } else if (trimmed.length === 2) {
        params.set('city', trimmed[0]);
        params.set('country', trimmed[1]);
      } else {
        params.set('city', trimmed[0]);
      }
    },

    openSignIn() {
      this.signInError = null;
      this.signInPassword = '';
      this.showSignInForm = true;
      // Switch to rain tab so the form is visible
      if (this.activeTab !== 'rain') this.activeTab = 'rain';
    },

    closeSignIn() {
      this.showSignInForm = false;
      this.signInPassword = '';
      this.signInError = null;
    },

    submitSignIn() {
      var me = this;
      var vc = window.vcCommunicator;
      if (!vc) {
        me.signInError = 'Unable to connect. Please try again.';
        return;
      }
      if (!me.signInUsername || !me.signInUsername.trim()) {
        me.signInError = 'Please enter your email address.';
        return;
      }
      if (!me.signInPassword) {
        me.signInError = 'Please enter your password.';
        return;
      }
      me.signInLoading = true;
      me.signInError = null;
      vc.createsession(me.signInUsername.trim(), me.signInPassword)
        .then(function () {
          me.signInLoading = false;
          me.showSignInForm = false;
          me.signInPassword = '';
        })
        .catch(function (error) {
          me.signInLoading = false;
          me.signInError = error || 'Sign in failed. Please check your credentials.';
        });
    },

    // ---- Location ----
    searchLocation() {
      var me = this;
      var query = me.locationQuery.trim();
      if (!query) return;

      function doSearch(location, name) {
        me.selectedLocation = { location: location, name: name };
        me.isGpsLocation = false;
        VcLocationsStorage.setValue('wl_isGpsLocation', false);
        VcLocationsStorage.addLocation(me.selectedLocation);
        me.refreshLocationsHistory();
        me.showLocationSearch = false;
        me.locationQuery = '';
        me.historyData = null;
        me._historyLastDate = null;
        me.fetchWeatherData(true);
      }

      // Try Apple forward geocoding to resolve to lat/lon + display name
      VcGeocoder.forwardGeocode(query).then(function (result) {
        if (result && result.latitude != null && result.longitude != null) {
          var latlon = result.latitude.toFixed(4) + ',' + result.longitude.toFixed(4);
          doSearch(latlon, result.displayName || query);
        } else {
          // Non-native or no result — use raw query
          doSearch(query, query);
        }
      }).catch(function (err) {
        console.warn('Forward geocode failed, using raw query:', err);
        doSearch(query, query);
      });
    },

    selectLocation(loc) {
      var me = this;
      me.selectedLocation = loc;
      VcLocationsStorage.addLocation(loc);
      me.refreshLocationsHistory();
      me.showLocationSearch = false;
      me.locationQuery = '';
      me.isGpsLocation = false;
      VcLocationsStorage.setValue('wl_isGpsLocation', false);
      me.historyData = null;
      me._historyLastDate = null;
      me.fetchWeatherData(true);
    },

    findMyLocation() {
      var me = this;
      me.gpsLocating = true;
      me.loadingStatus = 'Acquiring GPS location...';
      VcFindMyLocation.findMyLocation(true).then(function (location) {
        me.gpsLocating = false;
        if (!location) { me.loadingStatus = ''; return; }
        // Check if location is lat,lon (GPS) or an address string (IP fallback)
        var parts = location.split(',');
        var isLatLon = parts.length === 2 && !isNaN(parseFloat(parts[0].trim())) && !isNaN(parseFloat(parts[1].trim()));
        if (isLatLon) {
          // Start weather fetch immediately with coordinates — don't wait for reverse geocode
          me.selectedLocation = { location: location, name: location };
          me.isGpsLocation = true;
          VcLocationsStorage.setValue('wl_isGpsLocation', true);
          VcLocationsStorage.addLocation(me.selectedLocation);
          me.refreshLocationsHistory();
          me.showLocationSearch = false;
          me.historyData = null;
          me._historyLastDate = null;
          me.fetchWeatherData(true);
          // Reverse geocode in background to update display name
          VcGeocoder.reverseGeocode(parseFloat(parts[0].trim()), parseFloat(parts[1].trim())).then(function (result) {
            if (result && result.displayName) {
              me.selectedLocation = { location: location, name: result.displayName };
              VcLocationsStorage.addLocation(me.selectedLocation);
            }
          });
        } else {
          // IP fallback returned an address string — GPS not actually working
          me.selectedLocation = { location: location, name: location };
          me.isGpsLocation = false;
          VcLocationsStorage.setValue('wl_isGpsLocation', false);
          VcLocationsStorage.addLocation(me.selectedLocation);
          me.refreshLocationsHistory();
          me.showLocationSearch = false;
          me.historyData = null;
          me._historyLastDate = null;
          me.fetchWeatherData(true);
        }
      }).catch(function (err) {
        me.gpsLocating = false;
        console.error('Geolocation failed:', err);
      });
    },

    _checkGpsLocationChange() {
      var me = this;
      if (!me.isGpsLocation || !me.wxdata) return;
      VcFindMyLocation.findMyLocation(true).then(function (newCoords) {
        if (!newCoords) return;
        var parts = newCoords.split(',');
        var isLatLon = parts.length === 2 && !isNaN(parseFloat(parts[0].trim())) && !isNaN(parseFloat(parts[1].trim()));
        if (!isLatLon) {
          // GPS failed, fell back to IP — stop tracking GPS
          console.log('[WeatherLive] GPS check returned IP fallback, disabling GPS tracking');
          me.isGpsLocation = false;
          VcLocationsStorage.setValue('wl_isGpsLocation', false);
          return;
        }
        var newLat = parseFloat(parts[0]);
        var newLon = parseFloat(parts[1]);
        var oldLat = me.wxdata.latitude;
        var oldLon = me.wxdata.longitude;
        if (!oldLat || !oldLon) return;
        // ~1 km threshold (0.01 degrees ≈ 1.1 km)
        var dLat = Math.abs(newLat - oldLat);
        var dLon = Math.abs(newLon - oldLon);
        if (dLat > 0.01 || dLon > 0.01) {
          console.log('GPS location changed significantly, updating...');
          VcGeocoder.reverseGeocode(newLat, newLon).then(function (result) {
            var displayName = (result && result.displayName) ? result.displayName : newCoords;
            me.selectedLocation = { location: newCoords, name: displayName };
            VcLocationsStorage.addLocation(me.selectedLocation);
            me.refreshLocationsHistory();
            me.fetchWeatherData(true);
          });
        }
      }).catch(function (err) {
        console.warn('GPS location check failed:', err);
      });
    },

    refreshLocationsHistory() {
      this.locationsHistory = VcLocationsStorage.getLocations();
    },

    // ---- Unit group ----
    setUnitGroup(ug) {
      this.unitGroup = ug;
      this.showUnitMenu = false;
      VcLocationsStorage.setUnitGroup(ug);
      this.fetchWeatherData(true);
    },

    toggleV2Forecast() {
      this.useV2Forecast = !this.useV2Forecast;
      VcLocationsStorage.setValue('wl_useV2Forecast', this.useV2Forecast ? 1 : 0);
      this.fetchWeatherData(true);
    },

    toggleLlxApi() {
      this.useLlxApi = !this.useLlxApi;
      VcLocationsStorage.setValue('wl_useLlxApi', this.useLlxApi ? 1 : 0);
      this.fetchWeatherData(true);
    },

    async sendTestNotification() {
      var me = this;
      me.testNotifStatus = 'Scheduling...';
      try {
        var { LocalNotifications } = await import('@capacitor/local-notifications');
        // Request permission first
        var perm = await LocalNotifications.requestPermissions();
        if (perm.display !== 'granted') {
          me.testNotifStatus = 'Permission denied';
          setTimeout(function () { me.testNotifStatus = null; }, 3000);
          return;
        }
        // Schedule notification 30 seconds from now
        var triggerAt = new Date(Date.now() + 30 * 1000);
        await LocalNotifications.schedule({
          notifications: [
            {
              title: '\u26a0\ufe0f Weather Alert (Test)',
              body: 'This is a test notification from WeatherLive. If you see this, notifications are working!',
              id: 99999,
              schedule: { at: triggerAt },
              sound: 'default',
              smallIcon: 'ic_stat_icon_config_sample',
              iconColor: '#3b82f6',
            },
          ],
        });
        me.testNotifStatus = 'Scheduled! (30s)';
        setTimeout(function () { me.testNotifStatus = null; }, 5000);
      } catch (e) {
        console.error('[WeatherLive] Test notification error:', e);
        me.testNotifStatus = 'Error: ' + (e.message || e);
        setTimeout(function () { me.testNotifStatus = null; }, 5000);
      }
    },

    async _initBackgroundFetch() {
      var me = this;
      try {
        var { BackgroundFetch } = await import('@transistorsoft/capacitor-background-fetch');
        var status = await BackgroundFetch.configure({
          minimumFetchInterval: 15, // minutes (iOS minimum)
          stopOnTerminate: false,
          enableHeadless: false,
        }, async function (taskId) {
          // Background fetch event fired
          console.log('[BackgroundFetch] Event:', taskId);
          try {
            await me._backgroundWeatherCheck();
          } catch (err) {
            console.error('[BackgroundFetch] Weather check error:', err);
          }
          await BackgroundFetch.finish(taskId);
        }, async function (taskId) {
          // Timeout — must finish immediately
          console.log('[BackgroundFetch] Timeout:', taskId);
          await BackgroundFetch.finish(taskId);
        });
        console.log('[BackgroundFetch] Configured, status:', status);
      } catch (e) {
        console.warn('[BackgroundFetch] Not available:', e);
      }
    },

    async _backgroundWeatherCheck() {
      var me = this;
      // Get the current location from saved state
      var location = me.selectedLocation && me.selectedLocation.location;
      if (!location) return;

      var apiKey = me.getApiKey();
      var unitGroup = me.unitGroup || 'us';
      var url = 'https://weather.visualcrossing.com/VisualCrossingWebServices/rest/services/timeline/'
        + encodeURIComponent(location) + '?key=' + apiKey
        + '&unitGroup=' + unitGroup
        + '&include=alerts,hours,current&iconSet=icons2';

      var response = await fetch(url, { method: 'GET', mode: 'cors', cache: 'no-cache' });
      if (!response.ok) return;
      var data = await response.json();

      var notifications = [];
      var notifId = 10000 + Math.floor(Math.random() * 80000);

      // Check for weather alerts
      if (data.alerts && data.alerts.length > 0) {
        var lastAlertId = me._lastBgAlertId || '';
        var alert = data.alerts[0];
        var alertId = (alert.id || alert.event || '').substring(0, 50);
        // Only notify if this is a new alert
        if (alertId !== lastAlertId) {
          me._lastBgAlertId = alertId;
          notifications.push({
            title: '\u26a0\ufe0f ' + (alert.event || 'Weather Alert'),
            body: alert.headline || alert.description || 'A weather alert has been issued for your area.',
            id: notifId++,
          });
        }
      }

      // Check for rain in the next 3 hours
      if (data.days && data.days[0] && data.days[0].hours) {
        var nowEpoch = Date.now() / 1000;
        var rainHours = data.days[0].hours.filter(function (h) {
          return h.datetimeEpoch >= nowEpoch && h.datetimeEpoch < nowEpoch + 3 * 3600 && h.precipprob >= 50;
        });
        if (rainHours.length > 0 && !me._lastBgRainNotifTime) {
          // Only send rain notification once per 2 hours
          me._lastBgRainNotifTime = Date.now();
          var rainHour = rainHours[0];
          var timeStr = new Date(rainHour.datetimeEpoch * 1000).toLocaleTimeString(undefined, me.use24HourTime ? { hour: 'numeric', hourCycle: 'h23' } : { hour: 'numeric', hour12: true });
          notifications.push({
            title: '\ud83c\udf27\ufe0f Rain Expected',
            body: 'Rain likely around ' + timeStr + ' (' + Math.round(rainHour.precipprob) + '% chance).',
            id: notifId++,
          });
        } else if (me._lastBgRainNotifTime && Date.now() - me._lastBgRainNotifTime > 2 * 3600 * 1000) {
          // Reset rain cooldown after 2 hours
          me._lastBgRainNotifTime = null;
        }
      }

      if (notifications.length > 0) {
        var { LocalNotifications } = await import('@capacitor/local-notifications');
        var perm = await LocalNotifications.requestPermissions();
        if (perm.display === 'granted') {
          await LocalNotifications.schedule({
            notifications: notifications.map(function (n) {
              return {
                title: n.title,
                body: n.body,
                id: n.id,
                sound: 'default',
                smallIcon: 'ic_stat_icon_config_sample',
                iconColor: '#3b82f6',
              };
            }),
          });
          console.log('[BackgroundFetch] Sent', notifications.length, 'notification(s)');
        }
      }
    },

    // Convert LLX flat JSON to hierarchical format (matching Timeline API structure)
    convertLlxToHierarchical(flatData) {
      if (!flatData || !flatData.days) return flatData;
      // If already hierarchical (array of objects), return as-is
      if (Array.isArray(flatData.days)) return flatData;

      var result = Object.assign({}, flatData);
      var dictionaries = flatData.dictionaries || {};
      var flatDays = flatData.days;
      var keys = Object.keys(flatDays);
      if (!keys.length) return result;

      // Determine timezone offset for synthesizing datetimeEpoch
      var tzoffset = flatData.tzoffset || (flatData.info && flatData.info.tzoffset) || 0;

      // Helper: parse a local datetime string to UTC epoch seconds
      // datetime strings are in the location's local time; tzoffset is hours ahead of UTC
      function localDatetimeToEpoch(dtStr) {
        if (!dtStr) return undefined;
        var normalized = dtStr;
        if (normalized.length === 10) normalized += 'T00:00:00'; // date only
        else if (normalized.length <= 13) normalized += ':00:00'; // truncated hour e.g. "2026-02-12T00"
        else if (normalized.length <= 16) normalized += ':00'; // missing seconds
        var ms = Date.parse(normalized + 'Z'); // parse as UTC
        if (isNaN(ms)) return undefined;
        return ms / 1000 - tzoffset * 3600; // adjust to real UTC
      }

      var numDays = flatDays[keys[0]].length;
      var daysArray = [];

      for (var i = 0; i < numDays; i++) {
        var day = {};
        keys.forEach(function (key) {
          var val = flatDays[key][i];
          // Resolve dictionary indices for conditions and icon
          if (key === 'conditions' && typeof val === 'number' && dictionaries.conditions) {
            val = dictionaries.conditions[val] || val;
          } else if (key === 'icon' && typeof val === 'number' && dictionaries.icon) {
            val = dictionaries.icon[val] || val;
          }
          day[key] = val;
        });
        // Synthesize datetimeEpoch if missing
        if (day.datetimeEpoch == null && day.datetime) {
          day.datetimeEpoch = localDatetimeToEpoch(day.datetime);
        }
        day.hours = [];
        daysArray.push(day);
      }

      // Process hours if present
      if (flatData.hours) {
        var flatHours = flatData.hours;
        var hourKeys = Object.keys(flatHours);
        if (hourKeys.length) {
          var numHours = flatHours[hourKeys[0]].length;
          for (var h = 0; h < numHours; h++) {
            var hour = {};
            hourKeys.forEach(function (key) {
              var val = flatHours[key][h];
              // Resolve dictionary indices for conditions and icon
              if (key === 'conditions' && typeof val === 'number' && dictionaries.conditions) {
                val = dictionaries.conditions[val] || val;
              } else if (key === 'icon' && typeof val === 'number' && dictionaries.icon) {
                val = dictionaries.icon[val] || val;
              }
              hour[key] = val;
            });
            // Normalize truncated datetime (e.g. "2026-02-12T00" -> "2026-02-12T00:00:00")
            if (hour.datetime && hour.datetime.length <= 13) {
              hour.datetime = hour.datetime + ':00:00';
            }
            // Synthesize datetimeEpoch if missing
            if (hour.datetimeEpoch == null && hour.datetime) {
              hour.datetimeEpoch = localDatetimeToEpoch(hour.datetime);
            }
            // Assign hour to the correct day by matching date prefix
            var hourDateStr = (hour.datetime || '').substring(0, 10);
            var assigned = false;
            for (var d = 0; d < daysArray.length; d++) {
              if (daysArray[d].datetime === hourDateStr) {
                daysArray[d].hours.push(hour);
                assigned = true;
                break;
              }
            }
            // Fallback: assign by datetimeEpoch if date string match failed
            if (!assigned && hour.datetimeEpoch) {
              for (var d2 = 0; d2 < daysArray.length; d2++) {
                if (daysArray[d2].datetimeEpoch && hour.datetimeEpoch >= daysArray[d2].datetimeEpoch &&
                    hour.datetimeEpoch < daysArray[d2].datetimeEpoch + 86400) {
                  daysArray[d2].hours.push(hour);
                  break;
                }
              }
            }
          }
        }
        delete result.hours;
      }

      // Process minutes if present - convert from flat arrays to array of objects
      if (flatData.minutes) {
        var flatMinutes = flatData.minutes;
        var minuteKeys = Object.keys(flatMinutes);
        if (minuteKeys.length && Array.isArray(flatMinutes[minuteKeys[0]])) {
          var numMinutes = flatMinutes[minuteKeys[0]].length;
          var minutesArray = [];
          for (var m = 0; m < numMinutes; m++) {
            var minute = {};
            minuteKeys.forEach(function (key) {
              minute[key] = flatMinutes[key][m];
            });
            minutesArray.push(minute);
          }
          result.minutes = minutesArray;
        }
      }

      result.days = daysArray;

      // latitude/longitude are at top level in LLX response,
      // already copied by Object.assign. Also check info section as fallback.
      if (flatData.info) {
        if (flatData.info.latitude != null && result.latitude == null) result.latitude = flatData.info.latitude;
        if (flatData.info.longitude != null && result.longitude == null) result.longitude = flatData.info.longitude;
        if (flatData.info.address != null && result.address == null) result.address = flatData.info.address;
        if (flatData.info.resolvedAddress != null && result.resolvedAddress == null) result.resolvedAddress = flatData.info.resolvedAddress;
        if (flatData.info.timezone != null && result.timezone == null) result.timezone = flatData.info.timezone;
        if (flatData.info.tzoffset != null && result.tzoffset == null) result.tzoffset = flatData.info.tzoffset;
      }

      // Clean up dictionaries from result since values are now resolved
      delete result.dictionaries;

      return result;
    },

    // ---- History data fetching ----
    fetchHistoryData(forceRefresh) {
      var me = this;
      if (!me.selectedLocation) return;

      // Check if we already have today's history and skip unless forced
      var todayStr = new Date().toISOString().slice(0, 10);
      if (!forceRefresh && me.historyData && me._historyLastDate === todayStr) return;

      me.isLoadingHistory = true;
      me.historyError = null;

      var loc = encodeURIComponent(me.selectedLocation.location);
      // Build date range: 3 days for LLX, 7 days for standard API
      var historyLength = me.useLlxApi ? 3 : 7;
      var end = new Date();
      end.setDate(end.getDate() - 1); // yesterday
      var start = new Date();
      start.setDate(start.getDate() - historyLength); // N days ago

      var startStr = start.toISOString().slice(0, 10);
      var endStr = end.toISOString().slice(0, 10);

      var url;
      if (me.useLlxApi) {
        var llxParams = new URLSearchParams({
          key: me.getApiKey(),
          unitGroup: me.unitGroup,
          include: 'days,hours,info',
          iconSet: 'icons2',
          datestart: startStr,
          dateend: endStr,
        });
        llxParams.set('elements', 'add:datetimeEpoch,add:dew,add:precipcover,add:preciptype,add:snowdepth,add:pressure,add:solarradiation,add:solarenergy,add:uvindex,add:severerisk,add:sunrise,add:sunset,add:moonphase,add:description,add:offset,add:source,add:sunriseEpoch,add:sunsetEpoch');
        me._setLlxLocationParams(llxParams);
        url = VcGlobal.SERVER_ROOT + '/VisualCrossingWebServices/rest/api/v1/timelinellx?' + llxParams.toString();
      } else {
        var params = new URLSearchParams({
          key: me.getApiKey(),
          unitGroup: me.unitGroup,
          include: 'days,hours',
          iconSet: 'icons2'
        });
        url = VcGlobal.SERVER_ROOT + '/VisualCrossingWebServices/rest/services/timeline/' + loc + '/' + startStr + '/' + endStr + '?' + params.toString();
      }

      fetch(url, {
        method: 'GET',
        mode: 'cors',
        cache: 'no-cache',
        credentials: 'same-origin',
        redirect: 'follow',
        referrerPolicy: 'strict-origin-when-cross-origin',
      }).then(function (response) {
        if (!response.ok) throw response;
        return response.json();
      }).then(function (data) {
        // Convert LLX flat format if needed
        if (me.useLlxApi) {
          data = me.convertLlxToHierarchical(data);
        }
        me.historyData = data;
        me._historyLastDate = todayStr;
        me.isLoadingHistory = false;
      }).catch(function (error) {
        console.error('Error loading history data:', error);
        if (error && error.text) {
          error.text().then(function (msg) {
            me.historyError = msg || 'Failed to load history data';
          });
        } else {
          me.historyError = (error && error.message) || 'Failed to load history data';
        }
        me.isLoadingHistory = false;
      });
    },

    _startHistoryRefreshTimer() {
      var me = this;
      if (me._historyRefreshTimer) clearInterval(me._historyRefreshTimer);
      // Refresh history data every 2 hours
      me._historyRefreshTimer = setInterval(function () {
        if (me.activeTab === 'history' && me.selectedLocation) {
          var todayStr = new Date().toISOString().slice(0, 10);
          if (me._historyLastDate !== todayStr) {
            // Day changed — force refresh
            me.fetchHistoryData(true);
          } else {
            me.fetchHistoryData(false);
          }
        }
      }, 2 * 60 * 60 * 1000); // 2 hours
    },

    // ---- Formatting helpers ----
    fmtVal(period, element, shortForm, formatElement) {
      if (!period) return '-';
      formatElement = formatElement || element;
      if (element === 'aqi') return this.fmtAqi(period, shortForm);
      if (element === 'wind') return this.fmtWind(period);
      var val = period[element];
      var fixed = shortForm ? 0 : null;
      return _formatElementValue(val, formatElement, this.unitGroup, fixed, null, null, shortForm);
    },

    /** Format an accumulated precip/snow value with units */
    fmtPrecipAccum(value, element) {
      if (value == null || value <= 0) return '0';
      return _formatElementValue(value, element, this.unitGroup);
    },

    fmtAqi(period, shortForm) {
      var aqi = period.aqius;
      if (!aqi && aqi !== 0) return '-';
      return _getAqiText(aqi, shortForm);
    },

    fmtWind(period) {
      return _getCompassDirection(period.winddir) + ' ' +
        _formatElementValue(period.windspeed, 'wind', this.unitGroup);
    },

    fmtTime(period) {
      if (!period) return '-';
      return _formatTime(period.datetimeEpoch, this.wxdata && this.wxdata.timezone, this.use24HourTime);
    },

    fmtDayName(day) {
      if (!day) return '';
      var tz = (this.wxdata && this.wxdata.timezone) || (this.historyData && this.historyData.timezone);
      if (_isToday(day.datetimeEpoch, tz)) return 'Today';
      if (_isYesterday(day.datetimeEpoch, tz)) return 'Yest.';
      var d = new Date(day.datetimeEpoch * 1000);
      return d.toLocaleDateString(undefined, { weekday: 'short' });
    },

    fmtDateShort(day) {
      if (!day) return '';
      var d = new Date(day.datetimeEpoch * 1000);
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    },

    fmtSunTime(epoch) {
      if (!epoch) return '-';
      return _formatTime(epoch, this.wxdata && this.wxdata.timezone, this.use24HourTime);
    },

    fmtSunTimeHM(epoch) {
      if (!epoch) return '-';
      var opts = this.use24HourTime
        ? { hour: 'numeric', minute: '2-digit', hourCycle: 'h23', timeZone: this.wxdata && this.wxdata.timezone }
        : { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: this.wxdata && this.wxdata.timezone };
      return _formatDateTime(epoch, this.wxdata && this.wxdata.timezone, opts);
    },

    isCurrentHour(hour) {
      if (!hour) return false;
      var now = new Date().getTime() / 1000;
      return Math.abs(hour.datetimeEpoch - now) < 3600;
    },

    isPastHour(hour) {
      if (!hour) return false;
      var now = new Date().getTime() / 1000;
      return hour.datetimeEpoch < now - 3600;
    },

    hourProximityClass(hour) {
      if (!hour || !hour.datetimeEpoch) return '';
      var now = new Date();
      // Round current time to nearest whole hour (10:29 → 10, 10:31 → 11)
      var currentHourEpoch = Math.round(now.getTime() / 3600000) * 3600;
      var hourEpoch = hour.datetimeEpoch;
      if (hourEpoch === currentHourEpoch) return 'wl-hour-card--current';
      if (hourEpoch < currentHourEpoch) return 'wl-hour-card--past';
      return '';
    },

    hourDayLabel(hour, index) {
      if (!hour || !hour.datetimeEpoch || !this.todayHours) return null;
      var tz = this.wxdata && this.wxdata.timezone;
      // Only show a label when the day changes from the previous hour
      if (index > 0) {
        var prevHour = this.todayHours[index - 1];
        if (prevHour && prevHour.datetimeEpoch) {
          var sameDay = _isSameDate(hour.datetimeEpoch, new Date(prevHour.datetimeEpoch * 1000), tz);
          if (sameDay) return null;
        }
      } else {
        // Don't label the very first hour (it's implied as "today")
        return null;
      }
      // Determine the label
      var tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      if (_isSameDate(hour.datetimeEpoch, tomorrow, tz)) return 'Tomorrow';
      // For dates further out, show the date
      return _formatDateTime(hour.datetimeEpoch, tz, { weekday: 'short', month: 'short', day: 'numeric', timeZone: tz });
    },

    saveDarkMode() {
      VcLocationsStorage.setValue('wl_darkMode', this.darkMode ? 1 : 0);
    },

    saveTimeFormat() {
      VcLocationsStorage.setValue('wl_use24HourTime', this.use24HourTime ? 1 : 0);
    },

    // ---- Alert helpers ----
    getAlertBadgeClass(alert) {
      var alertInfo = getAlertByType(alert.event);
      var category = alertInfo ? alertInfo.category : 'warning';
      return {
        'wl-alert-badge--danger': category === 'danger',
        'wl-alert-badge--warning': category === 'warning',
        'wl-alert-badge--info': category === 'info',
      };
    },

    getAlertIconClass(alert) {
      var alertInfo = getAlertByType(alert.event);
      return alertInfo ? getAlertIcon(alertInfo.category) : 'bi-exclamation-circle-fill';
    },

    formatAlertDesc(alert) {
      var desc = alert && alert.description;
      return (desc && desc.replace(/(\*)/g, '<br>')) || '';
    },

    // ---- Rain description ----
    getRainDescription(reflectivityOrRate, precipTypes, isRate) {
      var intensity;
      if (isRate) {
        // rate is in mm/hr
        if (reflectivityOrRate > 50) intensity = 'Torrential';
        else if (reflectivityOrRate > 7.6) intensity = 'Heavy';
        else if (reflectivityOrRate > 2.5) intensity = 'Moderate';
        else if (reflectivityOrRate > 0.5) intensity = 'Light';
        else if (reflectivityOrRate > 0) intensity = 'Very light';
        else return null;
      } else {
        var reflectivity = reflectivityOrRate;
        if (reflectivity > 50) intensity = 'Torrential';
        else if (reflectivity > 40) intensity = 'Heavy';
        else if (reflectivity > 20) intensity = 'Moderate';
        else if (reflectivity > 10) intensity = 'Light';
        else if (reflectivity > 0) intensity = 'Very light';
        else return null;
      }
      var pt = precipTypes && precipTypes.length ? precipTypes : ['rain'];
      var typeStr = pt.join('/');
      if (typeStr === 'rain' && intensity === 'Very light') return 'Drizzle';
      return intensity + ' ' + typeStr;
    },

    formatTimeAgo(minutes, prefix, fallback) {
      var mins = Number(minutes);
      if (isNaN(mins)) return fallback || 'invalid time';
      if (minutes < 10) return fallback || (prefix + 'any moment');
      if (minutes >= 1440) {
        var days = Math.floor(minutes / 1440);
        return prefix + days + ' day' + (days > 1 ? 's' : '');
      } else if (minutes >= 60) {
        var hours = Math.floor(minutes / 60);
        return prefix + hours + ' hour' + (hours > 1 ? 's' : '');
      }
      return prefix + minutes + ' minute' + (minutes !== 1 ? 's' : '');
    },

    // ---- Detail dialog ----
    openDetail(record, type) {
      this.detailRecord = record;
      this.detailType = type || 'hour';
    },

    sectionHasData(section) {
      var me = this;
      if (!me.detailRecord) return false;
      for (var i = 0; i < section.elements.length; i++) {
        if (me.hasValue(me.detailRecord[section.elements[i].key])) return true;
      }
      return false;
    },

    hasValue(val) {
      if (val === null || val === undefined) return false;
      if (typeof val === 'number' && isNaN(val)) return false;
      if (typeof val === 'string' && val === '') return false;
      if (Array.isArray(val) && val.length === 0) return false;
      return true;
    },

    getCompassDir(degrees) {
      return _getCompassDirection(degrees);
    },

    getMoonPhaseLabel(phase) {
      if (phase === null || phase === undefined) return '';
      return _getMoonPhaseDesc(phase);
    },

    // ---- Day expand ----
    toggleDayExpand(day) {
      var me = this;
      var opening = me.expandedDay !== day;
      me.expandedDay = opening ? day : null;
      if (opening && day.hours) {
        me.$nextTick(function () {
          var hoursEl = document.querySelector('.wl-day-card__hours');
          if (!hoursEl) return;
          var rows = hoursEl.querySelectorAll('.wl-day-hour');
          var target = null;
          for (var i = 0; i < rows.length; i++) {
            var timeText = rows[i].querySelector('.wl-day-hour__time');
            if (timeText && /^7[\s:]|^7\s?(AM|am)/i.test(timeText.textContent.trim())) {
              target = rows[i];
              break;
            }
          }
          if (!target && day.hours.length > 7) {
            target = rows[7]; // fallback: index 7 = 07:00
          }
          if (target) {
            hoursEl.scrollTop = target.offsetTop - hoursEl.offsetTop;
          }
        });
      }
    },

    // ---- Map ----
    initMap() {
      var me = this;
      var mapEl = me.$refs.mapEl;
      if (!mapEl || !L) return;

      if (me.leafletMap) {
        me.updateMapView();
        return;
      }

      var lat = (me.wxdata && me.wxdata.latitude) || 40;
      var lon = (me.wxdata && me.wxdata.longitude) || -100;

      var defaultZoom = me.hasTodayPrecip ? 8 : 5;
      var isSmall = me.mapDisplayMode !== 'full';
      me.leafletMap = new L.Map(mapEl, {
        fadeAnimation: false,
        minZoom: 1,
        maxZoom: 16,
        center: [lat, lon],
        zoom: defaultZoom,
        zoomControl: false,
        attributionControl: false,
        doubleClickZoom: !isSmall,
        dragging: !isSmall,
        scrollWheelZoom: !isSmall,
        touchZoom: !isSmall,
        boxZoom: false,
        keyboard: false,
        tap: false,
      });

      // Add zoom control only when map is full size
      if (me.mapDisplayMode === 'full') {
        me._mapZoomControl = L.control.zoom({ position: 'topright' });
        me._mapZoomControl.addTo(me.leafletMap);
      }
      L.control.attribution({ position: 'bottomright', prefix: false }).addTo(me.leafletMap);

      // Resize observer for container — stored on instance for cleanup
      if (me._mapResizeObserver) me._mapResizeObserver.disconnect();
      me._mapResizeObserver = new ResizeObserver(function () {
        if (me.leafletMap) me.leafletMap.invalidateSize();
      });
      me._mapResizeObserver.observe(mapEl);
      if (mapEl.parentElement) me._mapResizeObserver.observe(mapEl.parentElement);

      // Base map — dark or light based on darkMode
      me.baseLayer = me.createBaseLayer();
      me.leafletMap.addLayer(me.baseLayer);

      // Create weather tile layer
      me.wxTileLayer = me.createWxLayer(me.activeMapLayer);
      me.leafletMap.addLayer(me.wxTileLayer);

      me.updateMapView();

      // Restore persisted overlay toggles
      if (me.overlayLightning) {
        me.overlayLightning = false;
        me.toggleOverlay('lightning');
      }
      if (me.overlayHail) {
        me.overlayHail = false;
        me.toggleOverlay('hail');
      }

      // Context menu for location
      me.leafletMap.on('contextmenu', function (e) {
        me._showMapLocationMenu(e.latlng);
      });

      // Long-press (hold) for touch & desktop
      var longPressTimer = null;
      var longPressLatlng = null;
      var longPressStartXY = null;
      var longPressFired = false;

      function cancelLongPress() {
        if (longPressTimer) { clearTimeout(longPressTimer); longPressTimer = null; }
      }

      // Desktop: mouse events
      me.leafletMap.on('mousedown', function (e) {
        if (e.originalEvent.button !== 0) return;
        longPressFired = false;
        longPressLatlng = e.latlng;
        longPressStartXY = { x: e.originalEvent.clientX, y: e.originalEvent.clientY };
        longPressTimer = setTimeout(function () {
          longPressTimer = null;
          longPressFired = true;
          me._showMapLocationMenu(longPressLatlng);
        }, 600);
      });
      me.leafletMap.on('mousemove', function (e) {
        if (longPressTimer && longPressStartXY) {
          var dx = e.originalEvent.clientX - longPressStartXY.x;
          var dy = e.originalEvent.clientY - longPressStartXY.y;
          if (dx * dx + dy * dy > 100) { // >10px movement
            cancelLongPress();
          }
        }
      });
      me.leafletMap.on('mouseup dragstart zoomstart', cancelLongPress);

      // Touch: attach to map container for mobile long-press
      var mapContainer = me.leafletMap.getContainer();
      mapContainer.addEventListener('touchstart', function (e) {
        if (e.touches.length !== 1) { cancelLongPress(); return; }
        longPressFired = false;
        var touch = e.touches[0];
        longPressStartXY = { x: touch.clientX, y: touch.clientY };
        // Convert touch point to latlng
        var rect = mapContainer.getBoundingClientRect();
        var pt = L.point(touch.clientX - rect.left, touch.clientY - rect.top);
        longPressLatlng = me.leafletMap.containerPointToLatLng(pt);
        longPressTimer = setTimeout(function () {
          longPressTimer = null;
          longPressFired = true;
          me._showMapLocationMenu(longPressLatlng);
        }, 600);
      }, { passive: true });
      mapContainer.addEventListener('touchmove', function (e) {
        if (longPressTimer && longPressStartXY && e.touches.length === 1) {
          var touch = e.touches[0];
          var dx = touch.clientX - longPressStartXY.x;
          var dy = touch.clientY - longPressStartXY.y;
          if (dx * dx + dy * dy > 100) { // >10px movement = scroll/pan
            cancelLongPress();
          }
        }
      }, { passive: true });
      mapContainer.addEventListener('touchend', function () {
        cancelLongPress();
      }, { passive: true });
      mapContainer.addEventListener('touchcancel', function () {
        cancelLongPress();
      }, { passive: true });
    },

    createBaseLayer() {
      if (this.darkMode) {
        return L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}{r}.png', {
          subdomains: 'abcd', maxZoom: 20,
        });
      }
      return L.tileLayer('https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png', {
        subdomains: 'abcd', maxZoom: 20,
      });
    },

    createWxLayer(layerId) {
      var me = this;
      var config = me.mapLayerOptions.find(function (l) { return l.id === layerId; });
      if (!config) config = me.mapLayerOptions[0];

      // Use GeoJSON vector layer for alerts
      if (config.element === 'alerts') {
        return import('/src/weather-map/WxAlertVectorLayer.js').then(function (mod) {
          me._alertVectorModule = mod;
          var alertLayer = mod.createAlertVectorLayer(L, {
            key: VcGlobal.MAP_TILE_KEY,
            serverRoot: VcGlobal.TILE_SERVER_ROOT,
            opacity: 0.55,
            unitGroup: me.unitGroup,
            getDateTimeFn: function () { return (me.leafletMap && me.leafletMap.options.dateTime) || new Date(); },
          });
          alertLayer._isVectorAlert = true;
          return alertLayer;
        });
      }

      var model = null;
      if (config.element === 'precipcomposite') model = me.precipSource;

      return new L.TileLayer.WxTileLayer({
        opacity: 0.65,
        level: 0,
        model: model,
        element: config.element,
        forceRefresh: true,
        key: VcGlobal.MAP_TILE_KEY,
        unitGroup: me.unitGroup,
        dateTimeInterval: config.interval,
      });
    },

    switchMapLayer(layerId) {
      var me = this;
      if (me.activeMapLayer === layerId) return;

      me.activeMapLayer = layerId;

      var config = me.mapLayerOptions.find(function (l) { return l.id === layerId; });
      if (!config) return;

      // Remove old layer and add new
      if (me.wxTileLayer && me.leafletMap) {
        me.leafletMap.removeLayer(me.wxTileLayer);
      }

      var result = me.createWxLayer(layerId);

      // Handle async (alert vector layer) vs sync (raster tile layer)
      if (result && typeof result.then === 'function') {
        result.then(function (layer) {
          // Only add if still the active layer (user may have switched)
          if (me.activeMapLayer !== layerId) return;
          me.wxTileLayer = layer;
          me.leafletMap.addLayer(me.wxTileLayer);
          me.activeLayerInterval = config.interval;
          if (me.mapDateTime) me.setMapDateTime(me.mapDateTime);
        });
      } else {
        me.wxTileLayer = result;
        me.leafletMap.addLayer(me.wxTileLayer);
        me.activeLayerInterval = config.interval;
        if (me.mapDateTime) me.setMapDateTime(me.mapDateTime);
      }
    },

    toggleOverlay(type) {
      var me = this;
      if (!me.leafletMap) return;

      if (type === 'alerts') {
        me.overlayAlerts = !me.overlayAlerts;
        if (me.overlayAlerts) {
          me._createOverlayAlertLayer().then(function (group) {
            if (!me.overlayAlerts) return; // toggled off while loading
            me._overlayAlertsLayer = group;
            me.leafletMap.addLayer(group);
            me._startAlertFilterPoll();
            if (me.mapDateTime) me.setMapDateTime(me.mapDateTime);
          });
        } else {
          me._stopAlertFilterPoll();
          if (me._overlayAlertsLayer && me.leafletMap) {
            me.leafletMap.removeLayer(me._overlayAlertsLayer);
            me._overlayAlertsLayer = null;
          }
        }
      } else if (type === 'lightning') {
        me.overlayLightning = !me.overlayLightning;
        VcLocationsStorage.setValue('wl_overlayLightning', me.overlayLightning ? 1 : 0);
        if (me.overlayLightning) {
          me._overlayLightningLayer = new L.TileLayer.WxTileLayer({
            opacity: 0.7,
            level: 0,
            model: me.precipSource,
            element: 'lightning',
            forceRefresh: true,
            key: VcGlobal.MAP_TILE_KEY,
            unitGroup: me.unitGroup,
            dateTimeInterval: 10,
          });
          me.leafletMap.addLayer(me._overlayLightningLayer);
        } else {
          if (me._overlayLightningLayer) {
            me.leafletMap.removeLayer(me._overlayLightningLayer);
            me._overlayLightningLayer = null;
          }
        }
      } else if (type === 'hail') {
        me.overlayHail = !me.overlayHail;
        VcLocationsStorage.setValue('wl_overlayHail', me.overlayHail ? 1 : 0);
        if (me.overlayHail) {
          me._overlayHailLayer = new L.TileLayer.WxTileLayer({
            opacity: 0.7,
            level: 0,
            model: null,
            element: 'hail',
            forceRefresh: true,
            key: VcGlobal.MAP_TILE_KEY,
            unitGroup: me.unitGroup,
            dateTimeInterval: 10,
          });
          me.leafletMap.addLayer(me._overlayHailLayer);
        } else {
          if (me._overlayHailLayer) {
            me.leafletMap.removeLayer(me._overlayHailLayer);
            me._overlayHailLayer = null;
          }
        }
      }
    },

    /** Create a hybrid alert overlay: image tiles at zoom ≤7, GeoJSON vector at zoom ≥8 */
    _createOverlayAlertLayer() {
      var me = this;
      return import('/src/weather-map/WxAlertVectorLayer.js').then(function (mod) {
        me._alertVectorModule = mod;
        var group = L.layerGroup();
        group._isHybridAlert = true;

        // Image tile sub-layer for zoom 1-7
        var tileLayer = L.TileLayer.WxTileLayer
          ? new L.TileLayer.WxTileLayer({
              opacity: 0.55,
              level: 0,
              model: null,
              element: 'alerts',
              forceRefresh: true,
              key: VcGlobal.MAP_TILE_KEY,
              unitGroup: me.unitGroup,
              dateTimeInterval: 60,
            })
          : null;

        // GeoJSON vector sub-layer for zoom 8+
        var vectorLayer = mod.createAlertVectorLayer(L, {
          key: VcGlobal.MAP_TILE_KEY,
          serverRoot: VcGlobal.TILE_SERVER_ROOT,
          opacity: 0.55,
          unitGroup: me.unitGroup,
          getDateTimeFn: function () { return (me.leafletMap && me.leafletMap.options.dateTime) || new Date(); },
        });

        group._alertTileLayer = tileLayer;
        group._alertVectorLayer = vectorLayer;
        group._alertZoomThreshold = 8;

        var syncVisibility = function () {
          if (!me.leafletMap) return;
          var zoom = me.leafletMap.getZoom();
          var useVector = zoom >= group._alertZoomThreshold;
          if (useVector) {
            if (tileLayer && group.hasLayer(tileLayer)) group.removeLayer(tileLayer);
            if (vectorLayer && !group.hasLayer(vectorLayer)) group.addLayer(vectorLayer);
          } else {
            if (vectorLayer && group.hasLayer(vectorLayer)) group.removeLayer(vectorLayer);
            if (tileLayer && !group.hasLayer(tileLayer)) group.addLayer(tileLayer);
          }
        };

        group.on('add', function () {
          syncVisibility();
          me.leafletMap.on('zoomend', syncVisibility);
        });
        group.on('remove', function () {
          me.leafletMap.off('zoomend', syncVisibility);
        });

        group.setAlertTime = function (dt) {
          if (vectorLayer && vectorLayer.setAlertTime) vectorLayer.setAlertTime(dt);
        };
        group._isVectorAlert = true;

        return group;
      });
    },

    // ─── Alert filter methods ───

    _refreshAlertFilterTypes() {
      var me = this;
      var v = me._overlayAlertsLayer && me._overlayAlertsLayer._alertVectorLayer;
      if (!v || !v.getEventTypes) {
        me.alertFilterTypes = [];
        me.alertHiddenCount = 0;
        return;
      }
      me.alertFilterTypes = v.getEventTypes();
      me.alertHiddenCount = me.alertFilterTypes.filter(function (t) { return !t.visible; }).length;
    },

    _startAlertFilterPoll() {
      var me = this;
      me._stopAlertFilterPoll();
      me._alertFilterTimer = setInterval(function () { me._refreshAlertFilterTypes(); }, 1500);
    },

    _stopAlertFilterPoll() {
      var me = this;
      if (me._alertFilterTimer) {
        clearInterval(me._alertFilterTimer);
        me._alertFilterTimer = null;
      }
      me.alertFilterTypes = [];
      me.alertHiddenCount = 0;
      me.alertFilterOpen = false;
    },

    onAlertFilterChange(eventKey, checked) {
      var me = this;
      var v = me._overlayAlertsLayer && me._overlayAlertsLayer._alertVectorLayer;
      if (!v) return;
      var types = v.getEventTypes();
      var hidden = types.filter(function (t) {
        if (t.event === eventKey) return !checked;
        return !t.visible;
      }).map(function (t) { return t.event; });
      v.setEventFilter(hidden);
      me._refreshAlertFilterTypes();
    },

    alertFilterShowAll() {
      var me = this;
      var v = me._overlayAlertsLayer && me._overlayAlertsLayer._alertVectorLayer;
      if (v) v.setEventFilter([]);
      me._refreshAlertFilterTypes();
    },

    alertFilterHideAll() {
      var me = this;
      var v = me._overlayAlertsLayer && me._overlayAlertsLayer._alertVectorLayer;
      if (!v) return;
      var all = v.getEventTypes().map(function (t) { return t.event; });
      v.setEventFilter(all);
      me._refreshAlertFilterTypes();
    },

    removeAllOverlays() {
      var me = this;
      if (me._overlayLightningLayer && me.leafletMap) {
        me.leafletMap.removeLayer(me._overlayLightningLayer);
        me._overlayLightningLayer = null;
      }
      if (me._overlayHailLayer && me.leafletMap) {
        me.leafletMap.removeLayer(me._overlayHailLayer);
        me._overlayHailLayer = null;
      }
      if (me._overlayAlertsLayer && me.leafletMap) {
        me.leafletMap.removeLayer(me._overlayAlertsLayer);
        me._overlayAlertsLayer = null;
      }
      me._stopAlertFilterPoll();
      me.overlayLightning = false;
      me.overlayHail = false;
      me.overlayAlerts = false;
    },

    updateMapView() {
      var me = this;
      if (!me.leafletMap || !me.wxdata) return;
      var lat = me.wxdata.latitude;
      var lon = me.wxdata.longitude;
      if (lat == null || lon == null) return;
      var zoom = me.hasTodayPrecip ? 8 : 5;
      me.leafletMap.setView([lat, lon], zoom);

      if (me.circleMarker) me.circleMarker.remove();
      me.circleMarker = L.marker([lat, lon], {
        icon: L.divIcon({
          className: 'wl-location-dot',
          iconSize: [14, 14],
          iconAnchor: [7, 7],
        }),
        interactive: false,
      }).addTo(me.leafletMap);
    },

    _showMapLocationMenu(latlng) {
      var me = this;
      var loc = (Math.round(latlng.lat * 10000) / 10000) + ',' + (Math.round(latlng.lng * 10000) / 10000);
      // Reverse geocode the held coordinates for a display name, then jump directly
      VcGeocoder.reverseGeocode(latlng.lat, latlng.lng).then(function (result) {
        var displayName = (result && result.displayName) ? result.displayName : loc;
        me.selectLocation({ location: loc, name: displayName });
      });
    },

    // ---- Map time controls ----
    initializeMapDateTime() {
      var me = this;
      var initialTime;
      if (!me.wxdata) { initialTime = new Date(); }
      else if (me.today) { initialTime = new Date(); }
      else {
        var day1 = me.days && me.days.length ? me.days[0] : null;
        initialTime = day1 ? new Date(day1.datetimeEpoch * 1000 + 12 * 3600000) : new Date();
      }
      initialTime = me.roundToInterval(initialTime, me.activeLayerInterval);
      me.setMapDateTime(initialTime);
    },

    setMapDateTime(datetime) {
      var me = this;
      var rounded = me.roundToInterval(datetime, me.activeLayerInterval);
      me.mapDateTime = rounded;

      if (me.leafletMap) {
        me.leafletMap.options.dateTime = rounded;
        me.leafletMap.options.dateTimeInterval = me.activeLayerInterval;
        me.leafletMap.fire('datetimeupdated', { dateTime: rounded });
      }
      if (me.updateMapTimeLine) me.updateMapTimeLine(rounded);
      if (me._rainNowcastTimeMarker) me._rainNowcastTimeMarker.update(rounded);
      if (me._rainHourlyTimeMarker) me._rainHourlyTimeMarker.update(rounded);
      // Drive alert overlay time
      if (me._overlayAlertsLayer && me._overlayAlertsLayer.setAlertTime) {
        me._overlayAlertsLayer.setAlertTime(rounded);
      }
    },

    roundToInterval(datetime, intervalMinutes) {
      var ms = datetime.getTime();
      var intervalMs = intervalMinutes * 60 * 1000;
      return new Date(Math.floor(ms / intervalMs) * intervalMs);
    },

    /* Skip buttons: move by step, no looping — just clamp at boundaries */
    stepTime(minutes) {
      var me = this;
      if (!me.mapDateTime) me.initializeMapDateTime();
      var step = minutes || me.computedStepInterval;
      var absStep = Math.abs(step);

      var config = me.mapLayerOptions.find(function (l) { return l.id === me.activeMapLayer; });
      var isPrecipComp = config && config.element === 'precipcomposite';
      var baseTime = me.mapDateTime.getTime();

      // For hourly steps on precipcomposite, round to the nearest hour first
      if (isPrecipComp && absStep >= 60) {
        var d = new Date(baseTime);
        d.setMinutes(0, 0, 0);
        if (me.mapDateTime.getMinutes() >= 30) d.setTime(d.getTime() + 60 * 60 * 1000);
        baseTime = d.getTime();
      }

      // For 3-hour steps, snap to UTC 3-hour boundaries (00Z, 03Z, 06Z, ...)
      if (absStep >= 180) {
        var utc = new Date(baseTime);
        var utcH = utc.getUTCHours();
        if (step > 0) {
          // Next 3h boundary
          var next3h = Math.ceil((utcH + 1) / 3) * 3;
          utc.setUTCHours(next3h, 0, 0, 0);
        } else {
          // Previous 3h boundary
          var prev3h = Math.floor((utcH - 1) / 3) * 3;
          if (prev3h < 0) {
            utc.setUTCDate(utc.getUTCDate() - 1);
            prev3h = 21;
          }
          utc.setUTCHours(prev3h, 0, 0, 0);
        }
        me.setMapDateTime(utc);
        return;
      }

      var newTime = new Date(baseTime + step * 60 * 1000);
      // No looping — just set the new time
      me.setMapDateTime(newTime);
    },

    /* Animation step: loops precipcomposite within -2h to +1h from now */
    animateStep() {
      var me = this;
      if (!me.mapDateTime) me.initializeMapDateTime();

      var config = me.mapLayerOptions.find(function (l) { return l.id === me.activeMapLayer; });
      var isPrecipComp = config && config.element === 'precipcomposite';
      var step = isPrecipComp ? 10 : (config ? config.interval : 60);
      var newTime = new Date(me.mapDateTime.getTime() + step * 60 * 1000);

      if (isPrecipComp) {
        var now = Date.now();
        var minT = now - 2 * 60 * 60 * 1000;
        var maxT = now + 1 * 60 * 60 * 1000;
        if (newTime.getTime() > maxT) newTime.setTime(minT);
        if (newTime.getTime() < minT) newTime.setTime(maxT);
      } else if (me.minutely.length) {
        var minTm = me.minutely[0].datetimeEpoch * 1000;
        var maxTm = me.minutely[me.minutely.length - 1].datetimeEpoch * 1000;
        if (newTime.getTime() > maxTm) newTime.setTime(minTm);
        if (newTime.getTime() < minTm) newTime.setTime(maxTm);
      }
      me.setMapDateTime(newTime);
    },

    playPause() {
      var me = this;
      me.isPlaying = !me.isPlaying;
      if (me.isPlaying) {
        me.playInterval = setInterval(function () { me.animateStep(); }, me.playSpeed);
      } else {
        me.stopPlaying();
      }
    },

    stopPlaying() {
      this.isPlaying = false;
      if (this.playInterval) { clearInterval(this.playInterval); this.playInterval = null; }
    },

    resetToNow() {
      this.stopPlaying();
      this.initializeMapDateTime();
    },

    setPrecipSource(source) {
      var me = this;
      me.precipSource = source;
      // Update the existing tile layer model and force a full redraw
      if (me.activeMapLayer === 'precip' && me.wxTileLayer && me.wxTileLayer.setOptions) {
        me.wxTileLayer.setOptions({ model: source, opacity: me.wxTileLayer.options.opacity }, false);
      }
    },

    setPlaySpeed(speed) {
      this.playSpeed = speed;
      VcLocationsStorage.setValue('wl_playSpeed', speed);
      // If currently playing, restart interval with new speed
      if (this.isPlaying && this.playInterval) {
        var me = this;
        clearInterval(me.playInterval);
        me.playInterval = setInterval(function () { me.animateStep(); }, speed);
      }
    },

    setMapDisplayMode(mode) {
      var me = this;
      me.mapDisplayMode = mode;
      VcLocationsStorage.setValue('wl_mapDisplayMode', mode);
      // If switching away from map tab and it's selected, go to now
      if (mode === 'full' && me.activeTab === 'map') {
        me.activeTab = 'now';
      }
      // Invalidate map size after layout change
      me.$nextTick(function () {
        if (me.leafletMap) me.leafletMap.invalidateSize();
      });
    },

    _moveMapToTab() {
      var me = this;
      var mapEl = me.$refs.mapEl;
      var tabWrap = me.$refs.mapTabWrap;
      if (!mapEl || !tabWrap) return;
      // Insert the map canvas as the first child of the tab wrapper
      tabWrap.insertBefore(mapEl, tabWrap.firstChild);
      // Re-observe new parent for resize
      if (me._mapResizeObserver) {
        me._mapResizeObserver.disconnect();
        me._mapResizeObserver.observe(mapEl);
        me._mapResizeObserver.observe(tabWrap);
      }
      me.$nextTick(function () {
        if (!me.leafletMap) {
          me.initMap();
        } else {
          // Re-enable full interactions on the Map tab
          me.leafletMap.dragging.enable();
          me.leafletMap.scrollWheelZoom.enable();
          me.leafletMap.doubleClickZoom.enable();
          me.leafletMap.touchZoom.enable();
          if (!me._mapZoomControl) {
            me._mapZoomControl = L.control.zoom({ position: 'topright' });
            me._mapZoomControl.addTo(me.leafletMap);
          }
          me.leafletMap.invalidateSize();
          // Delayed invalidation after layout settles
          requestAnimationFrame(function () {
            if (me.leafletMap) me.leafletMap.invalidateSize();
            setTimeout(function () { if (me.leafletMap) me.leafletMap.invalidateSize(); }, 50);
          });
        }
      });
    },

    onHomeMapClick() {
      // When map is not full size, clicking it switches to the Map tab
      if (this.mapDisplayMode !== 'full') {
        this.activeTab = 'map';
      }
    },

    _moveMapToHome() {
      var me = this;
      var mapEl = me.$refs.mapEl;
      var homeWrap = me.$refs.homeMapWrap;
      if (!mapEl || !homeWrap) return;
      // Only move if it's not already the first child
      if (mapEl.parentNode !== homeWrap) {
        homeWrap.insertBefore(mapEl, homeWrap.firstChild);
        // Re-observe new parent for resize
        if (me._mapResizeObserver) {
          me._mapResizeObserver.disconnect();
          me._mapResizeObserver.observe(mapEl);
          me._mapResizeObserver.observe(homeWrap);
        }
        me.$nextTick(function () {
          if (me.leafletMap) {
            // Disable interactions when back on home (small/preview)
            if (me.mapDisplayMode !== 'full') {
              me.leafletMap.dragging.disable();
              me.leafletMap.scrollWheelZoom.disable();
              me.leafletMap.doubleClickZoom.disable();
              me.leafletMap.touchZoom.disable();
              if (me._mapZoomControl) {
                me._mapZoomControl.remove();
                me._mapZoomControl = null;
              }
            }
            me.leafletMap.invalidateSize();
          }
        });
      }
    },

    openMapDateTimePicker() {
      var me = this;
      if (me.isPlaying) return;
      // Format current mapDateTime for the datetime-local input
      var dt = me.mapDateTime || new Date();
      var year = dt.getFullYear();
      var month = String(dt.getMonth() + 1).padStart(2, '0');
      var day = String(dt.getDate()).padStart(2, '0');
      var hours = String(dt.getHours()).padStart(2, '0');
      var mins = String(dt.getMinutes()).padStart(2, '0');
      me.mapDateTimePickerValue = year + '-' + month + '-' + day + 'T' + hours + ':' + mins;
      me.showMapDateTimePicker = true;
    },

    applyMapDateTimePicker() {
      var me = this;
      if (!me.mapDateTimePickerValue) return;
      var parsed = new Date(me.mapDateTimePickerValue);
      if (isNaN(parsed.getTime())) return;
      me.showMapDateTimePicker = false;
      me.setMapDateTime(parsed);
    },

    toEpochDateInstance(epochTime) {
      var me = this;
      var currentTzoffset = new Date().getTimezoneOffset() * 60000;
      var tzoffset = me.wxdata ? me.wxdata.tzoffset : 0;
      return new Date(epochTime * 1000 - tzoffset * 3600000 - currentTzoffset);
    },

    toLocalDateInstance(epochTime) {
      var me = this;
      var currentTzoffset = new Date().getTimezoneOffset() * 60000;
      var tzoffset = me.wxdata ? me.wxdata.tzoffset : 0;
      return new Date(epochTime * 1000 + tzoffset * 3600000 + currentTzoffset);
    },

    /** Return a url() reference for an SVG gradient that works on iOS WKWebView / Capacitor.
     *  Bare url(#id) breaks when the document base URL has a path component. */
    svgGradUrl(id) {
      var base = window.location.href.split('#')[0];
      return 'url(' + base + '#' + id + ')';
    },

    // ---- Nowcast D3 chart ----
    // ---- Charts Tab ----
    renderAllCharts() {
      this.renderTempChart();
      this.renderPrecipChart();
      this.renderWindChart();
      this.renderAtmosChart();
      this.renderUVChart();
      this.renderDaylightChart();
      this.renderPressureChart();
      this.renderHourlyPrecipDetailChart();
      this.renderCumulativeRainChart();
    },

    _chartDayName(d) {
      if (!(d instanceof Date)) return '';
      try {
        var tz = this.wxdata && this.wxdata.timezone;
        var opts = { weekday: 'short' };
        if (tz) opts.timeZone = tz;
        return new Intl.DateTimeFormat('en-US', opts).format(d);
      } catch (e) { return d3.timeFormat('%a')(d); }
    },

    chartSetup(svgRef, height) {
      var svg = this.$refs[svgRef];
      if (!svg) return null;
      var container = d3.select(svg);
      container.selectAll('*').remove();
      var rect = svg.parentElement.getBoundingClientRect();
      if (rect.width < 10) return null;
      var margin = { top: 30, right: 16, bottom: 34, left: 44 };
      var w = Math.max(rect.width - margin.left - margin.right, 100);
      var h = Math.max((height || 180) - margin.top - margin.bottom, 60);
      container.attr('viewBox', '0 0 ' + rect.width + ' ' + (height || 180)).attr('preserveAspectRatio', 'xMidYMid meet');
      var g = container.append('g').attr('transform', 'translate(' + margin.left + ',' + margin.top + ')');
      return { g: g, w: w, h: h, margin: margin, fullW: rect.width, fullH: height || 180 };
    },

    chartAxis(g, x, y, w, h, yLabel) {
      var me = this;
      // X axis
      g.append('g').attr('class', 'wl-chart-axis').attr('transform', 'translate(0,' + h + ')')
        .call(d3.axisBottom(x).ticks(Math.min(me.days.length, 7)).tickFormat(function (d, i) {
          if (d instanceof Date) return me._chartDayName(d);
          return d;
        }).tickSize(-3))
        .selectAll('text').attr('fill', 'var(--wl-text-muted)').style('font-size', '14px');
      // Y axis
      g.append('g').attr('class', 'wl-chart-axis')
        .call(d3.axisLeft(y).ticks(5).tickSize(-w))
        .selectAll('text').attr('fill', 'var(--wl-text-muted)').style('font-size', '14px');
      // Grid lines
      g.selectAll('.wl-chart-axis .tick line').attr('stroke', 'var(--wl-border-light)').attr('stroke-dasharray', '2,2');
      g.selectAll('.wl-chart-axis .domain').attr('stroke', 'var(--wl-border)');
      // Y label
      if (yLabel) {
        g.append('text').attr('x', -8).attr('y', -12).attr('fill', 'var(--wl-text-light)')
          .style('font-size', '12px').text(yLabel);
      }
    },

    renderTempChart() {
      var me = this;
      if (!me.days || me.days.length < 2) return;
      var setup = me.chartSetup('chartTemp', 220);
      if (!setup) return;
      var g = setup.g, w = setup.w, h = setup.h;

      var data = me.days.map(function (d) {
        return { date: new Date(d.datetimeEpoch * 1000), hi: d.tempmax, lo: d.tempmin, fl: d.feelslikemax };
      });

      var x = d3.scaleTime().domain(d3.extent(data, function (d) { return d.date; })).range([0, w]);
      var allTemps = data.reduce(function (a, d) { a.push(d.hi, d.lo); return a; }, []);
      var y = d3.scaleLinear().domain([d3.min(allTemps) - 3, d3.max(allTemps) + 3]).range([h, 0]);

      me.chartAxis(g, x, y, w, h, me.unitGroup === 'us' ? '°F' : '°C');

      // Temperature range area
      var area = d3.area()
        .x(function (d) { return x(d.date); })
        .y0(function (d) { return y(d.lo); })
        .y1(function (d) { return y(d.hi); })
        .curve(d3.curveCatmullRom);

      var grad = g.append('defs').append('linearGradient').attr('id', 'wl-temp-grad')
        .attr('x1', '0').attr('y1', '0').attr('x2', '0').attr('y2', '1');
      grad.append('stop').attr('offset', '0%').attr('stop-color', '#ef4444').attr('stop-opacity', 0.35);
      grad.append('stop').attr('offset', '100%').attr('stop-color', '#3b82f6').attr('stop-opacity', 0.2);

      g.append('path').datum(data).attr('d', area).attr('fill', me.svgGradUrl('wl-temp-grad'));

      // High line
      var hiLine = d3.line().x(function (d) { return x(d.date); }).y(function (d) { return y(d.hi); }).curve(d3.curveCatmullRom);
      g.append('path').datum(data).attr('d', hiLine).attr('fill', 'none').attr('stroke', '#ef4444').attr('stroke-width', 2);

      // Low line
      var loLine = d3.line().x(function (d) { return x(d.date); }).y(function (d) { return y(d.lo); }).curve(d3.curveCatmullRom);
      g.append('path').datum(data).attr('d', loLine).attr('fill', 'none').attr('stroke', '#3b82f6').attr('stroke-width', 2);

      // Dots + labels for hi
      g.selectAll('.wl-dot-hi').data(data).enter().append('circle')
        .attr('cx', function (d) { return x(d.date); }).attr('cy', function (d) { return y(d.hi); })
        .attr('r', 3).attr('fill', '#ef4444');
      g.selectAll('.wl-lbl-hi').data(data).enter().append('text')
        .attr('x', function (d) { return x(d.date); }).attr('y', function (d) { return y(d.hi) - 8; })
        .attr('text-anchor', 'middle').attr('fill', '#ef4444').style('font-size', '12px').style('font-weight', '600')
        .text(function (d, i) {
          if (y(d.hi) - 8 < 0) return '';
          var v = Math.round(d.hi);
          if (i === 0 || i === data.length - 1) return v + '°';
          var prev = Math.round(data[i - 1].hi), next = Math.round(data[i + 1].hi);
          if (v >= prev && v >= next) return v + '°';
          if (v <= prev && v <= next) return v + '°';
          return '';
        });

      // Dots + labels for lo
      g.selectAll('.wl-dot-lo').data(data).enter().append('circle')
        .attr('cx', function (d) { return x(d.date); }).attr('cy', function (d) { return y(d.lo); })
        .attr('r', 3).attr('fill', '#3b82f6');
      g.selectAll('.wl-lbl-lo').data(data).enter().append('text')
        .attr('x', function (d) { return x(d.date); }).attr('y', function (d) { return y(d.lo) + 14; })
        .attr('text-anchor', 'middle').attr('fill', '#3b82f6').style('font-size', '12px').style('font-weight', '600')
        .text(function (d, i) {
          if (y(d.lo) + 14 > h) return '';
          var v = Math.round(d.lo);
          if (i === 0 || i === data.length - 1) return v + '°';
          var prev = Math.round(data[i - 1].lo), next = Math.round(data[i + 1].lo);
          if (v >= prev && v >= next) return v + '°';
          if (v <= prev && v <= next) return v + '°';
          return '';
        });

      // Legend
      var leg = g.append('g').attr('transform', 'translate(' + (w - 108) + ',-16)');
      leg.append('line').attr('x1', 0).attr('y1', 4).attr('x2', 14).attr('y2', 4).attr('stroke', '#ef4444').attr('stroke-width', 2);
      leg.append('text').attr('x', 18).attr('y', 7).text('High').attr('fill', 'var(--wl-text-muted)').style('font-size', '12px');
      leg.append('line').attr('x1', 56).attr('y1', 4).attr('x2', 70).attr('y2', 4).attr('stroke', '#3b82f6').attr('stroke-width', 2);
      leg.append('text').attr('x', 74).attr('y', 7).text('Low').attr('fill', 'var(--wl-text-muted)').style('font-size', '12px');
    },

    renderPrecipChart() {
      var me = this;
      if (!me.days || me.days.length < 2) return;
      var setup = me.chartSetup('chartPrecip', 200);
      if (!setup) return;
      var g = setup.g, w = setup.w, h = setup.h;

      var data = me.days.map(function (d) {
        return { date: new Date(d.datetimeEpoch * 1000), prob: d.precipprob || 0, amount: d.precip || 0, snow: d.snow || 0 };
      });

      var x = d3.scaleBand().domain(data.map(function (d) { return d.date; })).range([0, w]).padding(0.25);
      var yAmt = d3.scaleLinear().domain([0, Math.max(d3.max(data, function (d) { return d.amount + d.snow; }), 0.2)]).range([h, 0]).nice();
      var yProb = d3.scaleLinear().domain([0, 100]).range([h, 0]);

      me.chartAxis(g, x, yAmt, w, h, me.unitGroup === 'us' ? 'in' : 'mm');

      // Override x tick format to day names, thin if many
      g.select('.wl-chart-axis').selectAll('text').text(function (d, i) {
        if (d instanceof Date) {
          if (data.length > 7 && i % 2 !== 0) return '';
          return me._chartDayName(d);
        }
        return '';
      });

      // Debug: log precip values from day records vs hourly sums
      console.log('=== PRECIP CHART DEBUG (v2=' + me.useV2Forecast + ') ===');
      me.days.forEach(function (d) {
        var hSum = (d.hours || []).reduce(function (s, h) { return s + (h.precip || 0); }, 0);
        console.log(d.datetime, '| day.precip=' + d.precip, '| hourlySum=' + hSum.toFixed(2), '| prob=' + d.precipprob, '| precipcover=' + d.precipcover, '| snow=' + d.snow, '| keys=' + Object.keys(d).filter(function (k) { return k.indexOf('precip') >= 0; }).join(','));
      });

      // Rain bars
      g.selectAll('.wl-bar-rain').data(data).enter().append('rect')
        .attr('x', function (d) { return x(d.date); })
        .attr('y', function (d) { return yAmt(d.amount); })
        .attr('width', x.bandwidth())
        .attr('height', function (d) { return h - yAmt(d.amount); })
        .attr('fill', 'var(--wl-accent)').attr('rx', 2).attr('opacity', 0.7);

      // Precip amount labels on bars
      g.selectAll('.wl-lbl-amt').data(data).enter().append('text')
        .attr('x', function (d) { return x(d.date) + x.bandwidth() / 2; })
        .attr('y', function (d) { return yAmt(d.amount) - 3; })
        .attr('text-anchor', 'middle').attr('fill', 'var(--wl-accent)').style('font-size', '12px').style('font-weight', '600')
        .text(function (d) { return (d.amount > 0 && yAmt(d.amount) - 3 >= 0) ? d.amount.toFixed(1) : ''; });

      // Snow bars stacked
      g.selectAll('.wl-bar-snow').data(data).enter().append('rect')
        .attr('x', function (d) { return x(d.date); })
        .attr('y', function (d) { return yAmt(d.amount + d.snow); })
        .attr('width', x.bandwidth())
        .attr('height', function (d) { return yAmt(d.amount) - yAmt(d.amount + d.snow); })
        .attr('fill', '#a78bfa').attr('rx', 2).attr('opacity', 0.7);

      // Probability line on secondary axis
      var probLine = d3.line().x(function (d) { return x(d.date) + x.bandwidth() / 2; })
        .y(function (d) { return yProb(d.prob); }).curve(d3.curveCatmullRom);
      g.append('path').datum(data).attr('d', probLine).attr('fill', 'none')
        .attr('stroke', '#f59e0b').attr('stroke-width', 2).attr('stroke-dasharray', '4,2');

      // Prob dots
      g.selectAll('.wl-dot-prob').data(data).enter().append('circle')
        .attr('cx', function (d) { return x(d.date) + x.bandwidth() / 2; })
        .attr('cy', function (d) { return yProb(d.prob); })
        .attr('r', 3).attr('fill', '#f59e0b');

      // Prob % labels (maxima only)
      g.selectAll('.wl-lbl-prob').data(data).enter().append('text')
        .attr('x', function (d) { return x(d.date) + x.bandwidth() / 2; })
        .attr('y', function (d) { return yProb(d.prob) - 7; })
        .attr('text-anchor', 'middle').attr('fill', '#f59e0b').style('font-size', '12px').style('font-weight', '600')
        .text(function (d, i) {
          if (d.prob <= 0 || yProb(d.prob) - 7 < 0) return '';
          var v = Math.round(d.prob);
          if (i === 0 || i === data.length - 1) return v + '%';
          var prev = Math.round(data[i - 1].prob), next = Math.round(data[i + 1].prob);
          if (v >= prev && v >= next) return v + '%';
          if (v <= prev && v <= next && v > 0) return v + '%';
          return '';
        });

      // Legend
      var leg = g.append('g').attr('transform', 'translate(' + (w - 170) + ',-16)');
      leg.append('rect').attr('width', 10).attr('height', 8).attr('fill', 'var(--wl-accent)').attr('opacity', 0.7).attr('rx', 1);
      leg.append('text').attr('x', 14).attr('y', 7).text('Rain').attr('fill', 'var(--wl-text-muted)').style('font-size', '12px');
      leg.append('rect').attr('x', 50).attr('width', 10).attr('height', 8).attr('fill', '#a78bfa').attr('opacity', 0.7).attr('rx', 1);
      leg.append('text').attr('x', 64).attr('y', 7).text('Snow').attr('fill', 'var(--wl-text-muted)').style('font-size', '12px');
      leg.append('line').attr('x1', 104).attr('y1', 4).attr('x2', 118).attr('y2', 4).attr('stroke', '#f59e0b').attr('stroke-width', 2).attr('stroke-dasharray', '4,2');
      leg.append('text').attr('x', 122).attr('y', 7).text('Prob').attr('fill', 'var(--wl-text-muted)').style('font-size', '12px');
    },

    renderWindChart() {
      var me = this;
      if (!me.days || me.days.length < 2) return;
      var setup = me.chartSetup('chartWind', 200);
      if (!setup) return;
      var g = setup.g, w = setup.w, h = setup.h;

      var data = me.days.map(function (d) {
        return { date: new Date(d.datetimeEpoch * 1000), speed: d.windspeed || 0, gust: d.windgust || 0, dir: d.winddir || 0 };
      });

      var x = d3.scaleTime().domain(d3.extent(data, function (d) { return d.date; })).range([0, w]);
      var maxW = Math.max(d3.max(data, function (d) { return d.gust; }), 10);
      var y = d3.scaleLinear().domain([0, maxW + 5]).range([h, 0]).nice();

      var windUnit = me.unitGroup === 'us' ? 'mph' : (me.unitGroup === 'metric' ? 'km/h' : (me.unitGroup === 'uk' ? 'mph' : 'm/s'));
      me.chartAxis(g, x, y, w, h, windUnit);

      // Gust area
      var gustArea = d3.area().x(function (d) { return x(d.date); })
        .y0(function (d) { return y(d.speed); }).y1(function (d) { return y(d.gust); })
        .curve(d3.curveCatmullRom);
      g.append('path').datum(data).attr('d', gustArea).attr('fill', '#f59e0b').attr('opacity', 0.15);

      // Gust line
      var gustLine = d3.line().x(function (d) { return x(d.date); }).y(function (d) { return y(d.gust); }).curve(d3.curveCatmullRom);
      g.append('path').datum(data).attr('d', gustLine).attr('fill', 'none').attr('stroke', '#f59e0b').attr('stroke-width', 1.5).attr('stroke-dasharray', '4,2');

      // Speed area to zero
      var speedArea = d3.area().x(function (d) { return x(d.date); })
        .y0(h).y1(function (d) { return y(d.speed); }).curve(d3.curveCatmullRom);
      g.append('path').datum(data).attr('d', speedArea).attr('fill', '#6366f1').attr('opacity', 0.15);

      // Speed line
      var speedLine = d3.line().x(function (d) { return x(d.date); }).y(function (d) { return y(d.speed); }).curve(d3.curveCatmullRom);
      g.append('path').datum(data).attr('d', speedLine).attr('fill', 'none').attr('stroke', '#6366f1').attr('stroke-width', 2);

      // Speed dots
      g.selectAll('.wl-dot-spd').data(data).enter().append('circle')
        .attr('cx', function (d) { return x(d.date); }).attr('cy', function (d) { return y(d.speed); })
        .attr('r', 3).attr('fill', '#6366f1');

      // Speed labels at maxima/minima
      g.selectAll('.wl-lbl-spd').data(data).enter().append('text')
        .attr('x', function (d) { return x(d.date); }).attr('y', function (d) { return y(d.speed) - 12; })
        .attr('text-anchor', 'middle').attr('fill', '#6366f1').style('font-size', '12px').style('font-weight', '600')
        .text(function (d, i) {
          if (y(d.speed) - 12 < 0) return '';
          var v = Math.round(d.speed);
          if (i === 0 || i === data.length - 1) return v;
          var prev = Math.round(data[i - 1].speed), next = Math.round(data[i + 1].speed);
          if (v >= prev && v >= next) return v;
          if (v <= prev && v <= next) return v;
          return '';
        });

      // Wind direction arrows
      g.selectAll('.wl-wind-dir').data(data).enter().append('text')
        .attr('x', function (d) { return x(d.date); }).attr('y', function (d) { return y(d.speed) - 10; })
        .attr('text-anchor', 'middle').attr('fill', '#6366f1').style('font-size', '14px')
        .attr('transform', function (d) { return 'rotate(' + d.dir + ',' + x(d.date) + ',' + (y(d.speed) - 10) + ')'; })
        .text('↓');

      // Legend
      var leg = g.append('g').attr('transform', 'translate(' + (w - 130) + ',-16)');
      leg.append('line').attr('x1', 0).attr('y1', 4).attr('x2', 14).attr('y2', 4).attr('stroke', '#6366f1').attr('stroke-width', 2);
      leg.append('text').attr('x', 18).attr('y', 7).text('Speed').attr('fill', 'var(--wl-text-muted)').style('font-size', '12px');
      leg.append('line').attr('x1', 64).attr('y1', 4).attr('x2', 78).attr('y2', 4).attr('stroke', '#f59e0b').attr('stroke-width', 1.5).attr('stroke-dasharray', '4,2');
      leg.append('text').attr('x', 82).attr('y', 7).text('Gust').attr('fill', 'var(--wl-text-muted)').style('font-size', '12px');
    },

    renderAtmosChart() {
      var me = this;
      if (!me.days || me.days.length < 2) return;
      var setup = me.chartSetup('chartAtmos', 200);
      if (!setup) return;
      var g = setup.g, w = setup.w, h = setup.h;

      var data = me.days.map(function (d) {
        return { date: new Date(d.datetimeEpoch * 1000), humidity: d.humidity || 0, cloud: d.cloudcover || 0, dew: d.dew || 0 };
      });

      var x = d3.scaleTime().domain(d3.extent(data, function (d) { return d.date; })).range([0, w]);
      var y = d3.scaleLinear().domain([0, 100]).range([h, 0]);

      me.chartAxis(g, x, y, w, h, '%');

      // Cloud cover area
      var cloudArea = d3.area().x(function (d) { return x(d.date); })
        .y0(h).y1(function (d) { return y(d.cloud); }).curve(d3.curveCatmullRom);
      g.append('path').datum(data).attr('d', cloudArea).attr('fill', '#94a3b8').attr('opacity', 0.15);
      var cloudLine = d3.line().x(function (d) { return x(d.date); }).y(function (d) { return y(d.cloud); }).curve(d3.curveCatmullRom);
      g.append('path').datum(data).attr('d', cloudLine).attr('fill', 'none').attr('stroke', '#94a3b8').attr('stroke-width', 1.5).attr('stroke-dasharray', '3,2');

      // Humidity area
      var humArea = d3.area().x(function (d) { return x(d.date); })
        .y0(h).y1(function (d) { return y(d.humidity); }).curve(d3.curveCatmullRom);
      g.append('path').datum(data).attr('d', humArea).attr('fill', '#06b6d4').attr('opacity', 0.15);
      var humLine = d3.line().x(function (d) { return x(d.date); }).y(function (d) { return y(d.humidity); }).curve(d3.curveCatmullRom);
      g.append('path').datum(data).attr('d', humLine).attr('fill', 'none').attr('stroke', '#06b6d4').attr('stroke-width', 2);

      // Humidity dots
      g.selectAll('.wl-dot-hum').data(data).enter().append('circle')
        .attr('cx', function (d) { return x(d.date); }).attr('cy', function (d) { return y(d.humidity); })
        .attr('r', 3).attr('fill', '#06b6d4');

      // Humidity labels at maxima/minima
      g.selectAll('.wl-lbl-hum').data(data).enter().append('text')
        .attr('x', function (d) { return x(d.date); }).attr('y', function (d) { return y(d.humidity) - 8; })
        .attr('text-anchor', 'middle').attr('fill', '#06b6d4').style('font-size', '12px').style('font-weight', '600')
        .text(function (d, i) {
          if (y(d.humidity) - 8 < 0) return '';
          var v = Math.round(d.humidity);
          if (i === 0 || i === data.length - 1) return v + '%';
          var prev = Math.round(data[i - 1].humidity), next = Math.round(data[i + 1].humidity);
          if (v >= prev && v >= next) return v + '%';
          if (v <= prev && v <= next) return v + '%';
          return '';
        });

      // Legend
      var leg = g.append('g').attr('transform', 'translate(' + (w - 148) + ',-16)');
      leg.append('line').attr('x1', 0).attr('y1', 4).attr('x2', 14).attr('y2', 4).attr('stroke', '#06b6d4').attr('stroke-width', 2);
      leg.append('text').attr('x', 18).attr('y', 7).text('Humidity').attr('fill', 'var(--wl-text-muted)').style('font-size', '12px');
      leg.append('line').attr('x1', 78).attr('y1', 4).attr('x2', 92).attr('y2', 4).attr('stroke', '#94a3b8').attr('stroke-width', 1.5).attr('stroke-dasharray', '3,2');
      leg.append('text').attr('x', 96).attr('y', 7).text('Cloud').attr('fill', 'var(--wl-text-muted)').style('font-size', '12px');
    },

    renderUVChart() {
      var me = this;
      if (!me.days || me.days.length < 2) return;
      var setup = me.chartSetup('chartUV', 180);
      if (!setup) return;
      var g = setup.g, w = setup.w, h = setup.h;

      var data = me.days.map(function (d) {
        return { date: new Date(d.datetimeEpoch * 1000), uv: d.uvindex || 0 };
      });

      var x = d3.scaleBand().domain(data.map(function (d) { return d.date; })).range([0, w]).padding(0.3);
      var y = d3.scaleLinear().domain([0, Math.max(d3.max(data, function (d) { return d.uv; }), 6) + 1]).range([h, 0]).nice();

      me.chartAxis(g, x, y, w, h, 'UV');
      g.select('.wl-chart-axis').selectAll('text').text(function (d, i) {
        if (d instanceof Date) {
          if (data.length > 7 && i % 2 !== 0) return '';
          return me._chartDayName(d);
        }
        return '';
      });

      function uvColor(v) {
        if (v <= 2) return '#22c55e';
        if (v <= 5) return '#f59e0b';
        if (v <= 7) return '#f97316';
        if (v <= 10) return '#ef4444';
        return '#7c3aed';
      }

      // UV bars
      g.selectAll('.wl-bar-uv').data(data).enter().append('rect')
        .attr('x', function (d) { return x(d.date); })
        .attr('y', function (d) { return y(d.uv); })
        .attr('width', x.bandwidth())
        .attr('height', function (d) { return h - y(d.uv); })
        .attr('fill', function (d) { return uvColor(d.uv); })
        .attr('rx', 3).attr('opacity', 0.8);

      // Value labels
      g.selectAll('.wl-lbl-uv').data(data).enter().append('text')
        .attr('x', function (d) { return x(d.date) + x.bandwidth() / 2; })
        .attr('y', function (d) { return y(d.uv) - 5; })
        .attr('text-anchor', 'middle').style('font-size', '14px').style('font-weight', '700')
        .attr('fill', function (d) { return uvColor(d.uv); })
        .text(function (d) { return (d.uv > 0 && y(d.uv) - 5 >= 0) ? Math.round(d.uv) : ''; });

      // Threshold lines
      [3, 6, 8, 11].forEach(function (threshold) {
        if (threshold <= y.domain()[1]) {
          g.append('line').attr('x1', 0).attr('y1', y(threshold)).attr('x2', w).attr('y2', y(threshold))
            .attr('stroke', uvColor(threshold)).attr('stroke-width', 0.5).attr('stroke-dasharray', '3,3').attr('opacity', 0.5);
        }
      });
    },

    renderDaylightChart() {
      var me = this;
      if (!me.days || me.days.length < 2) return;
      var setup = me.chartSetup('chartDaylight', 200);
      if (!setup) return;
      var g = setup.g, w = setup.w, h = setup.h;

      var data = me.days.filter(function (d) { return d.sunrise && d.sunset; }).map(function (d) {
        var sr = d.sunriseEpoch || d.sunrise;
        var ss = d.sunsetEpoch || d.sunset;
        var hours = (ss - sr) / 3600;
        return { date: new Date(d.datetimeEpoch * 1000), sunrise: sr, sunset: ss, daylight: hours };
      });
      if (data.length < 2) return;

      var x = d3.scaleBand().domain(data.map(function (d) { return d.date; })).range([0, w]).padding(0.2);
      var minH = d3.min(data, function (d) { return d.daylight; });
      var maxH = d3.max(data, function (d) { return d.daylight; });
      var y = d3.scaleLinear().domain([Math.max(minH - 1, 0), Math.min(maxH + 1, 24)]).range([h, 0]).nice();

      me.chartAxis(g, x, y, w, h, 'hours');
      g.select('.wl-chart-axis').selectAll('text').text(function (d, i) {
        if (d instanceof Date) {
          if (data.length > 7 && i % 2 !== 0) return '';
          return me._chartDayName(d);
        }
        return '';
      });

      // Daylight gradient
      var grad = g.append('defs').append('linearGradient').attr('id', 'wl-daylight-grad')
        .attr('x1', '0').attr('y1', '0').attr('x2', '0').attr('y2', '1');
      grad.append('stop').attr('offset', '0%').attr('stop-color', '#fbbf24').attr('stop-opacity', 0.8);
      grad.append('stop').attr('offset', '100%').attr('stop-color', '#f59e0b').attr('stop-opacity', 0.3);

      // Bars
      g.selectAll('.wl-bar-dl').data(data).enter().append('rect')
        .attr('x', function (d) { return x(d.date); })
        .attr('y', function (d) { return y(d.daylight); })
        .attr('width', x.bandwidth())
        .attr('height', function (d) { return h - y(d.daylight); })
        .attr('fill', me.svgGradUrl('wl-daylight-grad')).attr('rx', 3);

      // Value labels – rotate when bars are narrow to avoid overlap
      var bw = x.bandwidth();
      var narrow = bw < 35;
      var skipLabel = narrow && data.length > 10 ? 2 : 1; // show every Nth label when very tight
      g.selectAll('.wl-lbl-dl').data(data).enter().append('text')
        .attr('x', function (d) { return x(d.date) + bw / 2; })
        .attr('y', function (d) { return y(d.daylight) - 5; })
        .attr('text-anchor', narrow ? 'start' : 'middle')
        .attr('transform', narrow ? function (d) {
          return 'rotate(-45,' + (x(d.date) + bw / 2) + ',' + (y(d.daylight) - 5) + ')';
        } : null)
        .attr('fill', '#f59e0b').style('font-size', '12px').style('font-weight', '600')
        .text(function (d, i) {
          if (i % skipLabel !== 0) return '';
          var hrs = Math.floor(d.daylight);
          var mins = Math.round((d.daylight - hrs) * 60);
          return hrs + 'h' + (mins > 0 ? mins + 'm' : '');
        });
    },

    renderPressureChart() {
      var me = this;
      if (!me.days || me.days.length < 2) return;
      var setup = me.chartSetup('chartPressure', 200);
      if (!setup) return;
      var g = setup.g, w = setup.w, h = setup.h;

      var data = me.days.map(function (d) {
        return { date: new Date(d.datetimeEpoch * 1000), pressure: d.pressure || 0 };
      }).filter(function (d) { return d.pressure > 0; });
      if (data.length < 2) return;

      var x = d3.scaleTime().domain(d3.extent(data, function (d) { return d.date; })).range([0, w]);
      var pMin = d3.min(data, function (d) { return d.pressure; });
      var pMax = d3.max(data, function (d) { return d.pressure; });
      var padding = Math.max((pMax - pMin) * 0.2, 2);
      var y = d3.scaleLinear().domain([pMin - padding, pMax + padding]).range([h, 0]).nice();

      me.chartAxis(g, x, y, w, h, 'mb');

      // Pressure area fill
      var area = d3.area()
        .x(function (d) { return x(d.date); })
        .y0(h)
        .y1(function (d) { return y(d.pressure); })
        .curve(d3.curveCatmullRom);
      g.append('path').datum(data).attr('d', area).attr('fill', '#8b5cf6').attr('opacity', 0.12);

      // Pressure line
      var line = d3.line()
        .x(function (d) { return x(d.date); })
        .y(function (d) { return y(d.pressure); })
        .curve(d3.curveCatmullRom);
      g.append('path').datum(data).attr('d', line)
        .attr('fill', 'none').attr('stroke', '#8b5cf6').attr('stroke-width', 2);

      // Dots
      g.selectAll('.wl-dot-pres').data(data).enter().append('circle')
        .attr('cx', function (d) { return x(d.date); })
        .attr('cy', function (d) { return y(d.pressure); })
        .attr('r', 3).attr('fill', '#8b5cf6');

      // Standard pressure reference line (1013.25 mb)
      var stdPressure = 1013.25;
      if (stdPressure >= y.domain()[0] && stdPressure <= y.domain()[1]) {
        g.append('line')
          .attr('x1', 0).attr('y1', y(stdPressure))
          .attr('x2', w).attr('y2', y(stdPressure))
          .attr('stroke', '#94a3b8').attr('stroke-width', 0.8)
          .attr('stroke-dasharray', '4,3').attr('opacity', 0.6);
        g.append('text')
          .attr('x', w - 2).attr('y', y(stdPressure) - 3)
          .attr('text-anchor', 'end')
          .attr('fill', '#94a3b8')
          .style('font-size', '12px')
          .text('Std 1013');
      }
    },

    // ---- Pull-to-refresh ----
    _initPullToRefresh() {
      var me = this;
      var mainEl = me.$refs.mainContent;
      if (!mainEl) return;

      var startY = 0;
      var active = false;
      var indicator = me.$refs.pullIndicator;

      function setHeight(h) {
        me.pullDistance = h;
        if (indicator) indicator.style.height = h + 'px';
      }

      mainEl.addEventListener('touchstart', function (e) {
        if (me.pullRefreshing) return;
        if (mainEl.scrollTop > 1) return;
        // Don't activate on map or chart touches
        var target = e.target;
        while (target && target !== mainEl) {
          if (target.classList && (target.classList.contains('wl-map-wrap') || target.classList.contains('wl-map') || target.tagName === 'svg' || target.tagName === 'CANVAS')) return;
          target = target.parentElement;
        }
        startY = e.touches[0].clientY;
        active = true;
        me._pullReleasing = false;
      }, { passive: true });

      mainEl.addEventListener('touchmove', function (e) {
        if (!active || me.pullRefreshing) return;
        if (mainEl.scrollTop > 1) { active = false; setHeight(0); return; }
        var dy = e.touches[0].clientY - startY;
        if (dy <= 0) { setHeight(0); return; }
        // Dampen: first 60px maps 1:1, then heavy resistance
        var h = dy <= 60 ? dy : 60 + (dy - 60) * 0.3;
        setHeight(Math.min(h, 100));
      }, { passive: true });

      mainEl.addEventListener('touchend', function () {
        if (!active) return;
        active = false;
        if (me.pullDistance >= 60 && !me.pullRefreshing) {
          me.pullRefreshing = true;
          me._pullReleasing = true;
          setHeight(56);
          me.fetchWeatherData(true);
        } else {
          me._pullReleasing = true;
          setHeight(0);
          setTimeout(function () { me._pullReleasing = false; }, 250);
        }
      });
    },

    _closePullIndicator() {
      var me = this;
      var indicator = me.$refs.pullIndicator;
      me._pullReleasing = true;
      me.pullRefreshing = false;
      me.pullDistance = 0;
      if (indicator) indicator.style.height = '0px';
      setTimeout(function () { me._pullReleasing = false; }, 300);
    },

    // ---- Rain & Precip methods ----
    showRainTab() {
      var me = this;
      me.activeTab = 'rain';
      if (me.activeMapLayer !== 'precip') me.switchMapLayer('precip');
      me.$nextTick(function () {
        me.renderRainNowcastChart();
        me.renderRainHourlyChart();
      });
    },

    fmtRainAmount(val) {
      return _formatElementValue(val, 'precip', this.unitGroup, null, '0');
    },

    /** Render a small inline rain-rate chart on the Now tab (reuses full nowcast renderer) */
    renderMiniRainChart() {
      this.renderRainNowcastChart({
        svgRef: 'rainBriefSvg',
        scrollRef: 'rainBriefScroll',
        yPinRef: 'rainBriefYPin',
        svgHeight: 180,
        gradientId: 'wl-mini-rain-grad',
        bgVar: 'var(--wl-bg-secondary)',
      });
    },

    renderRainNowcastChart(opts) {
      var me = this;
      opts = opts || {};
      var svgRef = opts.svgRef || 'rainNowcastSvg';
      var scrollRef = opts.scrollRef || 'rainNowcastScroll';
      var yPinRef = opts.yPinRef || 'rainNowcastYPin';
      var gradientId = opts.gradientId || 'wl-rain-rate-grad';
      var bgVar = opts.bgVar || 'var(--wl-bg-card)';
      var svg = me.$refs[svgRef];
      if (!svg || !me.minutely.length) return;
      if (svg.parentElement.getBoundingClientRect().width < 10) return;

      var isUS = me.unitGroup === 'us';
      var rateUnit = isUS ? 'in/hr' : 'mm/hr';
      var unitDiv = isUS ? 25.4 : 1; // convert mm/hr → in/hr if needed

      var current = new Date();
      var currentLocal = me.toLocalDateInstance(current.getTime() / 1000);
      var nowEpoch = current.getTime() / 1000;
      var futureEpoch = nowEpoch + 6 * 3600;

      // Build merged dataset: minutely data + hourly forecast beyond minutely range
      var data = me.minutely.slice();
      data.forEach(function (d) {
        d.datetimeInstance = me.toLocalDateInstance(d.datetimeEpoch, d.tzoffset);
        d.rainRate = _minutelyRainRate(d) / unitDiv;
        d.source = 'minutely';
      });

      // Find where minutely data ends
      var lastMinuteEpoch = data.length ? data[data.length - 1].datetimeEpoch : nowEpoch;

      // Append hourly data for the gap between minutely end and now+6h
      if (me.days) {
        me.days.forEach(function (day) {
          if (!day.hours) return;
          day.hours.forEach(function (h) {
            if (h.datetimeEpoch <= lastMinuteEpoch || h.datetimeEpoch > futureEpoch) return;
            var refl = h.reflectivity || 0;
            // For future hours use precip amount as hourly rate (already in user units)
            var rate = refl > 0 ? _dbzToRainRateNum(refl) / unitDiv : (h.precip || 0);
            data.push({
              datetimeEpoch: h.datetimeEpoch,
              datetimeInstance: me.toLocalDateInstance(h.datetimeEpoch),
              rainRate: rate,
              reflectivity: refl,
              precipprob: h.precipprob || 0,
              precip: h.precip || 0,
              precipremote: h.precipremote || 0,
              source: 'hourly',
            });
          });
        });
      }

      var container = d3.select(svg);
      container.selectAll('*').remove();

      // Remove width attr so CSS width:100% is sole sizing authority,
      // then measure actual rendered width — guarantees coord system matches screen pixels
      var svgH = opts.svgHeight || 200;
      container.attr('width', null).attr('viewBox', null).style('width', null);
      container.style('height', svgH + 'px');
      var svgW = svg.getBoundingClientRect().width;
      var margin = { top: 32, right: 16, bottom: 28, left: 54 };
      var totalHoursNc = (futureEpoch - (data.length ? data[0].datetimeEpoch : nowEpoch)) / 3600;
      var minPxPerHourNc = 120;
      var naturalWidthNc = svgW - margin.left - margin.right;
      var width = Math.max(totalHoursNc * minPxPerHourNc, naturalWidthNc, 100);
      if (width > naturalWidthNc) {
        container.style('width', (width + margin.left + margin.right) + 'px');
      }
      var height = Math.max(svgH - margin.top - margin.bottom, 80);

      var g = container.append('g').attr('transform', 'translate(' + margin.left + ',' + margin.top + ')');

      // Force x domain to span from first data point to now+6h
      var xMin = data.length ? data[0].datetimeInstance : currentLocal;
      var xMax = me.toLocalDateInstance(futureEpoch);
      var x = d3.scaleTime().range([0, width]).domain([xMin, xMax]);
      var maxRate = Math.max(d3.max(data, function (d) { return d.rainRate; }) * 1.15, 0.5 / unitDiv);
      var y = d3.scaleLinear().domain([0, maxRate]).range([height, 0]).nice();

      var colors = _rainRateColorIntervals(maxRate, maxRate * unitDiv, me._precipLegend); // legend is in user units, fallback needs mm/hr

      // Gradient
      var grad = g.append('defs').append('linearGradient')
        .attr('id', gradientId).attr('gradientUnits', 'userSpaceOnUse')
        .attr('x1', 0).attr('y1', height).attr('x2', 0).attr('y2', 0);
      colors.forEach(function (c) {
        grad.append('stop').attr('offset', c.offset).attr('stop-color', c.color);
      });

      // Threshold lines (rain rate equivalents in mm/h: light=2.5, moderate=7.6, heavy=50)
      var thresholds = [
        { rate: 2.5 / unitDiv, label: 'Light', color: '#22c55e' },
        { rate: 7.6 / unitDiv, label: 'Moderate', color: '#f59e0b' },
        { rate: 50 / unitDiv, label: 'Heavy', color: '#ef4444' },
      ];
      thresholds.forEach(function (t) {
        if (t.rate > maxRate) return;
        g.append('line')
          .attr('x1', 0).attr('y1', y(t.rate))
          .attr('x2', width).attr('y2', y(t.rate))
          .attr('stroke', t.color).attr('stroke-width', 0.5)
          .attr('stroke-dasharray', '3,3').attr('opacity', 0.6);
      });

      // Precip prob fill
      var probArea = d3.area()
        .x(function (d) {
          var offset = d.source === 'hourly' ? 30 : 7.5;
          return x(d3.timeMinute.offset(d.datetimeInstance, offset));
        })
        .y0(height)
        .y1(function (d) {
          var p = d.precipprob || 0;
          return height - (p / 100) * height;
        })
        .curve(d3.curveMonotoneX);
      g.append('path').datum(data).attr('d', probArea)
        .attr('fill', 'var(--wl-nowcast-prob-fill, #e8e7e7)')
        .attr('stroke', 'var(--wl-nowcast-prob-stroke, #b4b4b4)')
        .attr('stroke-width', 0.5);

      // Threshold labels removed from scrolling area — drawn in pinned Y overlay below

      // Split data into minutely (radar) and hourly (forecast)
      var minutelyData = data.filter(function (d) { return d.source === 'minutely'; });
      var hourlyData = data.filter(function (d) { return d.source === 'hourly'; });

      // Rain rate area — minutely (radar, solid gradient)
      var rateArea = d3.area()
        .x(function (d) { return x(d3.timeMinute.offset(d.datetimeInstance, 7.5)); })
        .y0(height)
        .y1(function (d) { return y(d.rainRate); })
        .curve(d3.curveMonotoneX);
      g.append('path').datum(minutelyData).attr('d', rateArea).attr('fill', me.svgGradUrl(gradientId));

      // Rain rate bars — hourly forecast (distinct style)
      var hourlyRain = hourlyData.filter(function (d) { return d.rainRate > 0.001; });
      if (hourlyRain.length) {
        var barWidth = Math.max(width / (data.length || 1) * 3, 8);
        g.selectAll('.wl-hourly-rate-bar').data(hourlyRain).enter().append('rect')
          .attr('x', function (d) { return x(d.datetimeInstance) - barWidth / 2; })
          .attr('y', function (d) { return y(d.rainRate); })
          .attr('width', barWidth)
          .attr('height', function (d) { return height - y(d.rainRate); })
          .attr('fill', me.svgGradUrl(gradientId)).attr('opacity', 0.45)
          .attr('stroke', 'var(--wl-border)').attr('stroke-width', 0.5).attr('rx', 2);
      }

      // Precip amount bars (observed/forecast precip overlaid, skip negligible values)
      var minPrecipBar = isUS ? 0.005 : 0.1;
      var precipData = minutelyData.filter(function (d) {
        var val = Math.max(d.precip || 0, d.precipremote || 0);
        return val > minPrecipBar;
      });
      if (precipData.length) {
        var maxPrecip = d3.max(precipData, function (d) { return Math.max(d.precip || 0, d.precipremote || 0); });
        var yPrecip = d3.scaleLinear().range([height, height * 0.55]).domain([0, Math.max(maxPrecip * 1.1, 0.01)]);
        var barW = Math.max(width / data.length * 0.7, 2);
        g.selectAll('.wl-precip-bar').data(precipData).enter().append('rect')
          .attr('x', function (d) { return x(d.datetimeInstance) - barW / 2; })
          .attr('y', function (d) { return yPrecip(d.precip || d.precipremote || 0); })
          .attr('width', barW)
          .attr('height', function (d) { return height - yPrecip(d.precip || d.precipremote || 0); })
          .attr('fill', '#4a90e2').attr('fill-opacity', 0.55).attr('stroke', '#2c5aa0').attr('stroke-width', 0.5).attr('rx', 1);
      }

      // Now line (static reference)
      var nowX = x(currentLocal);
      g.append('line').attr('class', 'wl-now-ref')
        .attr('x1', nowX).attr('y1', 0)
        .attr('x2', nowX).attr('y2', height)
        .attr('stroke', '#ef4444').attr('stroke-width', 1)
        .attr('stroke-dasharray', '3,3').attr('opacity', 0.5);

      // Draggable time marker
      var timeMarkerX = nowX;
      var timeGroup = g.append('g').attr('class', 'wl-time-marker');
      var timeLine = timeGroup.append('line')
        .attr('x1', timeMarkerX).attr('y1', 0)
        .attr('x2', timeMarkerX).attr('y2', height)
        .attr('stroke', '#333').attr('stroke-width', 2);
      var timeLabel = timeGroup.append('text')
        .attr('x', timeMarkerX).attr('y', -5)
        .attr('text-anchor', 'middle')
        .attr('fill', '#333')
        .style('font-size', '12px').style('font-weight', '700')
        .text('Now');

      var _updatingFromNowcast = false;
      function updateTimeMarker(px) {
        px = Math.max(0, Math.min(width, px));
        timeLine.attr('x1', px).attr('x2', px);
        timeLabel.attr('x', px);
        // Convert pixel to time — dt is a "shifted" Date from toLocalDateInstance,
        // so its local time methods already show the weather location's time
        var dt = x.invert(px);
        // Format label — do NOT pass timeZone since dt is already location-shifted
        try {
          var fmtOpts = me.use24HourTime
            ? { hour: 'numeric', hourCycle: 'h23' }
            : { hour: 'numeric', hour12: true };
          timeLabel.text(new Intl.DateTimeFormat('en-US', fmtOpts).format(dt));
        } catch (e) { timeLabel.text('--'); }
        // Convert shifted Date back to real Date for setMapDateTime
        var currentTzoffset = new Date().getTimezoneOffset() * 60000;
        var tzoffset = me.wxdata ? me.wxdata.tzoffset : 0;
        var realDt = new Date(dt.getTime() - tzoffset * 3600000 - currentTzoffset);
        // Sync map time (flag prevents setMapDateTime from re-syncing this marker)
        _updatingFromNowcast = true;
        me.setMapDateTime(realDt);
        _updatingFromNowcast = false;
      }

      // Store refs so other syncs can update this chart's marker
      me._rainNowcastTimeMarker = { x: x, update: function (datetime) {
        if (_updatingFromNowcast) return;
        var local = me.toLocalDateInstance(datetime.getTime() / 1000);
        var px = Math.max(0, Math.min(width, x(local)));
        timeLine.attr('x1', px).attr('x2', px);
        timeLabel.attr('x', px);
        try {
          var tz = me.wxdata && me.wxdata.timezone;
          var fmtOpts = me.use24HourTime
            ? { hour: 'numeric', hourCycle: 'h23' }
            : { hour: 'numeric', hour12: true };
          if (tz) fmtOpts.timeZone = tz;
          timeLabel.text(new Intl.DateTimeFormat('en-US', fmtOpts).format(datetime));
        } catch (e) { timeLabel.text('--'); }
      }};

      // Y axis — grid lines only (labels are in pinned overlay)
      g.append('g').attr('class', 'wl-chart-axis')
        .style('pointer-events', 'none')
        .call(d3.axisLeft(y).ticks(5).tickFormat('').tickSize(-width));

      // Time axis
      var xAxisG = g.append('g').attr('transform', 'translate(0,' + height + ')')
        .style('pointer-events', 'none')
        .call(d3.axisBottom(x).ticks(d3.timeHour.every(width > 400 ? 1 : 2)).tickFormat(d3.timeFormat('%H')).tickSize(-4));
      xAxisG.selectAll('text').attr('fill', 'var(--wl-text-muted)').style('font-size', '14px');
      // Remove first/last label if too close to edge
      var xTicks = xAxisG.selectAll('.tick').nodes();
      if (xTicks.length > 2) {
        var firstTick = xTicks[0].getBoundingClientRect();
        var lastTick = xTicks[xTicks.length - 1].getBoundingClientRect();
        var svgRect = svg.getBoundingClientRect();
        if (firstTick.left < svgRect.left) d3.select(xTicks[0]).select('text').remove();
        if (lastTick.right > svgRect.right - 5) d3.select(xTicks[xTicks.length - 1]).select('text').remove();
      }

      g.selectAll('.wl-chart-axis .tick line').attr('stroke', 'var(--wl-border-light)').attr('stroke-dasharray', '2,2');
      g.selectAll('.domain').attr('stroke', 'var(--wl-border)');

      // Full-chart interaction overlay — MUST be last element so it's on top of everything
      var overlay = g.append('rect').attr('class', 'wl-chart-overlay')
        .attr('width', width).attr('height', height)
        .attr('fill', 'rgba(0,0,0,0)').style('cursor', 'crosshair')
        .style('pointer-events', 'all');
      var dragging = false;

      function getChartX(event) {
        var clientX = (event.touches && event.touches[0]) ? event.touches[0].clientX : event.clientX;
        var svgLeft = svg.getBoundingClientRect().left;
        return clientX - svgLeft - margin.left;
      }

      // Desktop: drag to scrub time marker; Mobile: tap to set, swipe to scroll
      overlay.on('mousedown', function (event) {
        event.preventDefault();
        dragging = true;
        updateTimeMarker(getChartX(event));
      });
      overlay.on('click', function (event) {
        updateTimeMarker(getChartX(event));
      });
      d3.select(window).on('mousemove.wlnowcast', function (event) {
        if (!dragging) return;
        event.preventDefault();
        updateTimeMarker(getChartX(event));
      });
      d3.select(window).on('mouseup.wlnowcast', function () {
        dragging = false;
      });

      // Sync initial position if map time is already set
      if (me.mapDateTime) me._rainNowcastTimeMarker.update(me.mapDateTime);

      // Pinned Y-axis overlay
      var yPinDiv = me.$refs[yPinRef];
      if (yPinDiv) {
        var yPinW = margin.left + 60;
        yPinDiv.style.width = yPinW + 'px';
        yPinDiv.innerHTML = '';
        var yPinSvg = d3.select(yPinDiv).append('svg')
          .attr('width', yPinW)
          .attr('height', svgH);
        // Solid background only under the axis area, threshold labels float over chart
        yPinSvg.append('rect')
          .attr('width', margin.left)
          .attr('height', svgH)
          .attr('fill', bgVar);
        var yPinG = yPinSvg.append('g')
          .attr('transform', 'translate(' + (margin.left - 1) + ',' + margin.top + ')');
        var yPinAxis = yPinG.append('g').attr('class', 'wl-chart-axis')
          .call(d3.axisLeft(y).ticks(5).tickFormat(function (v) {
            if (isUS) return v < 1 ? v.toFixed(2) : v < 10 ? v.toFixed(2) : Math.round(v);
            return v < 10 ? v.toFixed(1) : Math.round(v);
          }).tickSize(0));
        yPinAxis.selectAll('text').attr('fill', 'var(--wl-text-muted)').style('font-size', '12px');
        yPinAxis.select('.domain').attr('stroke', 'none');
        yPinG.append('text').attr('x', -4).attr('y', -14)
          .attr('fill', 'var(--wl-text-light)')
          .attr('text-anchor', 'end')
          .style('font-size', '11px').text(rateUnit);
        // Threshold labels pinned to Y-axis overlay
        thresholds.forEach(function (t) {
          if (t.rate > maxRate) return;
          yPinG.append('text')
            .attr('x', 4).attr('y', y(t.rate) - 3)
            .attr('text-anchor', 'start')
            .attr('fill', t.color)
            .style('font-size', '11px').style('font-weight', '600')
            .text(t.label);
        });
      }

      // Scroll so "Now" is near the left edge of the visible area
      var scrollDiv = me.$refs[scrollRef];
      if (scrollDiv && scrollDiv.scrollWidth > scrollDiv.clientWidth) {
        scrollDiv.scrollLeft = Math.max(0, nowX + margin.left - scrollDiv.clientWidth * 0.25);
      }
    },

    renderRainHourlyChart() {
      var me = this;
      var data = me.hourlyPrecipToday;
      if (!data || data.length < 2) return;
      var svg = me.$refs.rainHourlySvg;
      if (!svg) return;
      if (svg.parentElement.getBoundingClientRect().width < 10) return;

      var container = d3.select(svg);
      container.selectAll('*').remove();
      var rect = svg.parentElement.getBoundingClientRect();
      var margin = { top: 30, right: 16, bottom: 32, left: 44 };
      var w = Math.max(rect.width - margin.left - margin.right, 100);
      var h = Math.max(170 - margin.top - margin.bottom, 60);

      container.attr('viewBox', '0 0 ' + rect.width + ' 170').attr('preserveAspectRatio', 'xMidYMid meet');
      var g = container.append('g').attr('transform', 'translate(' + margin.left + ',' + margin.top + ')');

      var x = d3.scaleBand()
        .domain(data.map(function (d) { return d.time; }))
        .range([0, w])
        .padding(0.12);

      var maxVal = Math.max(d3.max(data, function (d) { return Math.max(d.precip, d.precipremote, d.snow); }), 0.05);
      var yAmt = d3.scaleLinear().domain([0, maxVal * 1.15]).range([h, 0]).nice();
      var yProb = d3.scaleLinear().domain([0, 100]).range([h, 0]);

      var precipUnit = me.unitGroup === 'us' ? 'in' : 'mm';

      // Y axis
      g.append('g').attr('class', 'wl-chart-axis')
        .call(d3.axisLeft(yAmt).ticks(5).tickSize(-w))
        .selectAll('text').attr('fill', 'var(--wl-text-muted)').style('font-size', '12px');
      g.append('text').attr('x', -8).attr('y', -12)
        .attr('fill', 'var(--wl-text-light)').style('font-size', '12px').text(precipUnit);

      // X axis
      g.append('g').attr('class', 'wl-chart-axis').attr('transform', 'translate(0,' + h + ')')
        .call(d3.axisBottom(x).tickFormat(function (d) {
          return _formatTime(d, me.wxdata && me.wxdata.timezone);
        }).tickSize(-3))
        .selectAll('text').attr('fill', 'var(--wl-text-muted)').style('font-size', '12px');

      // Thin out x labels — keep roughly 1 per 3h
      var xTickNodes = g.selectAll('.wl-chart-axis:last-of-type .tick').nodes();
      var keepEvery = Math.max(1, Math.ceil(xTickNodes.length / 8));
      xTickNodes.forEach(function (node, i) {
        if (i % keepEvery !== 0) d3.select(node).select('text').remove();
      });

      g.selectAll('.wl-chart-axis .tick line').attr('stroke', 'var(--wl-border-light)').attr('stroke-dasharray', '2,2');
      g.selectAll('.wl-chart-axis .domain').attr('stroke', 'var(--wl-border)');

      // Snow bars stacked (skip zero)
      g.selectAll('.wl-rh-bar-snow').data(data.filter(function (d) { return d.snow > 0; })).enter().append('rect')
        .attr('x', function (d) { return x(d.time); })
        .attr('y', function (d) { return yAmt(d.precip + d.snow); })
        .attr('width', x.bandwidth())
        .attr('height', function (d) { return yAmt(d.precip) - yAmt(d.precip + d.snow); })
        .attr('fill', '#a78bfa').attr('rx', 2).attr('opacity', 0.8);

      // Station precip bars (skip zero)
      var minPrecipHrly = me.unitGroup === 'us' ? 0.005 : 0.1;
      g.selectAll('.wl-rh-bar-precip').data(data.filter(function (d) { return d.precip > minPrecipHrly; })).enter().append('rect')
        .attr('x', function (d) { return x(d.time); })
        .attr('y', function (d) { return yAmt(d.precip); })
        .attr('width', x.bandwidth())
        .attr('height', function (d) { return h - yAmt(d.precip); })
        .attr('fill', function (d) { return d.isPast ? 'var(--wl-accent)' : '#a78bfa'; })
        .attr('rx', 2)
        .attr('opacity', function (d) { return d.isCurrent ? 1 : 0.65; });

      // Radar precip overlay (skip zero)
      g.selectAll('.wl-rh-bar-radar').data(data.filter(function (d) { return d.precipremote > minPrecipHrly; })).enter().append('rect')
        .attr('x', function (d) { return x(d.time) + x.bandwidth() * 0.25; })
        .attr('y', function (d) { return yAmt(d.precipremote); })
        .attr('width', x.bandwidth() * 0.5)
        .attr('height', function (d) { return h - yAmt(d.precipremote); })
        .attr('fill', '#f97316').attr('rx', 1).attr('opacity', 0.7);

      // Prob line
      var probLine = d3.line()
        .x(function (d) { return x(d.time) + x.bandwidth() / 2; })
        .y(function (d) { return yProb(d.precipprob); })
        .curve(d3.curveCatmullRom);
      g.append('path').datum(data).attr('d', probLine)
        .attr('fill', 'none').attr('stroke', '#f59e0b')
        .attr('stroke-width', 1.5).attr('stroke-dasharray', '4,2');

      // Map-synced time marker
      var nowIdx = data.findIndex(function (d) { return d.isCurrent; });
      var bandTimes = data.map(function (d) { return { epoch: d.time * 1000, band: x(d.time), bw: x.bandwidth() }; });
      function findNearestBandX(datetime) {
        var t = datetime.getTime();
        var best = 0, bestDist = Infinity;
        for (var i = 0; i < bandTimes.length; i++) {
          var dist = Math.abs(t - bandTimes[i].epoch);
          if (dist < bestDist) { bestDist = dist; best = bandTimes[i].band + bandTimes[i].bw / 2; }
        }
        return best;
      }
      var initMX = nowIdx >= 0 ? x(data[nowIdx].time) + x.bandwidth() / 2 : 0;
      var mtLine = g.append('line').attr('class', 'wl-maptimeline')
        .attr('x1', initMX).attr('y1', 0).attr('x2', initMX).attr('y2', h)
        .attr('stroke', '#555').attr('stroke-width', 1.5).attr('opacity', 0.7);
      var mtLabel = g.append('text').attr('class', 'wl-maptimeline-label')
        .attr('x', initMX).attr('y', h + 22).attr('text-anchor', 'middle')
        .attr('fill', '#555').style('font-size', '12px').style('font-weight', '600')
        .text('');

      me._rainHourlyTimeMarker = { update: function (datetime) {
        var px = findNearestBandX(datetime);
        mtLine.attr('x1', px).attr('x2', px);
        mtLabel.attr('x', px);
      }};
      if (me.mapDateTime) me._rainHourlyTimeMarker.update(me.mapDateTime);

      // Legend
      var leg = g.append('g').attr('transform', 'translate(' + (w - 280) + ',-18)');
      leg.append('rect').attr('width', 10).attr('height', 8).attr('fill', 'var(--wl-accent)').attr('opacity', 0.7).attr('rx', 1);
      leg.append('text').attr('x', 14).attr('y', 7).text('Observed').attr('fill', 'var(--wl-text-muted)').style('font-size', '12px');
      leg.append('rect').attr('x', 78).attr('width', 10).attr('height', 8).attr('fill', '#a78bfa').attr('opacity', 0.7).attr('rx', 1);
      leg.append('text').attr('x', 92).attr('y', 7).text('Forecast').attr('fill', 'var(--wl-text-muted)').style('font-size', '12px');
      leg.append('rect').attr('x', 156).attr('width', 10).attr('height', 8).attr('fill', '#f97316').attr('opacity', 0.7).attr('rx', 1);
      leg.append('text').attr('x', 170).attr('y', 7).text('Radar').attr('fill', 'var(--wl-text-muted)').style('font-size', '12px');
      leg.append('line').attr('x1', 214).attr('y1', 4).attr('x2', 228).attr('y2', 4).attr('stroke', '#f59e0b').attr('stroke-width', 1.5).attr('stroke-dasharray', '4,2');
      leg.append('text').attr('x', 232).attr('y', 7).text('Prob').attr('fill', 'var(--wl-text-muted)').style('font-size', '12px');
    },

    renderHourlyPrecipDetailChart() {
      var me = this;
      if (!me.days || me.days.length < 1) return;
      var setup = me.chartSetup('chartHourlyPrecip', 220);
      if (!setup) return;
      var g = setup.g, w = setup.w, h = setup.h;

      var data = [];
      var dayCount = Math.min(me.days.length, 3);
      for (var di = 0; di < dayCount; di++) {
        var day = me.days[di];
        if (!day.hours) continue;
        day.hours.forEach(function (hour) {
          data.push({
            time: new Date(hour.datetimeEpoch * 1000),
            precip: hour.precip || 0,
            precipremote: hour.precipremote || 0,
            precipprob: hour.precipprob || 0,
            snow: hour.snow || 0,
          });
        });
      }
      if (data.length < 2) return;

      var x = d3.scaleTime().domain(d3.extent(data, function (d) { return d.time; })).range([0, w]);
      var maxVal = Math.max(d3.max(data, function (d) { return Math.max(d.precip + d.snow, d.precipremote); }), 0.05);
      var yAmt = d3.scaleLinear().domain([0, maxVal * 1.1]).range([h, 0]).nice();
      var yProb = d3.scaleLinear().domain([0, 100]).range([h, 0]);

      var precipUnit = me.unitGroup === 'us' ? 'in' : 'mm';

      g.append('g').attr('class', 'wl-chart-axis').attr('transform', 'translate(0,' + h + ')')
        .call(d3.axisBottom(x).ticks(d3.timeDay.every(1)).tickFormat(function (d) {
          return me._chartDayName(d);
        }).tickSize(-3))
        .selectAll('text').attr('fill', 'var(--wl-text-muted)').style('font-size', '12px');
      g.append('g').attr('class', 'wl-chart-axis')
        .call(d3.axisLeft(yAmt).ticks(5).tickSize(-w))
        .selectAll('text').attr('fill', 'var(--wl-text-muted)').style('font-size', '12px');
      g.append('text').attr('x', -8).attr('y', -12)
        .attr('fill', 'var(--wl-text-light)').style('font-size', '12px').text(precipUnit);
      g.selectAll('.wl-chart-axis .tick line').attr('stroke', 'var(--wl-border-light)').attr('stroke-dasharray', '2,2');
      g.selectAll('.wl-chart-axis .domain').attr('stroke', 'var(--wl-border)');

      var barW = Math.max(w / data.length - 1, 1.5);

      // Precip bars
      g.selectAll('.wl-hpd-bar').data(data).enter().append('rect')
        .attr('x', function (d) { return x(d.time) - barW / 2; })
        .attr('y', function (d) { return yAmt(d.precip); })
        .attr('width', barW)
        .attr('height', function (d) { return h - yAmt(d.precip); })
        .attr('fill', 'var(--wl-accent)').attr('opacity', 0.6);

      // Snow bars stacked
      g.selectAll('.wl-hpd-snow').data(data).enter().append('rect')
        .attr('x', function (d) { return x(d.time) - barW / 2; })
        .attr('y', function (d) { return yAmt(d.precip + d.snow); })
        .attr('width', barW)
        .attr('height', function (d) { return d.snow > 0 ? yAmt(d.precip) - yAmt(d.precip + d.snow) : 0; })
        .attr('fill', '#a78bfa').attr('opacity', 0.7);

      // Radar overlay line
      var radarLine = d3.line()
        .x(function (d) { return x(d.time); })
        .y(function (d) { return yAmt(d.precipremote); })
        .defined(function (d) { return d.precipremote > 0; })
        .curve(d3.curveCatmullRom);
      g.append('path').datum(data).attr('d', radarLine)
        .attr('fill', 'none').attr('stroke', '#f97316')
        .attr('stroke-width', 1.5);

      // Prob line
      var probLine = d3.line()
        .x(function (d) { return x(d.time); })
        .y(function (d) { return yProb(d.precipprob); })
        .curve(d3.curveCatmullRom);
      g.append('path').datum(data).attr('d', probLine)
        .attr('fill', 'none').attr('stroke', '#f59e0b')
        .attr('stroke-width', 1.5).attr('stroke-dasharray', '4,2');

      // Legend
      var leg = g.append('g').attr('transform', 'translate(' + (w - 226) + ',-16)');
      leg.append('rect').attr('width', 10).attr('height', 8).attr('fill', 'var(--wl-accent)').attr('opacity', 0.7).attr('rx', 1);
      leg.append('text').attr('x', 14).attr('y', 7).text('Rain').attr('fill', 'var(--wl-text-muted)').style('font-size', '12px');
      leg.append('rect').attr('x', 52).attr('width', 10).attr('height', 8).attr('fill', '#a78bfa').attr('opacity', 0.7).attr('rx', 1);
      leg.append('text').attr('x', 66).attr('y', 7).text('Snow').attr('fill', 'var(--wl-text-muted)').style('font-size', '12px');
      leg.append('line').attr('x1', 108).attr('y1', 4).attr('x2', 122).attr('y2', 4).attr('stroke', '#f97316').attr('stroke-width', 1.5);
      leg.append('text').attr('x', 126).attr('y', 7).text('Radar').attr('fill', 'var(--wl-text-muted)').style('font-size', '12px');
      leg.append('line').attr('x1', 168).attr('y1', 4).attr('x2', 182).attr('y2', 4).attr('stroke', '#f59e0b').attr('stroke-width', 1.5).attr('stroke-dasharray', '4,2');
      leg.append('text').attr('x', 186).attr('y', 7).text('Prob').attr('fill', 'var(--wl-text-muted)').style('font-size', '12px');
    },

    renderCumulativeRainChart() {
      var me = this;
      if (!me.days || me.days.length < 2) return;
      var setup = me.chartSetup('chartCumulativeRain', 200);
      if (!setup) return;
      var g = setup.g, w = setup.w, h = setup.h;

      var data = [];
      var cumulative = 0;
      me.days.forEach(function (day) {
        if (!day.hours) return;
        day.hours.forEach(function (hour) {
          cumulative += hour.precip || 0;
          data.push({
            time: new Date(hour.datetimeEpoch * 1000),
            cumulative: cumulative,
          });
        });
      });
      if (data.length < 2) return;

      var x = d3.scaleTime().domain(d3.extent(data, function (d) { return d.time; })).range([0, w]);
      var maxCum = d3.max(data, function (d) { return d.cumulative; }) || 0.1;
      var y = d3.scaleLinear().domain([0, maxCum * 1.15]).range([h, 0]).nice();

      var precipUnit = me.unitGroup === 'us' ? 'in' : 'mm';

      g.append('g').attr('class', 'wl-chart-axis').attr('transform', 'translate(0,' + h + ')')
        .call(d3.axisBottom(x).ticks(Math.min(me.days.length, 7)).tickFormat(function (d) {
          return me._chartDayName(d);
        }).tickSize(-3))
        .selectAll('text').attr('fill', 'var(--wl-text-muted)').style('font-size', '14px');
      g.append('g').attr('class', 'wl-chart-axis')
        .call(d3.axisLeft(y).ticks(5).tickSize(-w))
        .selectAll('text').attr('fill', 'var(--wl-text-muted)').style('font-size', '12px');
      g.append('text').attr('x', -8).attr('y', -12)
        .attr('fill', 'var(--wl-text-light)').style('font-size', '12px').text(precipUnit);
      g.selectAll('.wl-chart-axis .tick line').attr('stroke', 'var(--wl-border-light)').attr('stroke-dasharray', '2,2');
      g.selectAll('.wl-chart-axis .domain').attr('stroke', 'var(--wl-border)');

      // Area fill
      var area = d3.area()
        .x(function (d) { return x(d.time); })
        .y0(h)
        .y1(function (d) { return y(d.cumulative); })
        .curve(d3.curveCatmullRom);

      var grad = g.append('defs').append('linearGradient').attr('id', 'wl-cumrain-grad')
        .attr('x1', '0').attr('y1', '0').attr('x2', '0').attr('y2', '1');
      grad.append('stop').attr('offset', '0%').attr('stop-color', 'var(--wl-accent, #3b82f6)').attr('stop-opacity', 0.4);
      grad.append('stop').attr('offset', '100%').attr('stop-color', 'var(--wl-accent, #3b82f6)').attr('stop-opacity', 0.05);

      g.append('path').datum(data).attr('d', area).attr('fill', me.svgGradUrl('wl-cumrain-grad'));

      // Line
      var line = d3.line()
        .x(function (d) { return x(d.time); })
        .y(function (d) { return y(d.cumulative); })
        .curve(d3.curveCatmullRom);
      g.append('path').datum(data).attr('d', line)
        .attr('fill', 'none').attr('stroke', 'var(--wl-accent, #3b82f6)').attr('stroke-width', 2);

      // End total
      var last = data[data.length - 1];
      g.append('text')
        .attr('x', w - 4).attr('y', y(last.cumulative) - 6)
        .attr('text-anchor', 'end')
        .attr('fill', 'var(--wl-accent, #3b82f6)')
        .style('font-size', '14px').style('font-weight', '700')
        .text('Total: ' + _formatElementValue(last.cumulative, 'precip', me.unitGroup));
    },

    renderNowcastChart() {
      var me = this;
      var svg = me.$refs.nowcastSvg;
      if (!svg || !me.minutely.length) return;
      if (svg.parentElement.getBoundingClientRect().width < 10) return;

      var data = me.minutely;
      var current = new Date();
      var currentLocal = me.toLocalDateInstance(current.getTime() / 1000);

      data.forEach(function (d) {
        d.datetimeInstance = me.toLocalDateInstance(d.datetimeEpoch, d.tzoffset);
      });

      var container = d3.select(svg);
      container.selectAll('*').remove();

      var rect = svg.parentElement.getBoundingClientRect();
      var margin = { top: 18, right: 15, bottom: 26, left: 20 };
      var width = Math.max(rect.width - margin.left - margin.right, 100);
      var height = Math.max(140 - margin.top - margin.bottom, 60);

      container.attr('viewBox', '0 0 ' + rect.width + ' 140').attr('preserveAspectRatio', 'xMidYMid meet');

      var g = container.append('g').attr('transform', 'translate(' + margin.left + ',' + margin.top + ')');

      var x = d3.scaleTime().range([0, width]).domain(d3.extent(data, function (d) { return d.datetimeInstance; }));

      var minR = 5;
      var y = d3.scaleLinear().domain([minR, 75]).range([height, 0]);
      var yProb = d3.scaleLinear().range([height, 5]).domain([0, 100]);

      // Convert dBZ range to rate for legend color mapping
      var isUS = me.unitGroup === 'us';
      var unitDiv = isUS ? 25.4 : 1;
      var maxRateForColor = _dbzToRainRateNum(75) / unitDiv;
      var colors = me._precipLegend ? _rainRateColorIntervals(maxRateForColor, _dbzToRainRateNum(75), me._precipLegend) : _dbzColorIntervals();

      // Gradient
      var grad = g.append('defs').append('linearGradient')
        .attr('id', 'wl-reflectivity-grad').attr('gradientUnits', 'userSpaceOnUse')
        .attr('x1', 0).attr('y1', height).attr('x2', 0).attr('y2', 0);
      colors.forEach(function (c) {
        grad.append('stop').attr('offset', c.offset).attr('stop-color', c.color);
      });

      // Precip prob fill
      var probArea = d3.area()
        .x(function (d) { return x(d3.timeMinute.offset(d.datetimeInstance, 7.5)); })
        .y0(height)
        .y1(function (d) { return yProb(d.precipprob || 0); })
        .curve(d3.curveCatmullRom);

      g.append('path').datum(data).attr('d', probArea)
        .attr('fill', 'var(--wl-nowcast-prob-fill, #e8e7e7)')
        .attr('stroke', 'var(--wl-nowcast-prob-stroke, #b4b4b4)')
        .attr('stroke-width', 1);

      // Reflectivity area
      var reflArea = d3.area()
        .x(function (d) { return x(d3.timeMinute.offset(d.datetimeInstance, 7.5)); })
        .y0(height)
        .y1(function (d) { return y(d.reflectivity > minR ? d.reflectivity : minR); })
        .curve(d3.curveCatmullRom);

      g.append('path').datum(data).attr('d', reflArea).attr('fill', me.svgGradUrl('wl-reflectivity-grad'));

      // Now line
      var nowX = x(currentLocal);
      g.append('line').attr('x1', nowX).attr('y1', 0).attr('x2', nowX).attr('y2', height)
        .attr('stroke', '#ef4444').attr('stroke-width', 1.5).attr('stroke-dasharray', '4,2');

      // Map time line
      me.updateMapTimeLine = function (datetime) {
        var local = me.toLocalDateInstance(datetime.getTime() / 1000);
        var dtX = x(local);
        g.select('.wl-maptimeline').remove();
        g.append('line').attr('class', 'wl-maptimeline')
          .attr('x1', dtX).attr('y1', 0).attr('x2', dtX).attr('y2', height)
          .attr('stroke', '#333').attr('stroke-width', 1);
      };

      if (me.mapDateTime) me.updateMapTimeLine(me.mapDateTime);

      // Time axis
      g.append('g').attr('transform', 'translate(0,' + height + ')')
        .call(d3.axisBottom(x).ticks(d3.timeHour.every(width > 500 ? 1 : 3)).tickFormat(d3.timeFormat('%H')).tickSize(-4))
        .selectAll('text').attr('fill', 'var(--wl-text-muted)').style('font-size', '14px');

      g.selectAll('.domain').attr('stroke', 'var(--wl-border)');
      g.selectAll('.tick line').attr('stroke', 'var(--wl-border)');
    },

    // ---- Responsive ----
    checkMobile() {
      this.isMobile = window.innerWidth < 768;
    },

    scrollHourlyToNow() {
      var me = this;
      me.$nextTick(function () {
        var scroll = me.$refs.hourlyScrollCombined || me.$refs.hourlyScroll;
        // If the chosen element is hidden (zero width), try the other one
        if (scroll && scroll.offsetWidth === 0) {
          scroll = (scroll === me.$refs.hourlyScrollCombined) ? me.$refs.hourlyScroll : me.$refs.hourlyScrollCombined;
        }
        if (!scroll || scroll.offsetWidth === 0) return;
        var nowCard = scroll.querySelector('.wl-hour-card--current');
        if (nowCard) {
          var scrollLeft = nowCard.offsetLeft - scroll.offsetWidth / 2 + nowCard.offsetWidth / 2;
          scroll.scrollTo({ left: Math.max(0, scrollLeft), behavior: 'smooth' });
        }
      });
    },

    handleResize() {
      var me = this;
      me.checkMobile();
      if (me.leafletMap) {
        me.leafletMap.invalidateSize();
        // Second invalidation after layout settles for browsers that batch resize
        setTimeout(function () {
          if (me.leafletMap) me.leafletMap.invalidateSize();
        }, 100);
      }
      if (me.activeTab === 'rain') {
        me.renderRainNowcastChart();
        me.renderRainHourlyChart();
      } else {
        me.renderNowcastChart();
        me.renderMiniRainChart();
      }
      if (me.activeTab === 'charts') me.renderAllCharts();
    },
  },

  watch: {
    activeTab(val) {
      // Move map container back to home when leaving map tab
      if (val !== 'map') {
        this._moveMapToHome();
      }
      if (val === 'charts') {
        this.$nextTick(function () { this.renderAllCharts(); }.bind(this));
      }
      if (val === 'history') {
        this.fetchHistoryData();
      }
      if (val === 'map') {
        var me = this;
        me.$nextTick(function () {
          me._moveMapToTab();
        });
      }
      if (val === 'rain') {
        var me = this;
        me.$nextTick(function () {
          me.renderRainNowcastChart();
          me.renderRainHourlyChart();
        });
      }
      if (val === 'now') {
        var me = this;
        me.$nextTick(function () {
          me.renderNowcastChart();
          me.renderMiniRainChart();
          me.scrollHourlyToNow();
        });
      }
    },
    showLocationSearch(val) {
      if (val) {
        var me = this;
        me.$nextTick(function () {
          if (me.$refs.locationInput) me.$refs.locationInput.focus();
        });
      }
    },
    darkMode() {
      var me = this;
      if (me.leafletMap && me.baseLayer) {
        me.leafletMap.removeLayer(me.baseLayer);
        me.baseLayer = me.createBaseLayer();
        me.leafletMap.addLayer(me.baseLayer);
        me.baseLayer.bringToBack();
      }
    },
    mapFullscreen() {
      var me = this;
      me.$nextTick(function () {
        if (me.leafletMap) me.leafletMap.invalidateSize();
      });
    },
  },

  mounted() {
    var me = this;

    me.checkMobile();
    me._resizeHandler = _debounce(function () { me.handleResize(); }, 250);
    window.addEventListener('resize', me._resizeHandler);

    // Load saved state
    var savedUnitGroup = VcLocationsStorage.getUnitGroup();
    me.unitGroup = savedUnitGroup || 'us';
    me.refreshLocationsHistory();

    // Restore persisted settings
    if (VcLocationsStorage.hasValue('wl_darkMode')) me.darkMode = !!VcLocationsStorage.getValue('wl_darkMode');
    if (VcLocationsStorage.hasValue('wl_use24HourTime')) me.use24HourTime = !!VcLocationsStorage.getValue('wl_use24HourTime');
    if (VcLocationsStorage.hasValue('wl_useLlxApi')) me.useLlxApi = !!VcLocationsStorage.getValue('wl_useLlxApi');
    if (VcLocationsStorage.hasValue('wl_useV2Forecast')) me.useV2Forecast = !!VcLocationsStorage.getValue('wl_useV2Forecast');
    if (VcLocationsStorage.hasValue('wl_playSpeed')) me.playSpeed = parseInt(VcLocationsStorage.getValue('wl_playSpeed'), 10) || 800;
    if (VcLocationsStorage.hasValue('wl_mapDisplayMode')) me.mapDisplayMode = VcLocationsStorage.getValue('wl_mapDisplayMode') || 'full';
    if (VcLocationsStorage.hasValue('wl_overlayLightning')) me.overlayLightning = !!VcLocationsStorage.getValue('wl_overlayLightning');
    if (VcLocationsStorage.hasValue('wl_overlayHail')) me.overlayHail = !!VcLocationsStorage.getValue('wl_overlayHail');

    var lastLocation = me.locationsHistory && me.locationsHistory.length && me.locationsHistory[0];
    if (lastLocation) me.selectedLocation = lastLocation;

    // Restore GPS tracking state
    if (VcLocationsStorage.hasValue('wl_isGpsLocation') && !!VcLocationsStorage.getValue('wl_isGpsLocation')) {
      me.isGpsLocation = true;
      // Immediately show weather with the last saved location — don't block on GPS
      me.fetchWeatherData();
      // Re-acquire GPS position in the background; refresh only if it changed significantly
      VcFindMyLocation.findMyLocation(true).then(function (location) {
        if (location) {
          var parts = location.split(',');
          var isLatLon = parts.length === 2 && !isNaN(parseFloat(parts[0].trim())) && !isNaN(parseFloat(parts[1].trim()));
          if (isLatLon) {
            var lat = parseFloat(parts[0].trim());
            var lon = parseFloat(parts[1].trim());
            // Check if location actually changed enough to warrant a re-fetch
            var prevLoc = me.selectedLocation && me.selectedLocation.location;
            var prevParts = prevLoc ? prevLoc.split(',') : null;
            var needsRefresh = true;
            if (prevParts && prevParts.length === 2) {
              var prevLat = parseFloat(prevParts[0].trim());
              var prevLon = parseFloat(prevParts[1].trim());
              // ~1 km threshold
              if (Math.abs(lat - prevLat) < 0.01 && Math.abs(lon - prevLon) < 0.01) {
                needsRefresh = false;
              }
            }
            VcGeocoder.reverseGeocode(lat, lon).then(function (result) {
              var displayName = (result && result.displayName) ? result.displayName : location;
              me.selectedLocation = { location: location, name: displayName };
              VcLocationsStorage.addLocation(me.selectedLocation);
              me.refreshLocationsHistory();
              if (needsRefresh) me.fetchWeatherData(true);
            });
          } else {
            // IP fallback — GPS not working, clear the flag
            console.log('[WeatherLive] GPS re-acquire returned IP fallback, disabling GPS tracking');
            me.isGpsLocation = false;
            VcLocationsStorage.setValue('wl_isGpsLocation', false);
          }
        }
      }).catch(function (err) {
        console.warn('[WeatherLive] GPS re-acquire on startup failed:', err);
      });
    }

    // Session
    var vcCommunicator = window.vcCommunicator;
    if (vcCommunicator) {
      me.sessionId = vcCommunicator.sessionId;
      vcCommunicator.addHandler('createsession', function (sessionId) {
        me.sessionId = sessionId;
        // Re-fetch with expanded access after login
        me.fetchWeatherData(true);
      });
    }

    // Dark mode from system preference (only if no saved preference)
    if (!VcLocationsStorage.hasValue('wl_darkMode') && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      me.darkMode = true;
    }

    // Only fetch immediately if NOT a GPS user (GPS path already triggered fetch above)
    if (!me.isGpsLocation) {
      me.fetchWeatherData();
    }

    // Fetch map legend colors for precip charts
    me.fetchPrecipLegend();

    // Initialize pull-to-refresh
    me._initPullToRefresh();

    // Start history refresh timer
    me._startHistoryRefreshTimer();

    // Re-fetch when app resumes from background (iOS Capacitor + browser)
    me._handleAppResume = function () {
      if (!me.selectedLocation) return;
      var now = Date.now();
      // Only refresh if data is older than 5 minutes
      if (!me._lastFetchTime || (now - me._lastFetchTime) > 5 * 60 * 1000) {
        console.log('[WeatherLive] App resumed, refreshing data (age: ' + (me._lastFetchTime ? Math.round((now - me._lastFetchTime) / 1000) + 's' : 'never') + ')');
        if (me.isGpsLocation) {
          // GPS check will fetch new data if location changed
          me._checkGpsLocationChange();
        }
        // Always refresh weather data on resume
        me.fetchWeatherData(true);
      }
    };

    me._visibilityHandler = function () {
      if (document.visibilityState === 'visible') {
        me._handleAppResume();
      }
    };
    me._focusHandler = function () {
      me._handleAppResume();
    };
    me._pageshowHandler = function (e) {
      // persisted = page restored from bfcache
      if (e.persisted) {
        me._handleAppResume();
      }
    };

    document.addEventListener('visibilitychange', me._visibilityHandler);
    window.addEventListener('focus', me._focusHandler);
    window.addEventListener('pageshow', me._pageshowHandler);
    // Capacitor fires 'resume' on the document when the app comes to foreground
    document.addEventListener('resume', me._handleAppResume);

    // Request notification permission on first launch (iOS Capacitor)
    if (window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform()) {
      import('@capacitor/local-notifications').then(function (mod) {
        mod.LocalNotifications.requestPermissions();
      }).catch(function () { /* not available */ });

      // Configure background fetch for weather alerts & rain checks
      me._initBackgroundFetch();
    }

    // Tick every 30s to keep "updated X ago" label fresh
    me._tickInterval = setInterval(function () { me.nowTick = Date.now(); }, 30000);

    // Prevent iOS zoom on double tap
    document.addEventListener('gesturestart', function (e) { e.preventDefault(); }, { passive: false });
  },

  beforeUnmount() {
    var me = this;
    window.removeEventListener('resize', me._resizeHandler);
    if (me._visibilityHandler) document.removeEventListener('visibilitychange', me._visibilityHandler);
    if (me._focusHandler) window.removeEventListener('focus', me._focusHandler);
    if (me._pageshowHandler) window.removeEventListener('pageshow', me._pageshowHandler);
    if (me._handleAppResume) document.removeEventListener('resume', me._handleAppResume);
    if (me._tickInterval) clearInterval(me._tickInterval);
    if (me._historyRefreshTimer) clearInterval(me._historyRefreshTimer);
    me.stopPlaying();
    if (me._mapResizeObserver) { me._mapResizeObserver.disconnect(); me._mapResizeObserver = null; }
    if (me.leafletMap) { me.leafletMap.remove(); me.leafletMap = null; }
  },
};
</script>

<style src="./weather-live/weather-live.scss"></style>

<style>
/* ---- Rain tab ---- */
.wl-rain-view {
  padding: 0 4px 4px;
}

/* Sign-in CTA for rain features */
.wl-rain-signin-cta {
  text-align: center;
  padding: 16px 12px;
  margin-bottom: 10px;
  border-radius: 10px;
  background: linear-gradient(135deg, #eff6ff 0%, #f0fdf4 100%);
  border: 1px solid #bfdbfe;
}
.wl-rain-signin-cta__icon {
  font-size: 28px;
  color: #3b82f6;
  margin-bottom: 6px;
}
.wl-rain-signin-cta__text strong {
  display: block;
  font-size: 15px;
  color: var(--wl-text, #1e293b);
  margin-bottom: 4px;
}
.wl-rain-signin-cta__text p {
  font-size: 13px;
  color: var(--wl-text-muted, #64748b);
  margin: 0 0 10px;
  line-height: 1.4;
}
.wl-rain-signin-cta__actions {
  display: flex;
  gap: 8px;
  justify-content: center;
  flex-wrap: wrap;
}
.wl-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 7px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  border: none;
  cursor: pointer;
  transition: background 0.15s, box-shadow 0.15s;
}
.wl-btn--primary {
  background: #3b82f6;
  color: #fff;
}
.wl-btn--primary:hover { background: #2563eb; }
.wl-btn--primary:disabled { background: #93c5fd; cursor: not-allowed; }
.wl-btn--outline {
  background: #fff;
  color: #3b82f6;
  border: 1px solid #3b82f6;
}
.wl-btn--outline:hover { background: #eff6ff; }

/* Inline sign-in form */
.wl-signin-form {
  text-align: left;
}
.wl-signin-form__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.wl-signin-form__header strong {
  font-size: 15px;
  color: var(--wl-text, #1e293b);
}
.wl-signin-form__close {
  background: none;
  border: none;
  color: var(--wl-text-muted, #64748b);
  font-size: 16px;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 4px;
}
.wl-signin-form__close:hover { background: rgba(0,0,0,0.06); }
.wl-signin-form__body {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.wl-signin-form__label {
  font-size: 12px;
  font-weight: 600;
  color: var(--wl-text-muted, #64748b);
  margin-bottom: -4px;
}
.wl-signin-form__input {
  width: 100%;
  padding: 9px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 14px;
  background: #fff;
  color: var(--wl-text, #1e293b);
  outline: none;
  transition: border-color 0.15s;
  -webkit-appearance: none;
}
.wl-signin-form__input:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59,130,246,0.15);
}
.wl-signin-form__input:disabled {
  opacity: 0.6;
  background: #f8fafc;
}
.wl-signin-form__password-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}
.wl-signin-form__password-wrapper .wl-signin-form__input {
  padding-right: 40px;
}
.wl-signin-form__password-toggle {
  position: absolute;
  right: 8px;
  background: none;
  border: none;
  color: var(--wl-text-muted, #64748b);
  font-size: 15px;
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 4px;
  line-height: 1;
}
.wl-signin-form__password-toggle:hover {
  color: var(--wl-text, #1e293b);
  background: rgba(0,0,0,0.06);
}
.wl-signin-form__submit {
  width: 100%;
  justify-content: center;
  padding: 10px;
  margin-top: 4px;
}
.wl-signin-form__error {
  background: #fef2f2;
  color: #dc2626;
  font-size: 13px;
  padding: 8px 10px;
  border-radius: 6px;
  border: 1px solid #fecaca;
  display: flex;
  align-items: center;
  gap: 6px;
}
.wl-signin-form__links {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: var(--wl-text-muted, #64748b);
  margin-top: 2px;
}
.wl-signin-form__links a {
  color: #3b82f6;
  text-decoration: none;
}
.wl-signin-form__links a:hover { text-decoration: underline; }
@keyframes wl-spin {
  to { transform: rotate(360deg); }
}
.wl-spin {
  animation: wl-spin 0.8s linear infinite;
}

/* Rain intensity status banner */
.wl-rain-status {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 8px;
  margin-bottom: 6px;
  background: var(--wl-bg-card, #f3f4f6);
}
.wl-rain-status--level-0 { border-left: 4px solid #94a3b8; }
.wl-rain-status--level-1 { border-left: 4px solid #22c55e; background: #f0fdf4; }
.wl-rain-status--level-2 { border-left: 4px solid #22c55e; background: #f0fdf4; }
.wl-rain-status--level-3 { border-left: 4px solid #f59e0b; background: #fffbeb; }
.wl-rain-status--level-4 { border-left: 4px solid #ef4444; background: #fef2f2; }
.wl-rain-status--level-5 { border-left: 4px solid #7c3aed; background: #f5f3ff; }
.wl--dark .wl-rain-status--level-1,
.wl--dark .wl-rain-status--level-2 { background: rgba(34,197,94,0.1); }
.wl--dark .wl-rain-status--level-3 { background: rgba(245,158,11,0.1); }
.wl--dark .wl-rain-status--level-4 { background: rgba(239,68,68,0.1); }
.wl--dark .wl-rain-status--level-5 { background: rgba(124,58,237,0.1); }
.wl-rain-status__icon { font-size: 20px; color: var(--wl-accent, #3b82f6); }
.wl-rain-status__label { font-weight: 700; font-size: 14px; }
.wl-rain-status__rate { font-size: 12px; color: var(--wl-text-muted, #6b7280); }
.wl-rain-status__dbz { font-size: 11px; color: var(--wl-text-light, #9ca3af); }
.wl-rain-status__detail { font-size: 12px; font-weight: 400; color: var(--wl-text-muted, #6b7280); }

/* Rain totals cards */
.wl-rain-totals {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4px;
  margin-bottom: 6px;
}
.wl-rain-total-card {
  background: var(--wl-bg-card, #f8fafc);
  border-radius: 8px;
  padding: 4px 6px;
  text-align: center;
}
.wl-rain-total-card__icon { font-size: 16px; color: var(--wl-accent, #3b82f6); margin-bottom: 1px; }
.wl-rain-total-card__label { font-size: 10px; color: var(--wl-text-muted, #6b7280); text-transform: uppercase; letter-spacing: 0.04em; }
.wl-rain-total-card__value { font-size: 18px; font-weight: 700; color: var(--wl-text, #1f2937); }
.wl-rain-total-card__sub { font-size: 10px; color: var(--wl-text-light, #9ca3af); }

/* Rain narrative */
.wl-rain-narrative {
  display: flex;
  gap: 6px;
  align-items: flex-start;
  padding: 4px 8px;
  border-radius: 6px;
  background: var(--wl-bg-card, #f3f4f6);
  margin-bottom: 6px;
  font-size: 12px;
  line-height: 1.3;
  color: var(--wl-text, #374151);
}
.wl-rain-narrative i { font-size: 18px; color: var(--wl-accent, #3b82f6); margin-top: 1px; flex-shrink: 0; }
.wl-rain-narrative--none { color: var(--wl-text-muted, #6b7280); }
.wl-rain-narrative--none i { color: var(--wl-text-light, #9ca3af); }

/* Rain chart sections */
.wl-rain-chart-section { margin-bottom: 6px; }
.wl-rain-nowcast-card { min-height: 140px; }

/* Precip type chips */
.wl-rain-precip-types { margin-bottom: 4px; }
.wl-rain-type-chips { display: flex; gap: 6px; flex-wrap: wrap; padding: 2px 0; }
.wl-rain-type-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 16px;
  background: var(--wl-bg-card, #e5e7eb);
  font-size: 12px;
  font-weight: 600;
  text-transform: capitalize;
  color: var(--wl-text, #374151);
}
.wl-rain-type-chip i { font-size: 14px; }

/* Upcoming rain hours */
.wl-rain-upcoming { margin-bottom: 4px; }
.wl-rain-hour-row {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 6px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s;
}
.wl-rain-hour-row:hover { background: var(--wl-bg-hover, #f3f4f6); }
.wl-rain-hour-row__time { font-size: 12px; font-weight: 600; min-width: 42px; color: var(--wl-text-muted, #6b7280); }
.wl-rain-hour-row__bar-track { flex: 1; height: 6px; background: var(--wl-border-light, #e5e7eb); border-radius: 3px; overflow: hidden; }
.wl-rain-hour-row__bar { height: 100%; background: var(--wl-accent, #3b82f6); border-radius: 3px; transition: width 0.3s; }
.wl-rain-hour-row__bar--heavy { background: #ef4444; }
.wl-rain-hour-row__prob { font-size: 12px; min-width: 32px; text-align: right; color: var(--wl-text-muted, #6b7280); }
.wl-rain-hour-row__amount { font-size: 12px; min-width: 40px; text-align: right; font-weight: 600; }
.wl-rain-hour-row__type { font-size: 14px; color: var(--wl-accent, #3b82f6); }

/* ---- Precip amounts in day cards ---- */
.wl-day-card__precip-amts {
  font-size: 11px;
  font-weight: 600;
  color: var(--wl-accent, #3b82f6);
  white-space: nowrap;
}
.wl-day-card__precip-amts i { font-size: 9px; }

/* ---- Settings tab ---- */
.wl-settings {
  padding: 8px 12px 16px;
}
.wl-settings-group {
  background: var(--wl-bg-card, #f8fafc);
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 12px;
}
.wl-settings-option {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  cursor: pointer;
  transition: background 0.15s;
  font-size: 14px;
  color: var(--wl-text, #1e293b);
  border-bottom: 1px solid var(--wl-border-light, #e5e7eb);
}
.wl-settings-option:last-child { border-bottom: none; }
.wl-settings-option:hover { background: var(--wl-bg-hover, #f1f5f9); }
.wl-settings-option--active {
  color: var(--wl-accent, #3b82f6);
  font-weight: 600;
}
.wl-settings-option i {
  font-size: 18px;
  color: var(--wl-text-muted, #94a3b8);
}
.wl-settings-option--active i {
  color: var(--wl-accent, #3b82f6);
}

.wl-settings-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  cursor: pointer;
  transition: background 0.15s;
  border-bottom: 1px solid var(--wl-border-light, #e5e7eb);
}
.wl-settings-toggle:last-child { border-bottom: none; }
.wl-settings-toggle:hover { background: var(--wl-bg-hover, #f1f5f9); }
.wl-settings-toggle__info { flex: 1; min-width: 0; }
.wl-settings-toggle__label {
  font-size: 14px;
  font-weight: 600;
  color: var(--wl-text, #1e293b);
}
.wl-settings-toggle__desc {
  font-size: 12px;
  color: var(--wl-text-muted, #64748b);
  line-height: 1.3;
  margin-top: 2px;
}

/* Toggle switch */
.wl-settings-switch {
  width: 44px;
  height: 24px;
  border-radius: 12px;
  background: #cbd5e1;
  position: relative;
  transition: background 0.2s;
  flex-shrink: 0;
}
.wl-settings-switch--on {
  background: var(--wl-accent, #3b82f6);
}
.wl-settings-switch__thumb {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #fff;
  position: absolute;
  top: 2px;
  left: 2px;
  transition: transform 0.2s;
  box-shadow: 0 1px 3px rgba(0,0,0,0.15);
}
.wl-settings-switch--on .wl-settings-switch__thumb {
  transform: translateX(20px);
}

/* Speed picker (segmented control) */
.wl-speed-picker {
  display: flex;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid var(--wl-border, #e2e8f0);
  flex-shrink: 0;
}
.wl-speed-picker__btn {
  padding: 5px 12px;
  border: none;
  background: var(--wl-bg-card, #f3f4f6);
  color: var(--wl-text-muted, #6b7280);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}
.wl-speed-picker__btn + .wl-speed-picker__btn {
  border-left: 1px solid var(--wl-border, #e2e8f0);
}
.wl-speed-picker__btn--active {
  background: var(--wl-accent, #3b82f6);
  color: #fff;
}

/* About section */
.wl-settings-about {
  background: var(--wl-bg-card, #f8fafc);
  border-radius: 10px;
  padding: 16px;
  text-align: center;
  margin-bottom: 12px;
}
.wl-settings-about__logo {
  margin-bottom: 8px;
}
.wl-settings-about__title {
  font-size: 15px;
  font-weight: 700;
  color: var(--wl-text, #1e293b);
  margin-bottom: 8px;
}
.wl-settings-about__text {
  font-size: 13px;
  color: var(--wl-text-muted, #64748b);
  line-height: 1.5;
  margin: 0 0 12px;
}
.wl-settings-about__links {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.wl-settings-link {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: var(--wl-accent, #3b82f6);
  text-decoration: none;
  transition: background 0.15s;
}
.wl-settings-link:hover {
  background: var(--wl-bg-hover, #eff6ff);
}
.wl-settings-link i {
  font-size: 16px;
}

/* Dark mode overrides for settings */
.wl--dark .wl-settings-group {
  background: var(--wl-bg-card, #1e293b);
}
.wl--dark .wl-settings-option {
  border-bottom-color: rgba(255,255,255,0.06);
}
.wl--dark .wl-settings-toggle {
  border-bottom-color: rgba(255,255,255,0.06);
}
.wl--dark .wl-settings-switch {
  background: #475569;
}
.wl--dark .wl-settings-about {
  background: var(--wl-bg-card, #1e293b);
}
.wl--dark .wl-settings-link:hover {
  background: rgba(59,130,246,0.1);
}
</style>
